// 由 src/views/smart-workbench/index.vue 的 methods 拆分而来，请勿在此文件之外重复定义同名方法。
// 分析配置与内容构建：各类分析/计划/指标/技能 的 config 获取、normalize、默认值、内容/卡片 HTML 构建等
// 方法体与原文件逐字节一致（仅调整文件归属）。
export default {
  methods: {
    async fetchGrossMarginAnalysisConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['分析毛利率'])
        return this.normalizeGrossMarginAnalysisConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchGrossMarginAnalysisConfig failed:', e)
        return this.normalizeGrossMarginAnalysisConfig()
      }
    },
    normalizeGrossMarginAnalysisConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse gross margin remark failed:', e)
          config = null
        }
      }
      return {
        ...this.getDefaultGrossMarginAnalysisConfig(),
        ...(config || {}),
        summary: {
          ...this.getDefaultGrossMarginAnalysisConfig().summary,
          ...((config || {}).summary || {})
        },
        indicator: {
          ...this.getDefaultGrossMarginAnalysisConfig().indicator,
          ...((config || {}).indicator || {})
        },
        chart: {
          ...this.getDefaultGrossMarginAnalysisConfig().chart,
          ...((config || {}).chart || {})
        },
        analysis: {
          ...this.getDefaultGrossMarginAnalysisConfig().analysis,
          ...((config || {}).analysis || {})
        },
        actions: Array.isArray((config || {}).actions)
          ? config.actions
          : this.getDefaultGrossMarginAnalysisConfig().actions
      }
    },
    getDefaultGrossMarginAnalysisConfig() {
      return {
        summary: {
          title: '毛利率分析',
          description: '正在从指标字典中检索毛利率数据并进行综合计算分析...',
          statusText: this.$t('workbench.statusDeepThinking'),
          resultText: '正在综合计算分析：同比下降1.6个百分点，低于目标值1.6个百分点'
        },
        indicator: {
          title: '毛利率',
          definition: '毛利率定义：毛利率 =（销售收入 - 销售成本）/ 销售收入 × 100%'
        },
        chart: {
          title: '毛利率趋势对比',
          rangeLabel: '近5个月',
          xAxis: ['1月', '2月', '3月', '4月', '5月'],
          series: [
            { name: '去年同期', data: ['20%', '18%', '22%', '21%', '24%'] },
            { name: '今年同期', data: ['11%', '9%', '12%', '10%', '16%'] }
          ]
        },
        analysis: {
          title: '数据分析结论',
          paragraphs: [
            '今年毛利率为17.1%，去年同期为18.7%，同比下降1.6个百分点。从月度趋势来看，今年各月毛利率均低于去年同期，呈现持续下滑态势。',
            '毛利金额同比减少约1,312万元。其中，销量下降贡献减少约4,264万元，成本上升侵蚀利润约2,624万元，价格竞争影响约1,468万元。',
            '当前毛利率水平已低于行业平均水平约2.3个百分点，建议尽快采取针对性措施进行改善。'
          ]
        },
        actions: [
          {
            label: 'PVMC模型分析毛利波动原因',
            actionKey: 'pvmc_analysis',
            style: 'primary'
          },
          { label: '什么是PVMC?', actionKey: 'pvmc_help', style: 'link' }
        ]
      }
    },
    getGrossMarginThinkingSteps(config) {
      const indicatorTitle =
        config.indicator?.title || this.$t('workbench.currentGrossMargin')
      return [
        this.$t('workbench.stepRetrieveIndicators'),
        this.$t('workbench.stepIndicatorFound', { indicator: indicatorTitle }),
        this.$t('workbench.stepFetchingData'),
        config.summary?.description || this.$t('workbench.statusInProgress'),
        config.summary?.resultText || this.$t('workbench.stepCalculatingResult')
      ]
    },
    getEnterpriseBriefAnalysisConfig() {
      return {
        summary: {
          title: '企业经营简报分析',
          description: '正在分析今日内部经营整体运行情况...',
          statusText: this.$t('workbench.statusDeepThinking'),
          resultText: '收入与回款总体稳中有升，毛利修复承压'
        },
        analysis: {
          title: '经营简报分析结论',
          paragraphs: [
            '今日内部经营整体延续平稳运行态势，收入与回款总体稳中有升。',
            '收入方面：本月累计收入8.2亿元，同比增长9.5%，保持良好增长态势。',
            '回款方面：回款进度基本符合预期，资金周转状况良好。',
            '毛利方面：毛利率8.2%，同比下降1.6个百分点，毛利修复承压明显。',
            '风险提示：当前毛利率水平偏低，建议关注成本控制和产品定价策略调整。'
          ]
        },
        keyMetrics: [
          {
            key: 'gross-margin',
            value: '17.1%',
            label: '毛利率',
            change: '↓1.6pct'
          },
          { key: 'revenue', value: '8.2亿', label: '本月收入' },
          { key: 'profit', value: '1.4亿', label: '本月利润' },
          { key: 'cost', value: '6.1亿', label: '本月成本' },
          { key: 'orders', value: '12,847', label: '订单量' },
          { key: 'output', value: '28.6万', label: '本月产量' }
        ],
        actions: [
          {
            label: '深入分析毛利下降原因',
            actionKey: 'pvmc_analysis',
            style: 'primary'
          },
          {
            label: '查看详细经营报告',
            actionKey: 'view_report',
            style: 'link'
          }
        ]
      }
    },
    getEnterpriseBriefThinkingSteps(config) {
      return [
        '指标定义智能体：检索今日营收、毛利率、产能利用率、现金流、库存周转与订单准交率指标定义与口径。',
        '指标定义智能体：拉取核心经营指标今日值、预算值、预警阈值与环比变化。',
        '本体治理智能体：拆解营收、毛利率、库存与交付指标的原子指标构成与关联链路。',
        '本体治理智能体：核对高毛利订单占比、低价订单占比、主线排产负荷与库存结构。',
        '经营决策智能体：评估收入稳定、利润承压、产线偏满与库存温和上行的联动影响。',
        '经营决策智能体：输出今日经营风险预警与优先处置建议，生成每日经营简报。'
      ]
    },
    getCsoEnterpriseBriefAnalysisConfig() {
      return {
        summary: {
          title: '供应链全局简报',
          description: '正在分析今日供应链运营整体运行情况...',
          statusText: this.$t('workbench.statusDeepThinking'),
          resultText: '供应链运营整体平稳向好，四大风险并行需关注',
          riskCount: 4,
          overviewText:
            '今日供应链运营整体平稳向好。采购降本累计 1260 万元（达成 84%）。需优先关注 MCU 库存缺口、铜价暴涨、客户流失、资质到期四大风险并行，可下钻查看详情。'
        },
        keyMetrics: [
          {
            key: 'delivery',
            value: '91%',
            label: 'T-1 交付率',
            change: '-2%',
            status: 'warning',
            spark: 'down'
          },
          {
            key: 'kitting',
            value: '91%',
            label: '物料齐套率',
            change: '-1%',
            status: 'warning',
            spark: 'down'
          },
          {
            key: 'turnover',
            value: '52 天',
            label: '库存周转',
            change: '+1 天',
            status: 'normal',
            spark: 'up'
          },
          {
            key: 'saving',
            value: '1260 万',
            label: '采购降本',
            change: '+84%',
            status: 'normal',
            spark: 'up'
          },
          {
            key: 'health',
            value: '78 分',
            label: '供应链健康',
            change: '+4',
            status: 'normal',
            spark: 'up'
          },
          {
            key: 'risk',
            value: '4 项',
            label: '风险预警',
            change: '+2',
            status: 'risk',
            spark: 'up'
          }
        ],
        risks: [
          {
            name: 'MCU 库存危机',
            percent: 100,
            color: '#ef4444',
            status: '待决策（17:00 前）'
          },
          {
            name: '铜价暴涨应对',
            percent: 70,
            color: '#f59e0b',
            status: 'TCO 对比中'
          },
          {
            name: '客户流失保盘',
            percent: 50,
            color: '#7c3aed',
            status: '分级方案测算中'
          },
          {
            name: '资质到期续期',
            percent: 30,
            color: '#8b5cf6',
            status: '3 家 A 级启动中'
          }
        ],
        references: [
          {
            tag: '内部经营指标 5 项',
            date: '2026-06-25',
            desc: 'T-1 齐套/库存/降本/网络'
          },
          {
            tag: '外部资讯 4 类',
            date: '2026-06-25',
            desc: '铜价/华为/竞品/政策'
          },
          {
            tag: '四大风险信号评估',
            date: '2026-06-25',
            desc: 'MCU/铜价/客户/资质'
          },
          {
            tag: '采购降本兑现台账',
            date: '2026-06-25',
            desc: '累计达成 84%'
          }
        ]
      }
    },
    renderCeoThinkingCard(thinkingId, firstStep, title, summary, totalSteps) {
      title = title || '深度分析中'
      summary = summary || '正在分析毛利率异常问题，进行系统性诊断与根因归因...'
      const total = Number.isFinite(totalSteps) && totalSteps > 0 ? totalSteps : ''
      const innerHtml = `
        <div id="${thinkingId}" style="margin-top:12px;display:flex;flex-direction:column;gap:12px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <div style="position:relative;width:28px;height:28px;flex-shrink:0;">
              <div style="position:absolute;inset:0;border-radius:50%;background:linear-gradient(135deg,#667eea,#764ba2);opacity:0.2;animation:think-pulse-${thinkingId} 1.5s ease-in-out infinite;"></div>
              <div style="position:absolute;inset:2px;border-radius:50%;background:linear-gradient(135deg,#667eea,#764ba2);display:flex;align-items:center;justify-content:center;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              </div>
            </div>
            <div style="flex:1;min-width:0;">
              <div style="font-size:12px;font-weight:600;color:#667eea;">深度思考中</div>
              <div style="font-size:10px;color:#94a3b8;margin-top:2px;">多智能体协同推理中...</div>
            </div>
            <div id="thinking-progress-${thinkingId}" style="font-size:10px;color:#667eea;font-weight:600;white-space:nowrap;">步骤 1 / ${total}</div>
          </div>
          <div style="padding:12px;border-radius:12px;background:linear-gradient(135deg,rgba(102,126,234,0.05),rgba(118,75,162,0.03));border:1px solid rgba(102,126,234,0.12);">
            <div style="font-size:10px;color:#64748b;margin-bottom:6px;display:flex;align-items:center;gap:4px;">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 6v6l4 2"/></svg>
              推理过程
            </div>
            <div style="border-radius:8px;background:#ffffff;border:1px solid #e8ecf0;padding:10px 12px;position:relative;overflow:hidden;">
              <div id="thinking-track-${thinkingId}" style="display:flex;flex-direction:column;position:relative;">
                <div style="display:flex;align-items:flex-start;gap:8px;padding-left:16px;position:relative;">
                  <div style="position:absolute;left:0;top:0;bottom:0;width:10px;display:flex;flex-direction:column;align-items:center;">
                    <div id="thinking-active-dot-${thinkingId}" style="width:6px;height:6px;border-radius:50%;background:#667eea;flex:none;margin-top:5px;box-shadow:0 0 4px rgba(102,126,234,0.4);animation:dot-pulse-${thinkingId} 1.2s ease-in-out infinite;"></div>
                    <div style="width:1px;flex:1;background:linear-gradient(180deg,#667eea40,#667eea10);margin-top:2px;"></div>
                  </div>
                  <span style="font-size:11px;line-height:1.6;color:#1f2937;padding-top:2px;">${firstStep}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <style>
          @keyframes think-pulse-${thinkingId} {
            0%, 100% { transform: scale(1); opacity: 0.2; }
            50% { transform: scale(1.5); opacity: 0.08; }
          }
          @keyframes dot-pulse-${thinkingId} {
            0%, 100% { transform: scale(1); box-shadow: 0 0 4px rgba(102,126,234,0.4); }
            50% { transform: scale(1.6); box-shadow: 0 0 12px rgba(102,126,234,0.7); }
          }
        </style>
      `
      return `
        <div data-ceo-card="shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px 16px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div style="font-size:14px;font-weight:700;color:#1f2937;">${title}</div>
            <div style="margin-top:6px;font-size:12px;line-height:1.7;color:#5a6b7e;width:100%;">${summary}</div>
            <div style="width:100%;box-sizing:border-box;">${innerHtml}</div>
          </div>
        </div>
      `
    },
    updateCeoThinkingCard(thinkingId, steps, stepIndex, retryCount = 0) {
      const trackEl = this._findVisibleEl(`thinking-track-${thinkingId}`)
      const progressEl = this._findVisibleEl(`thinking-progress-${thinkingId}`)

      if (!trackEl) {
        if (retryCount < 5) {
          setTimeout(() => {
            this.updateCeoThinkingCard(thinkingId, steps, stepIndex, retryCount + 1)
          }, 200)
        }
        return
      }

      const safeSteps = Array.isArray(steps) && steps.length ? steps : ['正在分析中']
      const total = safeSteps.length
      const idx = Math.min(stepIndex, total - 1)

      // 更新进度
      if (progressEl) {
        progressEl.textContent = `步骤 ${idx + 1} / ${total}`
      }

      // 追加新步骤（带时间轴效果）
      const stepItem = document.createElement('div')
      stepItem.style.cssText =
        'display:flex;align-items:flex-start;gap:8px;opacity:0;transform:translateX(-4px);transition:all 0.35s ease;position:relative;padding-left:16px;'

      // 时间轴竖线 + 圆点
      const dotLine = document.createElement('div')
      dotLine.style.cssText =
        'position:absolute;left:0;top:0;bottom:0;width:10px;display:flex;flex-direction:column;align-items:center;'

      const dot = document.createElement('div')
      dot.style.cssText = `width:6px;height:6px;border-radius:50%;background:#667eea;flex:none;margin-top:5px;box-shadow:0 0 4px rgba(102,126,234,0.4);animation:dot-pulse-${thinkingId} 1.2s ease-in-out infinite;`
      dotLine.appendChild(dot)

      const line = document.createElement('div')
      line.style.cssText =
        'width:1px;flex:1;background:linear-gradient(180deg,#667eea40,#667eea10);margin-top:2px;'
      dotLine.appendChild(line)

      stepItem.appendChild(dotLine)

      const text = document.createElement('span')
      text.style.cssText =
        'font-size:11px;line-height:1.6;color:#1f2937;padding-top:2px;flex:1;'
      text.textContent = safeSteps[idx]
      stepItem.appendChild(text)

      trackEl.appendChild(stepItem)

      // 将前一条的时间轴底部竖线变淡，并移除动画
      if (trackEl.children.length > 1) {
        const prev = trackEl.children[trackEl.children.length - 2]
        const prevDotLine = prev.querySelector('div')
        if (prevDotLine) {
          if (prevDotLine.children[1]) {
            prevDotLine.children[1].style.background =
              'linear-gradient(180deg,#cbd5e1,#667eea20)'
          }
          if (prevDotLine.children[0]) {
            prevDotLine.children[0].style.animation = 'none'
            prevDotLine.children[0].style.boxShadow = '0 0 2px rgba(102,126,234,0.3)'
          }
        }
      }

      // 淡入
      requestAnimationFrame(() => {
        stepItem.style.opacity = '1'
        stepItem.style.transform = 'translateX(0)'
      })

      // 限制最多6条
      while (trackEl.children.length > 6) {
        trackEl.removeChild(trackEl.firstChild)
      }

      // 检查是否完成（stepIndex >= total - 1 表示最后一步）
      if (idx >= total - 1) {
        this.completeCeoThinkingCard(thinkingId)
      }
    },
    completeCeoThinkingCard(thinkingId) {
      const thinkingEl = this._findVisibleEl(thinkingId)
      if (!thinkingEl) return

      // 将"深度思考中"改为"思考完成"
      const titleEl = thinkingEl.querySelector(
        'div:nth-child(1) > div:nth-child(2) > div:nth-child(1)'
      )
      if (titleEl) {
        titleEl.textContent = '思考完成'
      }

      // 将"多智能体协同推理中..."改为"推理完成"
      const descEl = thinkingEl.querySelector(
        'div:nth-child(1) > div:nth-child(2) > div:nth-child(2)'
      )
      if (descEl) {
        descEl.textContent = '推理完成'
      }

      // 移除最后一个圆点的动画
      const lastDot = thinkingEl.querySelector(
        '#thinking-track-' + thinkingId + ' > div:last-child > div > div:first-child'
      )
      if (lastDot) {
        lastDot.style.animation = 'none'
        lastDot.style.boxShadow = '0 0 2px rgba(102,126,234,0.3)'
      }

      // 移除进度显示
      const progressEl = this._findVisibleEl('thinking-progress-' + thinkingId)
      if (progressEl) {
        progressEl.style.display = 'none'
      }
    },
    renderEnterpriseBriefThinkingCard(thinkingId, firstStep, summary) {
      const title = summary.title || '每日经营简报'
      const total = 6
      const visibleStep = `<div style="display:flex;align-items:flex-start;gap:8px;font-size:12px;line-height:1.75;color:#1f2937;"><span style="margin-top:6px;width:6px;height:6px;border-radius:999px;background:#4A5FF3;flex:none;"></span><span>${firstStep}</span></div>`
      return `
        <div id="${thinkingId}" data-enterprise-brief-thinking="true" style="display:flex;flex-direction:column;gap:12px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <div style="width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,#4A5FF3,#6B7FFF);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                <path d="M12 2a10 10 0 1 0 10 10" stroke-linecap="round"></path>
                <path d="M12 6v6l4 2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div style="flex:1;min-width:0;">
              <div style="font-size:13px;font-weight:700;color:#1f2937;">${title}</div>
              <div style="font-size:11px;color:#94a3b8;margin-top:2px;">正在逐条分析指标与风险，完成后生成经营简报</div>
            </div>
            <div id="enterprise-brief-thinking-progress-${thinkingId}" style="font-size:11px;color:#4A5FF3;font-weight:600;">1 / ${total}</div>
          </div>
          <div style="padding:12px;border-radius:12px;background:linear-gradient(135deg,rgba(74,95,243,0.05),rgba(14,165,233,0.03));border:1px solid rgba(74,95,243,0.12);">
            <div style="font-size:11px;color:#64748b;margin-bottom:8px;">大模型思考过程</div>
            <div style="height:92px;overflow:hidden;border-radius:10px;background:#ffffff;border:1px solid #e8ecf0;padding:10px 12px;">
              <div id="enterprise-brief-thinking-track-${thinkingId}" style="display:flex;flex-direction:column;gap:8px;transform:translateY(0);">${visibleStep}</div>
            </div>
          </div>
          <div style="padding:10px 12px;border-radius:10px;background:#f8fafc;border:1px solid #e8ecf0;">
            <div style="font-size:11px;color:#64748b;margin-bottom:6px;">当前结论</div>
            <div id="enterprise-brief-thinking-current-${thinkingId}" style="font-size:12px;color:#475569;line-height:1.8;">${firstStep}</div>
          </div>
        </div>
      `
    },
    updateThinkingCard(thinkingId, steps, stepIndex) {
      const currentEl = this._findVisibleEl(
        `enterprise-brief-thinking-current-${thinkingId}`
      )
      const progressEl = this._findVisibleEl(
        `enterprise-brief-thinking-progress-${thinkingId}`
      )
      const trackEl = this._findVisibleEl(
        `enterprise-brief-thinking-track-${thinkingId}`
      )
      if (!currentEl || !progressEl || !trackEl) return

      const safeSteps = Array.isArray(steps) && steps.length ? steps : ['正在分析中']
      const visibleSteps = safeSteps.slice(Math.max(0, stepIndex - 2), stepIndex + 1)
      currentEl.textContent = safeSteps[stepIndex] || safeSteps[0]
      progressEl.textContent =
        Math.min(stepIndex + 1, safeSteps.length) + ' / ' + safeSteps.length
      trackEl.style.transition = 'none'
      trackEl.style.transform = 'translateY(18px)'
      trackEl.innerHTML = visibleSteps
        .map((step, index) => {
          const isCurrent = index === visibleSteps.length - 1
          return `<div style="display:flex;align-items:flex-start;gap:8px;font-size:12px;line-height:1.75;color:${
            isCurrent ? '#1f2937' : '#64748b'
          };opacity:${isCurrent ? '1' : '0.82'};">
          <span style="margin-top:6px;width:6px;height:6px;border-radius:999px;background:${
  isCurrent ? '#4A5FF3' : '#cbd5e1'
};flex:none;"></span>
          <span>${step}</span>
        </div>`
        })
        .join('')
      setTimeout(() => {
        trackEl.style.transition = 'transform 0.4s ease'
        trackEl.style.transform = 'translateY(0)'
      }, 30)
    },
    clearExistingThinkingCards() {
      const messages = this.$store.state.cosmoClaw.messages
      const filtered = messages.filter((msg) => {
        if (typeof msg.content !== 'string') return true
        const content = msg.content
        // Collapsed thinking toggles (data-thinking-toggle) are completed content that
        // keeps the full original card HTML for expand/collapse: preserve them so the
        // mock flow can be restored after switching sessions.
        if (content.indexOf('data-thinking-toggle') !== -1) return true
        // Enterprise brief thinking: only clear cards that are still running.
        if (content.indexOf('data-enterprise-brief-thinking') !== -1) return false
        // CEO deep thinking: only clear live cards (they carry animation markers
        // thinking-track- / think-pulse- / dot-pulse- / thinking-status-). Completed
        // strategy/report cards also use data-ceo-card but have no animation markers,
        // so keep them to avoid losing the mock flow on session switch.
        if (content.indexOf('data-ceo-card') !== -1) {
          const isLiveThinking =
            content.indexOf('thinking-track-') !== -1 ||
            content.indexOf('think-pulse-') !== -1 ||
            content.indexOf('dot-pulse-') !== -1 ||
            content.indexOf('thinking-status-') !== -1
          return !isLiveThinking
        }
        return true
      })
      if (filtered.length !== messages.length) {
        this.$store.commit('cosmoClaw/SET_MESSAGES', filtered)
      }
    },
    removeThinkingCard(thinkingId) {
      this.completeCeoThinkingCard(thinkingId)

      const cardEl = this._findVisibleEl(thinkingId)
      if (!cardEl) return

      // 找到外壳 data-ceo-card="shell"，用于保存完整的思考卡片 HTML（展开内容）
      const shell = cardEl.closest('[data-ceo-card]') || cardEl.parentElement
      // 向上追溯找到 .message-item，读取 data-msg-id（用于写回 store）
      let messageItem = null
      let walker = cardEl.parentElement
      while (walker && walker !== document.body) {
        if (
          walker.classList &&
          (walker.classList.contains('message-item') ||
            (walker.className &&
              typeof walker.className === 'string' &&
              walker.className.indexOf('message-item') !== -1))
        ) {
          messageItem = walker
          break
        }
        walker = walker.parentElement
      }
      const messageId = messageItem ? messageItem.getAttribute('data-msg-id') : null
      // 保存完整外壳 HTML 作为折叠后可展开查看的原始内容
      const originalContent = shell ? shell.outerHTML : ''

      // 提取标题 + 状态文案
      let title = '深度分析'
      if (shell) {
        const titleEl = shell.querySelector('div[style*="font-weight:700"]')
        if (titleEl) {
          title = titleEl.textContent.replace('完成', '').trim() || title
        }
      }
      const statusEl = this._findVisibleEl(`thinking-status-${thinkingId}`)
      const finalStatus = statusEl ? statusEl.textContent : '推理完成'

      // 走 store 同步路径：_persistCollapsedThinking → setMessageContent → UPDATE_MESSAGE_CONTENT_BY_ID
      // mutation 内部会同步 sessionCache，切换会话后折叠状态不丢失
      if (messageId) {
        this._persistCollapsedThinking(messageId, title, finalStatus, originalContent)
        return
      }

      // 兜底：没找到 messageId 时保留原 DOM 折叠逻辑
      let fallbackItem = cardEl.parentElement
      while (fallbackItem && fallbackItem !== document.body) {
        if (
          fallbackItem.classList &&
          (fallbackItem.classList.contains('message-item') ||
            (fallbackItem.className &&
              typeof fallbackItem.className === 'string' &&
              fallbackItem.className.indexOf('message-item') !== -1))
        ) {
          this._collapseThinkingMessage(fallbackItem, cardEl)
          return
        }
        if (
          fallbackItem.classList &&
          (fallbackItem.classList.contains('justify-start') ||
            fallbackItem.classList.contains('justify-end') ||
            (fallbackItem.className &&
              typeof fallbackItem.className === 'string' &&
              (fallbackItem.className.indexOf('justify-start') !== -1 ||
                fallbackItem.className.indexOf('justify-end') !== -1)))
        ) {
          this._collapseThinkingMessage(fallbackItem, cardEl)
          return
        }
        fallbackItem = fallbackItem.parentElement
      }
      this._collapseThinkingMessage(null, cardEl)
    },
    _persistCollapsedThinking(messageId, title, status, originalContent) {
      if (!messageId) return
      const safeTitle = (title || '深度分析').replace(/`/g, '')
      const safeStatus = (status || '推理完成').replace(/`/g, '')
      const toggleId = 'ct-' + Date.now()
      const arrowSvg = '<svg data-toggle-arrow="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667eea" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="transition:transform 0.25s ease;"><polyline points="9 18 15 12 9 6"/></svg>'
      const collapsedHtml = '<div style="margin-bottom:12px;">' +
        '<div id="' + toggleId + '-bar" data-thinking-toggle="true" data-toggle-id="' + toggleId + '" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:10px;padding:10px 14px;cursor:pointer;display:flex;align-items:center;gap:10px;transition:all 0.2s ease;">' +
        '<div style="width:24px;height:24px;border-radius:50%;background:linear-gradient(135deg,#667eea,#764ba2);display:flex;align-items:center;justify-content:center;flex-shrink:0;">' +
        '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>' +
        '</div>' +
        '<div style="flex:1;min-width:0;">' +
        '<div style="font-size:12px;font-weight:600;color:#667eea;">' + safeTitle + '</div>' +
        '<div style="font-size:10px;color:#94a3b8;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + safeStatus + '</div>' +
        '</div>' +
        '<div style="font-size:10px;color:#667eea;white-space:nowrap;display:flex;align-items:center;gap:4px;"><span data-toggle-label="true">展开查看</span>' +
        arrowSvg +
        '</div></div>' +
        '<div id="' + toggleId + '-content" style="max-height:0;opacity:0;overflow:hidden;transition:all 0.35s ease;padding:0 16px;box-sizing:border-box;">' + (originalContent || '') + '</div>' +
        '</div>'
      this.setMessageContent(messageId, collapsedHtml)
    },
    _collapseThinkingMessage(messageItem, thinkingEl) {
      // 收起推理详情，保留紧凑摘要，支持切换展开/收起
      const thinkingId = thinkingEl.id
      const statusEl = this._findVisibleEl(`thinking-status-${thinkingId}`)
      const finalStatus = statusEl ? statusEl.textContent : '推理完成'

      // 获取标题
      let title = '深度分析'
      if (thinkingEl) {
        const shell = thinkingEl.closest('[data-ceo-card]') || thinkingEl.parentElement
        if (shell) {
          const titleEl = shell.querySelector('div[style*="font-weight:700"]')
          if (titleEl) {
            title = titleEl.textContent.replace('完成', '').trim() || title
          }
        }
      }

      // 将原始卡片包装到可切换容器中，以便控制显示/隐藏
      const wrapperId = `thinking-wrapper-${thinkingId}`
      const collapsedId = `thinking-collapsed-${thinkingId}`

      // 创建紧凑摘要卡片（始终可见）
      let expandTarget = null
      if (messageItem && messageItem.parentElement) {
        expandTarget = messageItem
      } else if (thinkingEl && thinkingEl.parentElement) {
        expandTarget = thinkingEl
      }

      if (!expandTarget) {
        // 兜底：直接隐藏
        thinkingEl.style.display = 'none'
        return
      }

      // 给目标加上 wrapper id 便于切换
      expandTarget.id = wrapperId

      // 折叠动画
      expandTarget.style.transition = 'all 0.35s ease'
      expandTarget.style.maxHeight = '0px'
      expandTarget.style.opacity = '0'
      expandTarget.style.overflow = 'hidden'
      expandTarget.style.marginBottom = '0'
      expandTarget.style.padding = '0 16px'
      expandTarget.style.boxSizing = 'border-box'

      // 创建可切换的折叠卡片
      const toggleBar = document.createElement('div')
      toggleBar.id = collapsedId
      toggleBar.style.cssText =
        'background:#ffffff;border:1px solid #e8ecf0;border-radius:10px;padding:10px 14px;box-shadow:0 2px 8px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;margin-bottom:12px;cursor:pointer;display:flex;align-items:center;gap:10px;transition:all 0.2s ease;'
      toggleBar.setAttribute('data-thinking-toggle', 'true')

      // 箭头图标（展开时向下，收起时向右）
      const arrowSvg = `
        <svg id="toggle-arrow-${thinkingId}" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#667eea" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="transition:transform 0.25s ease;">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      `

      toggleBar.innerHTML = `
        <div style="width:24px;height:24px;border-radius:50%;background:linear-gradient(135deg,#667eea,#764ba2);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
          </svg>
        </div>
        <div style="flex:1;min-width:0;">
          <div style="font-size:12px;font-weight:600;color:#667eea;">${title}</div>
          <div id="toggle-status-${thinkingId}" style="font-size:10px;color:#94a3b8;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${finalStatus}</div>
        </div>
        <div id="toggle-label-${thinkingId}" style="font-size:10px;color:#667eea;white-space:nowrap;display:flex;align-items:center;gap:4px;">
          <span>展开查看</span>
          ${arrowSvg}
        </div>
      `

      // 插入折叠条到 expandTarget 之前
      expandTarget.parentElement.insertBefore(toggleBar, expandTarget)

      // 切换状态
      let isExpanded = false
      const vm = this

      toggleBar.addEventListener('click', function () {
        isExpanded = !isExpanded
        if (isExpanded) {
          // 展开
          expandTarget.style.maxHeight = '800px' // 足够大
          expandTarget.style.opacity = '1'
          expandTarget.style.overflow = ''
          expandTarget.style.marginBottom = ''
          expandTarget.style.padding = ''
          // 更新箭头和文字
          const arrowEl = vm._findVisibleEl(`toggle-arrow-${thinkingId}`)
          if (arrowEl) arrowEl.style.transform = 'rotate(0deg)'
          const labelEl = vm._findVisibleEl(`toggle-label-${thinkingId}`)
          if (labelEl) {
            labelEl.querySelector('span').textContent = '收起推理'
          }
        } else {
          // 收起
          expandTarget.style.maxHeight = '0px'
          expandTarget.style.opacity = '0'
          expandTarget.style.overflow = 'hidden'
          expandTarget.style.marginBottom = '0'
          expandTarget.style.padding = '0 16px'
          // 更新箭头和文字
          const arrowEl = vm._findVisibleEl(`toggle-arrow-${thinkingId}`)
          if (arrowEl) arrowEl.style.transform = 'rotate(-90deg)'
          const labelEl = vm._findVisibleEl(`toggle-label-${thinkingId}`)
          if (labelEl) {
            labelEl.querySelector('span').textContent = '展开查看'
          }
        }
      })

      // hover 效果
      toggleBar.addEventListener('mouseenter', function () {
        this.style.borderColor = '#667eea'
        this.style.boxShadow = '0 2px 12px rgba(102,126,234,0.12)'
      })
      toggleBar.addEventListener('mouseleave', function () {
        this.style.borderColor = '#e8ecf0'
        this.style.boxShadow = 'none'
      })
    },
    renderEnterpriseBriefStreamCard(config) {
      this.$store.commit('cosmoClaw/SET_LOCAL_DEMO_MODE', false)
      const replyId = `enterprise-brief-stream-${Date.now()}`
      const _cardColors = {
        revenue: '#16a34a',
        profit: '#4A5FF3',
        cost: '#dc2626',
        'gross-margin': '#e54b3d',
        orders: '#0ea5e9',
        output: '#10b981'
      }
      const keyMetrics = config.keyMetrics || [
        { key: 'gross-margin', value: '17.1%', label: '毛利率' },
        { key: 'revenue', value: '8.2亿', label: '本月收入' },
        { key: 'profit', value: '1.4亿', label: '本月利润' },
        { key: 'cost', value: '6.1亿', label: '本月成本' },
        { key: 'orders', value: '12,847', label: '订单量' },
        { key: 'output', value: '28.6万', label: '本月产量' }
      ]
      const _gmItem = keyMetrics.find((m) => m.key === 'gross-margin')
      const _gmVal = _gmItem ? _gmItem.value : '17.1%'
      const _gmChg = _gmItem ? _gmItem.change || '↓1.6pct' : '↓1.6pct'
      const _s4Revenue =
        (keyMetrics.find((m) => m.key === 'revenue') || {}).value || '8.2亿'
      const _s4Profit =
        (keyMetrics.find((m) => m.key === 'profit') || {}).value || '1.4亿'
      const _s4Cost = (keyMetrics.find((m) => m.key === 'cost') || {}).value || '6.1亿'
      const _s4Orders =
        (keyMetrics.find((m) => m.key === 'orders') || {}).value || '12,847'

      const section1Id = `daily-brief-summary-${replyId}`
      const section5Id = `daily-brief-recommend-${replyId}`

      // 分析结论条目文字
      const analysisItems = [
        `毛利率 ${_gmVal}，${_gmChg}，低于目标值。低价订单结构是利润压缩的主因——毛利率异常下降为当前最大经营风险，近5个月从18.0%连续下滑至14.5%，累计下滑3.5pct。`,
        `本月营收 ${_s4Revenue}，核心客户拉动明显，收入端总体稳定。`,
        `本月利润 ${_s4Profit}，同比下降，毛利率下滑直接传导至利润端，修复毛利率是保利润的关键。`,
        `本月成本 ${_s4Cost}，订单量 ${_s4Orders}，成本控制总体平稳，但需排查低价订单对毛利率的侵蚀效应。`
      ]

      const section1 = `
        <div id="daily-brief-section-1-${replyId}" style="display:none;padding:14px 15px;border-radius:12px;background:linear-gradient(135deg,rgba(74,95,243,0.08),rgba(14,165,233,0.05));border:1px solid rgba(74,95,243,0.16);opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px;">
            <div style="font-size:12px;font-weight:700;color:#1f2937;">每日经营简报</div>
            <span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:#eef4ff;color:#4A5FF3;font-size:11px;font-weight:600;">CEO数字人推送</span>
          </div>
          <div id="${section1Id}" style="font-size:12px;color:#5a6b7e;line-height:1.8;min-height:22px;"></div>
        </div>
      `

      const _section2Cards = keyMetrics
        .map((m, mi) => {
          const isGm = m.key === 'gross-margin'
          const bg = isGm ? '#fef2f2' : '#fff'
          const brd = isGm ? '1px solid #fecaca' : '1px solid #e8ecf0'
          return `<div id="brief-metric-${mi}-${replyId}" style="display:none;padding:12px 10px;border-radius:10px;background:${bg};border:${brd};box-shadow:0 1px 3px rgba(15,23,42,0.04);opacity:0;transform:translateY(6px);transition:all .28s ease;">
          <div id="brief-metric-val-${mi}-${replyId}" style="font-size:20px;font-weight:800;color:${
  _cardColors[m.key] || '#6b7280'
};line-height:1;min-height:26px;"></div>
          <div id="brief-metric-lbl-${mi}-${replyId}" style="margin-top:6px;font-size:12px;color:#8c9bab;min-height:16px;"></div>
        </div>`
        })
        .join('')

      const section2 = `
        <div id="daily-brief-section-2-${replyId}" style="display:none;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          ${_section2Cards}
        </div>
      `

      const section3 = `
        <div id="daily-brief-section-3-${replyId}" style="display:none;gap:6px;flex-wrap:wrap;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#fef2f2;border:1px solid rgba(229,75,61,0.18);font-size:12px;color:#e54b3d;font-weight:700;">毛利率预警</span>
          <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#eef2ff;border:1px solid rgba(74,95,243,0.18);font-size:12px;color:#4A5FF3;">收入稳定</span>
          <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#fffbeb;border:1px solid rgba(245,158,11,0.18);font-size:12px;color:#d97706;">利润承压</span>
          <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#fef2f2;border:1px solid rgba(220,38,38,0.18);font-size:12px;color:#dc2626;">结构预警</span>
        </div>
      `

      const section4 = `
        <div id="daily-brief-section-4-${replyId}" style="display:none;flex-direction:column;gap:8px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div id="brief-analysis-0-${replyId}" style="display:none;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:#fef2f2;border:1px solid #fecaca;opacity:0;transform:translateX(-6px);transition:all .32s ease;">
            <span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:#e54b3d;flex-shrink:0;"></span>
            <div id="brief-analysis-text-0-${replyId}" style="font-size:12px;color:#4b5563;line-height:1.7;min-height:18px;"></div>
          </div>
          <div id="brief-analysis-1-${replyId}" style="display:none;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateX(-6px);transition:all .32s ease;">
            <span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:#16a34a;flex-shrink:0;"></span>
            <div id="brief-analysis-text-1-${replyId}" style="font-size:12px;color:#4b5563;line-height:1.7;min-height:18px;"></div>
          </div>
          <div id="brief-analysis-2-${replyId}" style="display:none;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateX(-6px);transition:all .32s ease;">
            <span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:#4A5FF3;flex-shrink:0;"></span>
            <div id="brief-analysis-text-2-${replyId}" style="font-size:12px;color:#4b5563;line-height:1.7;min-height:18px;"></div>
          </div>
          <div id="brief-analysis-3-${replyId}" style="display:none;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateX(-6px);transition:all .32s ease;">
            <span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:#dc2626;flex-shrink:0;"></span>
            <div id="brief-analysis-text-3-${replyId}" style="font-size:12px;color:#4b5563;line-height:1.7;min-height:18px;"></div>
          </div>
        </div>
      `

      const _actionItems = [
        {
          text: '设置低价订单占比红线（不超过35%），严控新增低价订单。',
          priority: 'high'
        },
        {
          text: '推动与核心客户的价格联动机制，加快成本传导。',
          priority: 'medium'
        },
        { text: '建立基地良率周跟踪看板，降低制造损耗。', priority: 'medium' },
        {
          text: '利润达成率94%，需关注费用投放节奏与毛利修复进度。',
          priority: 'low'
        }
      ]
      const _actionItemsHtml = _actionItems
        .map((item, idx) => {
          const dotColor =
            item.priority === 'high'
              ? '#dc2626'
              : item.priority === 'medium'
                ? '#f59e0b'
                : '#10b981'
          return `<div id="brief-action-${idx}-${replyId}" style="display:none;align-items:flex-start;gap:8px;padding:8px 0;opacity:0;transform:translateX(-6px);transition:all .32s ease;border-bottom:${
            idx < _actionItems.length - 1 ? '1px solid #f1f5f9' : 'none'
          };">
          <span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:${dotColor};flex-shrink:0;"></span>
          <div id="brief-action-text-${idx}-${replyId}" style="font-size:12px;color:#4b5563;line-height:1.7;min-height:18px;"></div>
        </div>`
        })
        .join('')

      const section5 = `
        <div id="daily-brief-section-5-${replyId}" style="display:none;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="font-size:13px;font-weight:700;color:#1f2937;margin-bottom:12px;">行动建议</div>
          <div id="${section5Id}" style="display:flex;flex-direction:column;">${_actionItemsHtml}</div>
        </div>
      `

      const followUpToken = `follow-up-${replyId}`
      this.followUpRegistry = this.followUpRegistry || {}
      this.followUpRegistry[followUpToken] = {
        title: '追问每日经营简报',
        summary:
          '基于刚才每日经营简报的结论，请继续展开说明今天最需要优先处理的风险、责任人和落地动作。',
        prompt:
          '基于刚才每日经营简报的结论，请继续展开说明今天最需要优先处理的风险、责任人和落地动作。'
      }

      const followUpHtml = `
        <div id="daily-brief-section-6-${replyId}" style="display:none;margin-top:14px;gap:12px;align-items:center;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <span style="font-size:12px;color:#4A5FF3;cursor:pointer;text-decoration:underline;" onclick="window.triggerCeoFollowUp('${followUpToken}')">追问</span>
        </div>
      `

      const cardHtml = `
        <div data-ceo-card="shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px 16px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div style="font-size:15px;font-weight:700;color:#1f2937;">每日经营简报</div>
            <div style="margin-top:8px;font-size:13px;line-height:1.7;color:#5a6b7e;width:100%;">CEO数字人已完成晨间经营总览，正在分段输出正式简报。</div>
            <div id="daily-brief-stream-${replyId}" style="margin-top:2px;display:flex;flex-direction:column;gap:12px;">
              ${section1}${section2}${section3}${section4}${section5}${followUpHtml}
            </div>
          </div>
        </div>
      `

      this.appendLocalMessage('assistant', cardHtml, true)

      // 串行：通过回调链式触发
      // 区块1：总结 typewriter → 区块2 指标卡片 → 区块3 标签 → 区块4 逐条分析 → 区块5 建议 → 追问
      setTimeout(() => {
        this.revealSection(`daily-brief-section-1-${replyId}`, '')
        const _gmChgNum = _gmChg.replace(/[↑↓→]/g, '')
        setTimeout(() => {
          this.typeWriterEffect(
            section1Id,
            `中屏企业经营核心指标已汇总。毛利率 ${_gmVal}，同比下降 ${_gmChgNum}，为当前最大经营风险项。企业处于收入增长、利润承压、毛利率异常下降的状态。`,
            () => {
              this.revealSection(`daily-brief-section-2-${replyId}`, 'grid')
              let metricIdx = 0
              const showNextMetric = () => {
                if (metricIdx >= 6) {
                  this.revealSection(`daily-brief-section-3-${replyId}`, 'flex')
                  setTimeout(() => {
                    this.revealSection(`daily-brief-section-4-${replyId}`, 'flex')
                    let analysisIdx = 0
                    const showNextAnalysis = () => {
                      if (analysisIdx >= 4) {
                        setTimeout(() => {
                          this.revealSection(`daily-brief-section-5-${replyId}`, '')
                          setTimeout(() => {
                            let actionIdx = 0
                            const showNextAction = () => {
                              if (actionIdx >= _actionItems.length) {
                                setTimeout(
                                  () =>
                                    this.revealSection(
                                      `daily-brief-section-6-${replyId}`,
                                      'flex'
                                    ),
                                  300
                                )
                                return
                              }
                              setTimeout(() => {
                                const el = this._findVisibleEl(
                                  `brief-action-${actionIdx}-${replyId}`
                                )
                                if (el) {
                                  el.style.display = 'flex'
                                  el.style.opacity = '1'
                                  el.style.transform = 'translateX(0)'
                                }
                                setTimeout(() => {
                                  this.typeWriterEffect(
                                    `brief-action-text-${actionIdx}-${replyId}`,
                                    _actionItems[actionIdx].text,
                                    () => {
                                      actionIdx++
                                      showNextAction()
                                    }
                                  )
                                }, 240)
                              }, 200)
                            }
                            showNextAction()
                          }, 240)
                        }, 200)
                        return
                      }
                      setTimeout(() => {
                        const el = this._findVisibleEl(
                          `brief-analysis-${analysisIdx}-${replyId}`
                        )
                        if (el) {
                          el.style.display = 'flex'
                          el.style.opacity = '1'
                          el.style.transform = 'translateX(0)'
                        }
                        setTimeout(() => {
                          this.typeWriterEffect(
                            `brief-analysis-text-${analysisIdx}-${replyId}`,
                            analysisItems[analysisIdx],
                            () => {
                              analysisIdx++
                              showNextAnalysis()
                            }
                          )
                        }, 240)
                      }, 200)
                    }
                    showNextAnalysis()
                  }, 300)
                  return
                }
                setTimeout(() => {
                  const cellEl = this._findVisibleEl(
                    `brief-metric-${metricIdx}-${replyId}`
                  )
                  if (cellEl) {
                    cellEl.style.display = 'block'
                    cellEl.style.opacity = '1'
                    cellEl.style.transform = 'translateY(0)'
                  }
                  const m = keyMetrics[metricIdx]
                  this.typeWriterEffect(
                    `brief-metric-val-${metricIdx}-${replyId}`,
                    m.value,
                    () => {
                      setTimeout(() => {
                        this.typeWriterEffect(
                          `brief-metric-lbl-${metricIdx}-${replyId}`,
                          m.label,
                          () => {
                            metricIdx++
                            showNextMetric()
                          }
                        )
                      }, 150)
                    }
                  )
                }, 150)
              }
              showNextMetric()
            }
          )
        }, 240)
      })
    },
    renderCsoEnterpriseBriefStreamCard(config) {
      const replyId = `cso-brief-stream-${Date.now()}`
      const summary = config.summary || {}
      const metrics = config.keyMetrics || []
      const risks = config.risks || []
      const refs = config.references || []
      const riskCount = summary.riskCount || risks.length || 0
      const overviewText =
        summary.overviewText || summary.resultText || '今日供应链运营整体平稳。'

      const section1Id = `cso-brief-summary-${replyId}`
      const section1 = `
        <div id="cso-brief-section-1-${replyId}" style="display:none;padding:14px 15px;border-radius:12px;background:linear-gradient(135deg,rgba(74,95,243,0.08),rgba(14,165,233,0.05));border:1px solid rgba(74,95,243,0.16);opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px;">
            <div style="font-size:14px;font-weight:700;color:#1f2937;">供应链全局简报</div>
            <span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:#eef4ff;color:#4A5FF3;font-size:11px;font-weight:600;">${riskCount} 风险并行</span>
          </div>
          <div id="${section1Id}" style="font-size:12px;color:#5a6b7e;line-height:1.8;min-height:22px;"></div>
        </div>
      `

      const sparkPath = (trend, w = 60, h = 18) => {
        const d =
          trend === 'down'
            ? `M0,4 L12,3 L24,5 L36,4 L48,7 L60,12`
            : trend === 'up'
              ? `M0,12 L12,10 L24,8 L36,6 L48,4 L60,2`
              : `M0,8 L12,7 L24,8 L36,7 L48,8 L60,7`
        return d
      }
      const sparkColor = (status) =>
        status === 'normal' ? '#10b981' : status === 'warning' ? '#f59e0b' : '#ef4444'
      const statusMeta = {
        normal: { label: '正常', bg: '#e8f5ec', color: '#16a34a' },
        warning: { label: '预警', bg: '#fff7ed', color: '#f59e0b' },
        risk: { label: '风险', bg: '#fef2f2', color: '#dc2626' }
      }

      const metricCards = metrics
        .map((m, mi) => {
          const meta = statusMeta[m.status] || statusMeta.normal
          const c = sparkColor(m.status)
          const d = sparkPath(m.spark)
          return `<div id="cso-brief-metric-${mi}-${replyId}" style="display:none;display:block;padding:12px 10px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;box-shadow:0 1px 3px rgba(15,23,42,0.04);opacity:0;transform:translateY(6px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:6px;">
            <div id="cso-brief-metric-val-${mi}-${replyId}" style="font-size:20px;font-weight:800;color:#1f2937;line-height:1;min-height:26px;"></div>
            <svg width="60" height="18" viewBox="0 0 60 18"><path d="${d}" stroke="${c}" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </div>
          <div style="margin-top:6px;display:flex;align-items:center;justify-content:space-between;gap:6px;">
            <span id="cso-brief-metric-lbl-${mi}-${replyId}" style="font-size:12px;color:#5a6b7e;min-height:16px;"></span>
            <span style="font-size:11px;color:#94a3b8;font-weight:600;">${
  m.change || ''
}</span>
          </div>
          <div style="margin-top:6px;display:inline-flex;align-items:center;padding:2px 8px;border-radius:999px;background:${
  meta.bg
};color:${meta.color};font-size:11px;font-weight:600;">${meta.label}</div>
        </div>`
        })
        .join('')

      const section2 = `
        <div id="cso-brief-section-2-${replyId}" style="display:none;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          ${metricCards}
        </div>
      `

      const riskBars = risks
        .map((r, ri) => {
          const pct = Math.max(0, Math.min(100, Number(r.percent) || 0))
          const color = r.color || '#4A5FF3'
          return `<div id="cso-brief-risk-${ri}-${replyId}" style="display:none;display:block;opacity:0;transform:translateX(-6px);transition:all .32s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:6px;">
            <span style="font-size:12px;font-weight:600;color:#1f2937;">${r.name}</span>
            <span style="font-size:11px;color:#5a6b7e;font-weight:600;">${r.status}</span>
          </div>
          <div style="height:8px;border-radius:999px;background:#eef2f7;overflow:hidden;position:relative;">
            <div id="cso-brief-risk-bar-${ri}-${replyId}" data-target="${pct}" style="position:absolute;left:0;top:0;height:100%;width:0%;border-radius:999px;background:${color};transition:width 1.6s ease;"></div>
          </div>
        </div>`
        })
        .join('')

      const section3 = `
        <div id="cso-brief-section-3-${replyId}" style="display:none;flex-direction:column;gap:14px;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          ${riskBars}
        </div>
      `

      const safeRefs = refs.slice(0, 3)
      const refItems = safeRefs
        .map((r, ri) => {
          return `<div id="cso-brief-ref-${ri}-${replyId}" style="display:none;display:flex;align-items:flex-start;gap:8px;padding:8px 0;border-bottom:${
            ri < safeRefs.length - 1 ? '1px solid #f1f5f9' : 'none'
          };opacity:0;transform:translateX(-6px);transition:all .32s ease;">
          <span style="font-size:13px;color:#4A5FF3;line-height:1.5;flex-shrink:0;">❝</span>
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-size:11px;color:#94a3b8;font-weight:600;min-width:14px;">[${
  ri + 1
}]</span>
              <span style="font-size:12px;font-weight:600;color:#1f2937;">${r.tag}</span>
              <span style="font-size:11px;color:#94a3b8;">${r.date || ''}</span>
            </div>
            <div style="font-size:11px;color:#5a6b7e;margin-top:4px;padding-left:22px;">${
  r.desc || ''
}</div>
          </div>
        </div>`
        })
        .join('')

      const section4 = `
        <div id="cso-brief-section-4-${replyId}" style="display:none;flex-direction:column;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="font-size:13px;font-weight:700;color:#1f2937;margin-bottom:10px;display:flex;align-items:center;gap:6px;">
            <span style="color:#4A5FF3;">❝</span>引用来源
          </div>
          ${refItems}
        </div>
      `

      const followUpToken = `cso-follow-up-${replyId}`
      this.followUpRegistry = this.followUpRegistry || {}
      this.followUpRegistry[followUpToken] = {
        title: '追问供应链全局简报',
        summary:
          '基于刚才供应链全局简报的结论，请继续展开说明今天最需要优先处置的风险、责任人和落地动作。',
        prompt:
          '基于刚才供应链全局简报的结论，请继续展开说明今天最需要优先处置的风险、责任人和落地动作。'
      }

      const followUpHtml = `
        <div id="cso-brief-section-5-${replyId}" style="display:none;flex-direction:column;margin-top:14px;gap:8px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <span style="font-size:12px;color:#4A5FF3;cursor:pointer;text-decoration:underline;" onclick="window.triggerCeoFollowUp('${followUpToken}')">追问</span>
        </div>
      `

      const cardHtml = `
        <div data-ceo-card="shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px 16px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div style="font-size:15px;font-weight:700;color:#1f2937;">每日供应链全局简报</div>
            <div style="margin-top:8px;font-size:13px;line-height:1.7;color:#5a6b7e;width:100%;">CSO 数字人已完成供应链晨间总览，正在分段输出正式简报。</div>
            <div id="cso-brief-stream-${replyId}" style="margin-top:2px;display:flex;flex-direction:column;gap:12px;">
              ${section1}${section2}${section3}${section4}${followUpHtml}
            </div>
          </div>
        </div>
      `

      this.appendLocalMessage('assistant', cardHtml, true)

      setTimeout(() => {
        this.revealSection(`cso-brief-section-1-${replyId}`, '')
        setTimeout(() => {
          this.typeWriterEffect(section1Id, overviewText, () => {
            setTimeout(() => {
              this.revealSection(`cso-brief-section-2-${replyId}`, 'grid')
              let mi = 0
              const showNextMetric = () => {
                if (mi >= metrics.length) {
                  setTimeout(() => {
                    this.revealSection(`cso-brief-section-3-${replyId}`, 'flex')
                    setTimeout(() => {
                      let ri = 0
                      // 风险逐项淡入（卡内 640ms 紧凑节奏）
                      const showNextRisk = () => {
                        if (ri >= risks.length) {
                          setTimeout(() => {
                            this.revealSection(`cso-brief-section-4-${replyId}`, 'flex')
                            setTimeout(() => {
                              let fi = 0
                              // (refs loop — 200ms after title)
                              const showNextRef = () => {
                                if (fi >= safeRefs.length) {
                                  setTimeout(() => {
                                    this.revealSection(
                                      `cso-brief-section-5-${replyId}`,
                                      'flex'
                                    )
                                  }, 1200)
                                  return
                                }
                                setTimeout(() => {
                                  const el = this._findVisibleEl(
                                    `cso-brief-ref-${fi}-${replyId}`
                                  )
                                  if (el) {
                                    el.style.display = 'flex'
                                    el.style.opacity = '1'
                                    el.style.transform = 'translateX(0)'
                                  }
                                  fi++
                                  showNextRef()
                                }, 560)
                              }
                              showNextRef()
                            }, 200)
                          }, 1200)
                          return
                        }
                        setTimeout(() => {
                          const wrap = this._findVisibleEl(
                            `cso-brief-risk-${ri}-${replyId}`
                          )
                          const bar = this._findVisibleEl(
                            `cso-brief-risk-bar-${ri}-${replyId}`
                          )
                          if (wrap) {
                            wrap.style.display = 'block'
                            // 强制 reflow，触发 transition
                            void wrap.offsetWidth
                            wrap.style.opacity = '1'
                            wrap.style.transform = 'translateX(0)'
                          }
                          if (bar) {
                            const target = bar.dataset.target
                              ? Number(bar.dataset.target)
                              : 0
                            // 下一帧开始动画
                            requestAnimationFrame(() => {
                              bar.style.width = `${target}%`
                            })
                          }
                          ri++
                          showNextRisk()
                        }, 640)
                      }
                      showNextRisk()
                    }, 200)
                  }, 1200)
                  return
                }
                setTimeout(
                  () => {
                    const el = this._findVisibleEl(`cso-brief-metric-${mi}-${replyId}`)
                    if (el) {
                      el.style.display = 'block'
                      el.style.opacity = '1'
                      el.style.transform = 'translateY(0)'
                    }
                    setTimeout(() => {
                      this.typeWriterEffect(
                        `cso-brief-metric-val-${mi}-${replyId}`,
                        metrics[mi].value,
                        () => {
                          this.typeWriterEffect(
                            `cso-brief-metric-lbl-${mi}-${replyId}`,
                            metrics[mi].label,
                            () => {
                              mi++
                              showNextMetric()
                            }
                          )
                        }
                      )
                    }, 200)
                  },
                  mi === 0 ? 0 : 440
                )
              }
              showNextMetric()
            }, 1200)
          })
        }, 640)
      }, 520)
    },
    revealSection(sectionId, displayType, retryCount = 0) {
      const section = this._findVisibleEl(sectionId)
      if (section) {
        if (displayType !== undefined) {
          section.style.display = displayType
          void section.offsetHeight
        }
        this.scrollSmartChatToBottom()
        window.requestAnimationFrame(() => {
          section.style.opacity = '1'
          section.style.transform = 'translateY(0)'
          this.scrollSmartChatToBottom()
        })
      } else if (retryCount < 5) {
        setTimeout(() => {
          this.revealSection(sectionId, displayType, retryCount + 1)
        }, 200)
      } else {
        this.scrollSmartChatToBottom()
      }
    },
    typeWriterEffect(elementId, text, onComplete, retryCount = 0, speed = 35) {
      const element = this._findVisibleEl(elementId)
      if (!element) {
        if (retryCount < 5) {
          setTimeout(() => {
            this.typeWriterEffect(elementId, text, onComplete, retryCount + 1, speed)
          }, 200)
          return
        }
        if (onComplete) onComplete()
        return
      }
      element.innerHTML = ''
      let index = 0
      const self = this
      function type() {
        if (index < text.length) {
          element.innerHTML += text.charAt(index)
          index++
          if (index % 8 === 0) self.scrollSmartChatToBottom()
          setTimeout(type, speed)
        } else {
          self.scrollSmartChatToBottom()
          if (onComplete) onComplete()
        }
      }
      type()
    },
    // 智能 typewriter：HTML 标签原子插入，纯文本逐字输出
    smartTypeWriterEffect(elementId, htmlText, onComplete, retryCount = 0, speed = 35) {
      const element = this._findVisibleEl(elementId)
      if (!element) {
        if (retryCount < 5) {
          setTimeout(() => {
            this.smartTypeWriterEffect(
              elementId,
              htmlText,
              onComplete,
              retryCount + 1,
              speed
            )
          }, 200)
          return
        }
        if (onComplete) onComplete()
        return
      }
      element.innerHTML = ''
      // 将文本拆分为 text 段和 html 段
      const segments = []
      let remaining = htmlText
      while (remaining.length > 0) {
        const tagStart = remaining.indexOf('<')
        if (tagStart === -1) {
          segments.push({ type: 'text', content: remaining })
          break
        }
        if (tagStart > 0) {
          segments.push({ type: 'text', content: remaining.slice(0, tagStart) })
        }
        const tagEnd = remaining.indexOf('>', tagStart)
        if (tagEnd === -1) {
          segments.push({ type: 'text', content: remaining.slice(tagStart) })
          break
        }
        segments.push({
          type: 'html',
          content: remaining.slice(tagStart, tagEnd + 1)
        })
        remaining = remaining.slice(tagEnd + 1)
      }
      let segIdx = 0
      let charIdx = 0
      const self = this
      const typeNext = () => {
        if (segIdx >= segments.length) {
          self.scrollSmartChatToBottom()
          if (onComplete) onComplete()
          return
        }
        const seg = segments[segIdx]
        if (seg.type === 'html') {
          element.insertAdjacentHTML('beforeend', seg.content)
          segIdx++
          charIdx = 0
          self.scrollSmartChatToBottom()
          setTimeout(typeNext, 0)
        } else {
          if (charIdx < seg.content.length) {
            element.insertAdjacentText('beforeend', seg.content[charIdx])
            charIdx++
            if (charIdx % 8 === 0) self.scrollSmartChatToBottom()
            setTimeout(typeNext, speed)
          } else {
            segIdx++
            charIdx = 0
            self.scrollSmartChatToBottom()
            setTimeout(typeNext, 0)
          }
        }
      }
      typeNext()
    },
    handleCeoFollowUp(token) {
      const reference =
        (this.followUpRegistry && this.followUpRegistry[token]) ||
        (window.followUpRegistry && window.followUpRegistry[token])
      if (!reference) return
      if (window.setCeoDraftReference) {
        window.setCeoDraftReference(reference)
      }
    },
    renderFollowUpCard(suggestions) {
      const chipsHtml = (suggestions || [])
        .map((s, i) => {
          return `<button type="button" data-follow-up="${i}" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:999px;background:#fff;border:1px solid rgba(74,95,243,.2);font-size:11px;color:#4a5ff3;cursor:pointer;transition:all .15s;" onclick="window.triggerCeoFollowUp('followup-${i}')">
          <i class="fa fa-question-circle" style="font-size:10px;"></i>
          <span>${s.question}</span>
        </button>`
        })
        .join('')

      return `<div style="padding:12px 14px;border-radius:10px;background:linear-gradient(135deg,#f0f7ff,#f8fbff);border:1px solid rgba(74,95,243,.15);">
        <div style="font-size:11px;font-weight:600;color:#4a5ff3;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
          <i class="fa fa-lightbulb-o" style="font-size:11px;"></i>
          <span>您可以继续追问</span>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">${chipsHtml}</div>
      </div>`
    },
    renderMetricCards(metrics) {
      return `<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;">
        ${metrics
    .map((m) => {
      const trendColor =
              m.trendClass === 'up'
                ? '#16a34a'
                : m.trendClass === 'down'
                  ? '#dc2626'
                  : '#f59e0b'
      return `
            <div style="padding:14px;border-radius:10px;background:#f8fafc;border:1px solid #e8ecf0;text-align:center;">
              <div style="font-size:20px;font-weight:700;color:${trendColor};margin-bottom:4px;">${m.value}</div>
              <div style="font-size:12px;color:#5a6b7e;">${m.name}</div>
              <div style="font-size:11px;color:${trendColor};margin-top:4px;">${m.trend}</div>
            </div>
          `
    })
    .join('')}
      </div>`
    },
    renderCsoPendingBomStreamCard() {
      const replyId = `cso-bom-stream-${Date.now()}`
      const overviewId = `cso-bom-overview-${replyId}`
      const timelineCount = 4
      const sections = ['header', 'materials', 'timeline', 'matrix', 'refs', 'decision']
      const sectionIds = sections.map((s) => `${s}-${replyId}`)

      const materials = [
        {
          name: 'PCB',
          percent: 8.2,
          color: '#ef4444',
          valueText: '8.2 %',
          fillPercent: 49
        },
        {
          name: '铜',
          percent: 5.4,
          color: '#f59e0b',
          valueText: '5.4 %',
          fillPercent: 32
        },
        {
          name: '锡',
          percent: 4.1,
          color: '#3b82f6',
          valueText: '4.1 %',
          fillPercent: 25
        }
      ]

      const timeline = [
        {
          time: '08:00',
          name: '财务总览',
          skill: 'bom_alert',
          text:
            '财务总览反馈：BOM 成本上行，PCB 8.2% / 铜 5.4% / 锡 4.1%，供应商集中度 87%-92%。'
        },
        {
          time: '08:15',
          name: '成本归因',
          skill: 'cost_attribution',
          text:
            '涨跌归因分析：PCB 主因铜箔+环氧树脂传导；铜主因 LME 上行；锡主因缅甸选矿减产。年新增成本约 1240 万，影响毛利率约 0.9pct。'
        },
        {
          time: '08:30',
          name: '寻源采购',
          skill: 'lock_eval',
          text:
            '锁价可行性评估：供应链已正式致函涨价，集中度 87%-92% 议价能力弱，锁价/保价/空间为 0。'
        },
        {
          time: '08:45',
          name: '经营决策智能体',
          skill: 'pass_through_decision',
          text:
            '供应链利润无降价空间，内部消化难再降毛利 0.5pct 不可持续。建议将成本传导至客户，进入客户调价方案评估流程。'
        }
      ]

      const matrix = {
        head: ['评估维度', 'PCB', '铜', '锡'],
        rows: [
          ['涨幅', '8.2%', '5.4%', '4.1%'],
          ['供应商集中度', '92%', '87%', '87%'],
          ['锁价空间', '0', '0', '0'],
          ['主因', '铜箔+树脂传导', 'LME 上行', '缅甸减产']
        ]
      }

      const refs = [
        {
          tag: 'BOM成本指数',
          time: '2026-06-25 08:15',
          desc: 'PCB 8.2% / 铜 5.4% / 锡 4.1%'
        },
        { tag: '供应商集中度分析', time: '2026-06-25 08:20', desc: '87%-92%' },
        {
          tag: '锁货评估',
          time: '2026-06-25 08:30',
          desc: '供应商已发函，无锁价空间'
        },
        {
          tag: '传导决策提示',
          time: '2026-06-25 08:45',
          desc: '建议传导至客户'
        },
        {
          tag: '隆众资讯-有色金属（铜）',
          time: '2026-06-25 08:10',
          desc: 'LME 铜价走势 + 国内电解铜现货价参考，作为涨价归因外部溯源'
        }
      ]

      const headerHtml = `
        <div id="${sectionIds[0]}" style="display:none;padding:14px 15px;border-radius:12px;background:linear-gradient(135deg,rgba(74,95,243,0.08),rgba(14,165,233,0.05));border:1px solid rgba(74,95,243,0.16);opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px;">
            <div style="font-size:14px;font-weight:700;color:#1f2937;">BOM 成本上行预警</div>
            <span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:#FFF1F2;color:#F43F5E;font-size:11px;font-weight:700;border:1px solid #FECDD3;">利空</span>
          </div>
          <div id="${overviewId}" style="font-size:12px;color:#5a6b7e;line-height:1.8;min-height:22px;"></div>
        </div>
      `

      const materialsHtml = `
        <div id="${
  sectionIds[1]
}" style="display:none;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:10px;flex-wrap:wrap;">
            <div style="font-size:13px;font-weight:700;color:#1f2937;">BOM 三大物料涨幅幅度 <span style="font-size:11px;color:#94a3b8;font-weight:500;">[1]</span></div>
            <span style="font-size:11px;color:#94a3b8;">数据来源：ERP + 供应商函告 2026-06-25</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:10px;">
            ${materials
    .map(
      (m, mi) => `
              <div id="cso-bom-bar-${mi}-${replyId}" style="display:none;opacity:0;transform:translateX(-6px);transition:all .32s ease;">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:6px;">
                  <span style="font-size:12px;font-weight:600;color:#1f2937;">${
  m.name
}</span>
                  <span style="font-size:12px;font-weight:700;color:${m.color};">${
  m.valueText
}</span>
                </div>
                <div style="position:relative;height:8px;border-radius:999px;background:#eef2f7;overflow:hidden;">
                  <div id="cso-bom-bar-fill-${mi}-${replyId}" data-target="${
  m.fillPercent != null
    ? m.fillPercent
    : Math.min(100, Number(m.percent) * 6)
}" data-color="${
  m.color
}" style="position:absolute;left:0;top:0;height:100%;width:0%;border-radius:999px;background:${
  m.color
};transition:width 1.6s ease;"></div>
                </div>
              </div>
            `
    )
    .join('')}
          </div>
        </div>
      `

      const timelineHtml = `
        <div id="${
  sectionIds[2]
}" style="display:none;padding:14px;border-radius:12px;background:linear-gradient(180deg,#FAFBFF 0%,#F4F7FF 100%);border:1px solid rgba(74,95,243,0.18);opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:12px;">
            <div style="display:flex;align-items:center;gap:6px;">
              <span style="display:inline-block;width:4px;height:14px;border-radius:2px;background:linear-gradient(180deg,#4A5FF3,#7c3aed);"></span>
              <span style="font-size:13px;font-weight:700;color:#1f2937;">BOM 涨价归因与传导评估证据链</span>
            </div>
            <span style="font-size:11px;color:#94a3b8;">${timelineCount} 步</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:14px;position:relative;">
            ${timeline
    .map(
      (t, ti) => `
              <div id="cso-bom-step-${ti}-${replyId}" style="display:none;opacity:0;transform:translateX(-6px);transition:all .32s ease;display:flex;gap:10px;">
                <div style="flex-shrink:0;width:42px;font-size:11px;color:#94a3b8;font-weight:600;text-align:right;padding-top:2px;">${
  t.time
}</div>
                <div style="flex-shrink:0;display:flex;flex-direction:column;align-items:center;">
                  <div style="width:14px;height:14px;border-radius:50%;background:${
  ti === timeline.length - 1 ? '#4A5FF3' : '#10b981'
};border:2px solid #fff;box-shadow:0 0 0 2px ${
  ti === timeline.length - 1
    ? 'rgba(74,95,243,0.25)'
    : 'rgba(16,185,129,0.25)'
};"></div>
                  ${
  ti < timeline.length - 1
    ? '<div style="width:2px;flex:1;background:linear-gradient(180deg,#10b98140,#10b98110);margin-top:4px;"></div>'
    : ''
}
                </div>
                <div style="flex:1;min-width:0;">
                  <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                    <span style="font-size:12px;font-weight:700;color:#1f2937;">${
  t.name
}</span>
                    <span style="font-size:11px;color:#94a3b8;font-family:Menlo,Monaco,monospace;">${
  t.skill
}</span>
                  </div>
                  <div style="margin-top:4px;padding:8px 10px;border-radius:8px;background:#fff;border:1px solid #EEF2F7;font-size:12px;color:#4b5563;line-height:1.7;">
                    ${t.text}
                  </div>
                </div>
              </div>
            `
    )
    .join('')}
          </div>
        </div>
      `

      const matrixHtml = `
        <div id="${
  sectionIds[3]
}" style="display:none;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:grid;grid-template-columns:88px repeat(3,minmax(0,1fr));gap:0;font-size:12px;">
            <div style="padding:8px 6px;border-bottom:1px solid #e8ecf0;background:#F8FAFC;font-weight:700;color:#475569;">评估维度</div>
            ${matrix.head
    .slice(1)
    .map(
      (h) =>
        `<div style="padding:8px 6px;border-bottom:1px solid #e8ecf0;background:#F8FAFC;font-weight:700;color:#475569;text-align:center;">${h}</div>`
    )
    .join('')}
            ${matrix.rows
    .map(
      (row) => `
              <div style="padding:8px 6px;border-bottom:1px solid #F1F5F9;color:#475569;font-weight:600;">${
  row[0]
}</div>
              ${row
    .slice(1)
    .map(
      (cell) =>
        `<div style="padding:8px 6px;border-bottom:1px solid #F1F5F9;text-align:center;color:#1f2937;">${cell}</div>`
    )
    .join('')}
            `
    )
    .join('')}
          </div>
        </div>
      `

      const refsHtml = `
        <div id="${
  sectionIds[4]
}" style="display:none;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="font-size:13px;font-weight:700;color:#1f2937;margin-bottom:10px;display:flex;align-items:center;gap:6px;">
            <span style="color:#4A5FF3;">❝</span>引用来源
          </div>
          <div style="display:flex;flex-direction:column;">
            ${refs
    .map(
      (r, ri) => `
              <div id="cso-bom-ref-${ri}-${replyId}" style="display:none;display:flex;align-items:flex-start;gap:8px;padding:8px 0;${
  ri < refs.length - 1 ? 'border-bottom:1px solid #F1F5F9;' : ''
}opacity:0;transform:translateX(-6px);transition:all .32s ease;">
                <span style="font-size:13px;color:#4A5FF3;line-height:1.5;flex-shrink:0;">❝</span>
                <div style="flex:1;min-width:0;">
                  <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                    <span style="font-size:11px;color:#94a3b8;font-weight:600;">[${
  ri + 1
}]</span>
                    <span style="font-size:12px;font-weight:600;color:#1f2937;">${
  r.tag
}</span>
                    <span style="font-size:11px;color:#94a3b8;">${r.time}</span>
                  </div>
                  <div style="font-size:11px;color:#5a6b7e;margin-top:4px;padding-left:22px;">${
  r.desc
}</div>
                </div>
              </div>
            `
    )
    .join('')}
          </div>
        </div>
      `

      const decisionHtml = `
        <div id="${sectionIds[5]}" style="display:none;flex-direction:column;gap:10px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="padding:12px 14px;border-radius:12px;background:#FFF8EC;border:1px solid #FDE7B5;">
            <div style="font-size:13px;font-weight:700;color:#B45309;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
              <span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#F59E0B;color:#fff;font-size:11px;font-weight:700;line-height:1;">!</span>
              是否将供应商成本传导至客户？
            </div>
            <div style="font-size:12px;color:#5a6b7e;line-height:1.7;">供应链利润已确认无降价空间，内部消化难再降毛利 0.5pct 不可持续。建议进入<strong style="color:#4A5FF3;">供应商涨价传导 → 客户调价方案评估</strong>流程，生成分波调价方案。</div>
          </div>
          <div style="display:flex;gap:10px;flex-wrap:wrap;">
            <button id="cso-bom-decision-yes-${replyId}" style="flex:1;min-width:0;padding:8px 12px;border:none;border-radius:10px;background:linear-gradient(270deg,#4A5FF3 0%,#4E86FF 100%);color:#fff;font-size:11px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:4px;white-space:nowrap;letter-spacing:0;" onclick="window.__csoBomYes && window.__csoBomYes()">
              是，进入客户调价评估
            </button>
            <button id="cso-bom-decision-no-${replyId}" style="flex:1;min-width:0;padding:8px 12px;border:1px solid #D9D9D9;border-radius:10px;background:#fff;color:#1f2937;font-size:11px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:4px;white-space:nowrap;letter-spacing:0;" onclick="window.__csoBomNo && window.__csoBomNo()">
              否，暂不传导
            </button>
          </div>
          <a href="https://youse.oilchem.net/youse/copper.shtml" target="_blank" rel="noopener noreferrer" style="text-decoration:none;padding:8px 12px;border-radius:10px;background:#F4F7FF;border:1px dashed rgba(74,95,243,0.35);font-size:12px;color:#4A5FF3;display:flex;align-items:center;justify-content:space-between;cursor:pointer;">
            <span style="display:flex;align-items:center;gap:6px;">隆众资讯 · 有色金属（铜）</span>
            <span style="font-size:11px;">›</span>
          </a>
        </div>
      `

      const cardHtml = `
        <div data-ceo-card="shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px 16px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div id="cso-bom-stream-${replyId}" style="margin-top:0;display:flex;flex-direction:column;gap:12px;">
              ${headerHtml}${materialsHtml}${timelineHtml}${matrixHtml}${refsHtml}${decisionHtml}
            </div>
          </div>
        </div>
      `

      this.appendLocalMessage('assistant', cardHtml, true)

      setTimeout(() => {
        this.revealSection(sectionIds[0], '')
        setTimeout(() => {
          this.typeWriterEffect(
            overviewId,
            '财务总览反馈 BOM 成本上行:PCB 8.2% / 铜 5.4% / 锡 4.1%,供应商集中度 87%-92%。年新增成本约 1240 万,影响毛利率约 0.9pct。',
            () => {
              setTimeout(() => {
                this.revealSection(sectionIds[1], '')
                setTimeout(() => {
                  let mi = 0
                  const showNextMat = () => {
                    if (mi >= materials.length) {
                      setTimeout(() => {
                        this.revealSection(sectionIds[2], '')
                        setTimeout(() => {
                          let ti = 0
                          const showNextStep = () => {
                            if (ti >= timeline.length) {
                              setTimeout(() => {
                                this.revealSection(sectionIds[3], '')
                                setTimeout(() => {
                                  this.revealSection(sectionIds[4], '')
                                  setTimeout(() => {
                                    // 引用来源逐项淡入（首条 120ms 快速衔接，后续 560ms 紧凑节奏）
                                    let ri = 0
                                    const showNextRef = () => {
                                      if (ri >= refs.length) {
                                        setTimeout(() => {
                                          this.revealSection(sectionIds[5], 'flex')
                                        }, 1200)
                                        return
                                      }
                                      setTimeout(
                                        () => {
                                          const el = this._findVisibleEl(
                                            `cso-bom-ref-${ri}-${replyId}`
                                          )
                                          if (el) {
                                            el.style.display = 'flex'
                                            el.style.opacity = '1'
                                            el.style.transform = 'translateX(0)'
                                          }
                                          ri++
                                          showNextRef()
                                        },
                                        ri === 0 ? 120 : 560
                                      )
                                    }
                                    showNextRef()
                                  }, 120)
                                }, 1200)
                              }, 1200)
                              return
                            }
                            setTimeout(() => {
                              const el = this._findVisibleEl(
                                `cso-bom-step-${ti}-${replyId}`
                              )
                              if (el) {
                                el.style.display = 'flex'
                                el.style.opacity = '1'
                                el.style.transform = 'translateX(0)'
                              }
                              ti++
                              showNextStep()
                            }, 560)
                          }
                          showNextStep()
                        }, 300)
                      }, 1200)
                      return
                    }
                    setTimeout(() => {
                      const wrap = this._findVisibleEl(`cso-bom-bar-${mi}-${replyId}`)
                      const fill = this._findVisibleEl(
                        `cso-bom-bar-fill-${mi}-${replyId}`
                      )
                      if (wrap) {
                        wrap.style.display = 'block'
                        // 强制 reflow，触发 transition
                        void wrap.offsetWidth
                        wrap.style.opacity = '1'
                        wrap.style.transform = 'translateX(0)'
                      }
                      if (fill) {
                        // 先确认 fill 在视觉上可见，并把初始 width 锁住
                        fill.style.width = '0%'
                        if (fill.dataset.color) {
                          fill.style.background = fill.dataset.color
                        }
                        const target = fill.dataset.target
                          ? Number(fill.dataset.target)
                          : materials[mi].fillPercent != null
                            ? materials[mi].fillPercent
                            : Math.min(100, Number(materials[mi].percent) * 6)
                        // 下一帧开始动画
                        requestAnimationFrame(() => {
                          fill.style.width = `${target}%`
                        })
                      }
                      mi++
                      showNextMat()
                    }, 600)
                  }
                  showNextMat()
                }, 400)
              }, 1200)
            }
          )
        }, 640)
      }, 520)

      // 绑定决策按钮交互：否 → 内部消化方案；是 → 客户调价评估
      this.bindCsoBomDecisionHandlers(replyId)
    },
    bindCsoBomDecisionHandlers(replyId) {
      const self = this
      const yesBtn = this._findVisibleEl(`cso-bom-decision-yes-${replyId}`)
      const noBtn = this._findVisibleEl(`cso-bom-decision-no-${replyId}`)
      const lockButtons = () => {
        [yesBtn, noBtn].forEach((b) => {
          if (!b) return
        })
      }
      window.__csoBomYes = () => {
        lockButtons()
        self.appendLocalMessage('user', '是，进入客户调价方案评估')
        setTimeout(() => {
          self.renderCsoCustomerAdjustStreamCard()
        }, 320)
      }
      window.__csoBomNo = () => {
        lockButtons()
        self.appendLocalMessage('user', '否，暂不传导（内部消化）')
        setTimeout(() => {
          self.renderCsoPlanSummaryCard('internal')
        }, 320)
      }
    },
    renderCsoInternalAbsorbCards() {
      const replyId = `cso-absorb-stream-${Date.now()}`
      const introId = `cso-absorb-intro-${replyId}`
      const notifyPanelId = `cso-absorb-notify-${replyId}`
      const letterPanelId = `cso-absorb-letter-panel-${replyId}`

      const sectionIds = ['plan', 'letters']
      const planSectionId = `${sectionIds[0]}-${replyId}`
      const lettersSectionId = `${sectionIds[1]}-${replyId}`

      const kpis = [
        {
          label: '客户调价',
          value: '0%',
          suffix: '（零传导）',
          color: '#4A5FF3'
        },
        {
          label: '内部降本目标',
          value: '1,860 万',
          suffix: '',
          color: '#16a34a'
        },
        {
          label: '毛利率影响',
          value: '再降 1.1pct',
          suffix: '',
          color: '#dc2626'
        },
        {
          label: '风险等级',
          value: '高',
          suffix: '（不可持续）',
          color: '#f59e0b'
        }
      ]

      const planHtml = `
        <div id="${planSectionId}" style="display:none;border-radius:12px;border:1px solid rgba(74,95,243,0.18);background:linear-gradient(135deg,#EEF2FF 0%,#F5F3FF 100%);padding:14px 15px;box-shadow:0 2px 8px rgba(74,95,243,0.05);opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;flex-wrap:wrap;">
            <span style="display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:999px;background:linear-gradient(270deg,#4A5FF3,#7c3aed);color:#fff;font-size:11px;font-weight:700;">已选方案</span>
            <span style="font-size:14px;font-weight:700;color:#1f2937;">方案三：内部消化（暂不调价）</span>
          </div>
          <div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:rgba(255,255,255,0.7);border:1px solid rgba(74,95,243,0.12);margin-bottom:10px;">
            <span style="font-size:13px;color:#4A5FF3;line-height:1.6;flex-shrink:0;font-weight:700;">"</span>
            <div style="font-size:12px;color:#4b5563;line-height:1.7;">通过采购谈判、工艺优化、库存管理内部消化全部 1,860 万成本，毛利率将再降 1.1pct，仅适合短期客户关系维护场景，不可持续。</div>
          </div>
          <div style="display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin-bottom:10px;">
            ${kpis
    .map(
      (k) => `
              <div style="display:flex;flex-direction:column;align-items:center;justify-content:flex-start;text-align:center;padding:8px 6px;border-radius:10px;background:#fff;border:1px solid #E8ECF5;box-shadow:0 1px 3px rgba(15,23,42,0.04);min-height:74px;box-sizing:border-box;overflow:hidden;">
                <div style="font-size:11px;color:#94a3b8;line-height:1.3;width:100%;">${
  k.label
}</div>
                <div style="font-size:15px;font-weight:800;color:${
  k.color
};line-height:1.2;margin-top:6px;width:100%;word-break:break-all;overflow-wrap:break-word;">${
  k.value
}</div>
                ${
  k.suffix
    ? `<div style="font-size:10px;color:#94a3b8;line-height:1.3;margin-top:3px;width:100%;word-break:break-all;overflow-wrap:break-word;">${k.suffix}</div>`
    : ''
}
              </div>
            `
    )
    .join('')}
          </div>
          <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;border-radius:10px;background:linear-gradient(90deg,#ECFDF5 0%,#F0FDF4 100%);border:1px solid rgba(16,185,129,0.25);">
            <span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#10b981;color:#fff;font-size:11px;font-weight:700;line-height:1;">i</span>
            <span style="font-size:11px;color:#047857;font-weight:600;">共识 · 决策 skill 提示高风险（仅供短期过渡）</span>
          </div>
        </div>
      `

      const lettersHtml = `
        <div id="${lettersSectionId}" style="display:none;border:1px solid #e8ecf0;border-radius:12px;padding:14px 15px;background:#fff;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;flex-wrap:wrap;">
            <div style="display:flex;align-items:center;gap:6px;min-width:0;">
              <span style="display:inline-block;width:4px;height:14px;border-radius:2px;background:linear-gradient(180deg,#4A5FF3,#7c3aed);"></span>
              <span style="font-size:13px;font-weight:700;color:#1f2937;">AI 生成 · 内部消化成本管控通知 + 客户关系维护函</span>
            </div>
            <span style="font-size:11px;color:#10b981;font-weight:600;">已生成 2 份</span>
          </div>
          <div id="${introId}" style="font-size:12px;color:#5a6b7e;line-height:1.7;margin-bottom:12px;">采用内部消化策略，AI 已生成内部成本管控通知（面向采购/质量/生产团队）与客户关系维护函（告知暂不调价但需配合交付节奏）：</div>

          <div style="border:1px solid #e8ecf0;border-radius:10px;margin-bottom:8px;overflow:hidden;background:#FAFBFC;">
            <div id="cso-absorb-notify-head-${replyId}" style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;background:#fff;cursor:pointer;border-bottom:1px solid #e8ecf0;" onclick="window.__toggleAbsorbPanel && window.__toggleAbsorbPanel('${notifyPanelId}', '${replyId}', 'notify')">
              <div style="display:flex;align-items:center;gap:8px;min-width:0;flex:1;">
                <span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:4px;background:#FFF7ED;color:#F59E0B;font-size:11px;font-weight:700;line-height:1;flex-shrink:0;">!</span>
                <span style="font-size:13px;font-weight:700;color:#1f2937;">内部成本管控通知</span>
                <span style="font-size:11px;color:#94a3b8;">（采购/质量/生产团队）</span>
              </div>
              <div style="display:flex;align-items:center;gap:6px;flex-shrink:0;">
                <span style="font-size:11px;color:#4A5FF3;font-weight:600;cursor:pointer;" onclick="event.stopPropagation();window.__toggleAbsorbPanel && window.__toggleAbsorbPanel('${notifyPanelId}', '${replyId}', 'notify')">查看详情</span>
                <span id="cso-absorb-notify-arrow-${replyId}" style="font-size:11px;color:#94a3b8;transition:transform .2s ease;display:inline-block;">▾</span>
              </div>
            </div>
            <div id="${notifyPanelId}" style="display:none;padding:12px 14px;background:#FAFBFC;font-size:12px;color:#4b5563;line-height:1.8;max-height:480px;overflow-y:auto;">
              <div style="margin-bottom:8px;"><strong style="color:#1f2937;">【内部通知】关于发动全员降本应对内部消化专项行动的通知</strong></div>
              <div style="margin-bottom:6px;">致：采购部 / 质控部 / 生产部 / 财务部</div>
              <div style="margin-bottom:10px;">背景：受铜箔(+12%)/MLCC(+8%)/PCB(+15%) 三大原材料涨价影响，年新增成本 1,860 万。经决策层审议，本次不向客户传导涨价，由内部全部消化。</div>
              <div style="margin-bottom:4px;font-weight:700;color:#1f2937;">一、采购部专项行动</div>
              <div style="margin-bottom:2px;padding-left:8px;">1. 与宝钢等核心供应商谈判，争取 6 个月框架协议议价</div>
              <div style="margin-bottom:2px;padding-left:8px;">2. MLCC 启动村田/三星外延二供开发（渠道/保新料）</div>
              <div style="margin-bottom:2px;padding-left:8px;">3. PCB 引入 2 家备选供应商务必完成</div>
              <div style="margin-bottom:6px;padding-left:8px;">目标：年降本 800 万</div>

              <div style="margin-bottom:4px;margin-top:8px;font-weight:700;color:#1f2937;">二、质控部专项行动</div>
              <div style="margin-bottom:2px;padding-left:8px;">1. 加速 MCU 替代料样气飞凌验证（首批 5K 片试果）</div>
              <div style="margin-bottom:2px;padding-left:8px;">2. 涂覆铝箔 PVDF 粘接剂原产替代推进</div>
              <div style="margin-bottom:6px;padding-left:8px;">目标：年降本 240 万</div>

              <div style="margin-bottom:4px;margin-top:8px;font-weight:700;color:#1f2937;">三、生产部专项行动</div>
              <div style="margin-bottom:2px;padding-left:8px;">1. SMT 贴片效率提升 12%（工艺优化）</div>
              <div style="margin-bottom:2px;padding-left:8px;">2. 安全库存从 19 天调整至 22 天缓冲涨价</div>
              <div style="margin-bottom:2px;padding-left:8px;">3. 合并 PO 减少议价频次</div>
              <div style="margin-bottom:6px;padding-left:8px;">目标：年降本 420 万</div>

              <div style="margin-bottom:4px;margin-top:8px;font-weight:700;color:#1f2937;">四、财务部跟踪</div>
              <div style="margin-bottom:2px;padding-left:8px;">每月跟踪毛利率变动（指标：守住 17.1% 不再下探）</div>
              <div style="margin-bottom:6px;padding-left:8px;">若 Q3 毛利率跌破 16%，立即启动客户调价方案预案</div>

              <div style="margin-top:10px;color:#5a6b7e;">本次内部消化压力大，请各部门于 7 月 15 日前提交专项行动计划。</div>
              <div style="margin-top:6px;color:#1f2937;font-weight:600;">CSO Office / 2026-06-22</div>
            </div>
          </div>

          <div style="border:1px solid #e8ecf0;border-radius:10px;overflow:hidden;background:#FAFBFC;">
            <div id="cso-absorb-letter-head-${replyId}" style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;background:#fff;cursor:pointer;" onclick="window.__toggleAbsorbPanel && window.__toggleAbsorbPanel('${letterPanelId}', '${replyId}', 'letter')">
              <div style="display:flex;align-items:center;gap:8px;min-width:0;flex:1;">
                <span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:4px;background:#FEF2F2;color:#dc2626;font-size:11px;font-weight:700;line-height:1;flex-shrink:0;">@</span>
                <span style="font-size:13px;font-weight:700;color:#1f2937;">客户关系维护函</span>
                <span style="font-size:11px;color:#94a3b8;">（告知暂不调价 + 交付配合请求）</span>
              </div>
              <div style="display:flex;align-items:center;gap:6px;flex-shrink:0;">
                <span style="font-size:11px;color:#4A5FF3;font-weight:600;cursor:pointer;" onclick="event.stopPropagation();window.__toggleAbsorbPanel && window.__toggleAbsorbPanel('${letterPanelId}', '${replyId}', 'letter')">查看详情</span>
                <span id="cso-absorb-letter-arrow-${replyId}" style="font-size:11px;color:#94a3b8;transition:transform .2s ease;display:inline-block;">▾</span>
              </div>
            </div>
            <div id="${letterPanelId}" style="display:none;padding:12px 14px;background:#FAFBFC;font-size:12px;color:#4b5563;line-height:1.8;max-height:480px;overflow-y:auto;">
              <div style="margin-bottom:8px;">尊敬的合作伙伴：</div>
              <div style="margin-bottom:6px;">感谢贵司长期以来的信任与支持。</div>
              <div style="margin-bottom:8px;">近期受上游铜箔、MLCC、PCB 供应链价格影响，行业整体面临成本压力。经公司内部谨慎评估，为维护双方长期合作关系，本次决定暂不向贵司传导涨价，由我司内部通过供应策略调整、工艺优化、国产替代等措施全部消化成本。</div>
              <div style="margin-bottom:4px;">为确保持续稳定供货，需要贵司配合以下事项：</div>
              <div style="margin-bottom:2px;padding-left:8px;">1. 月度订单提前 7 天确认，便于我司排产稳定</div>
              <div style="margin-bottom:2px;padding-left:8px;">2. 齐欠 MCU 替代料飞凌片堂进入试产阶段，替代品请贵司协助验证</div>
              <div style="margin-bottom:8px;padding-left:8px;">3. 部分非关键物料（如包装材料）允许我司调整供应商</div>

              <div style="margin-bottom:4px;">我司承诺：</div>
              <div style="margin-bottom:2px;padding-left:8px;">· 未来 6 个月内不进行调价</div>
              <div style="margin-bottom:2px;padding-left:8px;">· 持续共享降本成果，定期提供成本通报报告</div>
              <div style="margin-bottom:8px;padding-left:8px;">· 若上游价格持续稳定，将于 Q4 更新评估调价策略</div>

              <div style="margin-bottom:6px;">期待继续携手共进。</div>
              <div style="font-weight:600;color:#1f2937;">CSO Office</div>
            </div>
          </div>
        </div>
      `

      // 单条 assistant 消息承载整个方案三+AI函件包，与 CEO 对话样式一致
      const cardHtml = `
        <div data-ceo-card="shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px 16px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div id="cso-absorb-stream-${replyId}" style="margin-top:0;display:flex;flex-direction:column;gap:12px;">
              ${planHtml}${lettersHtml}
            </div>
          </div>
        </div>
      `

      this.appendLocalMessage('assistant', cardHtml, true)

      // 串行淡入：先方案三概要，再 AI 生成区
      setTimeout(() => {
        this.revealSection(planSectionId, '')
        setTimeout(() => {
          this.revealSection(lettersSectionId, '')
        }, 240)
      }, 200)

      const self = this
      if (!window.__toggleAbsorbPanel) {
        window.__toggleAbsorbPanel = (panelId, ts, key) => {
          const panel = this._findVisibleEl(panelId)
          const arrowId =
            key === 'notify'
              ? `cso-absorb-notify-arrow-${ts}`
              : `cso-absorb-letter-arrow-${ts}`
          const arrow = this._findVisibleEl(arrowId)
          if (!panel) return
          const opened = panel.style.display !== 'none'
          if (opened) {
            panel.style.display = 'none'
            if (arrow) arrow.style.transform = 'rotate(0deg)'
          } else {
            panel.style.display = 'block'
            if (arrow) arrow.style.transform = 'rotate(180deg)'
            self.scrollSmartChatToBottom()
          }
        }
      }
    },
    renderCsoCustomerAdjustStreamCard() {
      const replyId = `cso-adjust-stream-${Date.now()}`
      const overviewId = `cso-adjust-overview-${replyId}`
      const timelineCount = 4
      const sections = [
        'overview',
        'tier',
        'evidence',
        'planA',
        'planB',
        'planC',
        'refs',
        'choose'
      ]
      const sectionIds = sections.map((s) => `${s}-${replyId}`)

      const tierRows = [
        {
          tier: 'A级战略',
          customer: '海尔、创维',
          price: '3%',
          volume: '290 万',
          risk: '定制批量 + 长期协议'
        },
        {
          tier: 'B类优级',
          customer: '长虹电器、东莞永胜',
          price: '5%',
          volume: '480 万',
          risk: '价格弹性 + 增值服务'
        },
        {
          tier: 'C类短单',
          customer: 'SharkNinja及贸易',
          price: '8%',
          volume: '680 万',
          risk: '标准报价 + AI风险材料'
        }
      ]

      const evidence = [
        {
          time: '07:00',
          name: '成本控制 skill',
          skill: 'price_monitor',
          text:
            '铜箔 LME 现货价 9600 $/t，近 30 天累计涨 18%；宝钢/鞍钢同步上调出厂价 12%。',
          attachment: 'cost_impact_analysis_0625.xlsx'
        },
        {
          time: '07:15',
          name: '寻源采购 skill',
          skill: 'supplier_comm',
          text:
            '收到村田/三星 MLCC 涨价函（8%）+ 交期延长通知（8→12 周）；4 家 PCB 供应商涨价函（15%）。',
          attachment: 'supplier_price_adjust_0625.pdf'
        },
        {
          time: '07:30',
          name: '成本控制 skill',
          skill: 'tco_impact',
          text:
            '全链路成本影响测算：铜箔 +860 万/年，MLCC +420 万/年，PCB +580 万/年，合计 1860 万/年。'
        },
        {
          time: '07:45',
          name: '经营决策智能体',
          skill: 'recommend_gen',
          text:
            '生成客户分级调价方案：A 涨 3% / B 涨 5% / C 涨 8%，预计覆盖 78% 新增成本。',
          attachment: 'customer_price_adjust_plan_0625.pdf'
        }
      ]

      const planA = {
        title: '方案一：分波调价（A 涨 3% / B 涨 5% / C 涨 8%）',
        recommended: true,
        cells: [
          { label: '覆盖度', value: '78%', sub: '（1450）', color: '#16a34a' },
          { label: '风险等级', value: '低', sub: '', color: '#16a34a' },
          {
            label: 'A 类接受度',
            value: '高',
            sub: '（定制优先）',
            color: '#4A5FF3'
          },
          {
            label: '成本传导',
            value: '保 78% 利润空间',
            sub: '',
            color: '#16a34a'
          }
        ],
        bullets: [
          '按客户战略价值分 A/B/C 三档调价 3%~8%',
          'A 类涨 3%（290 万）/ B 类涨 5%（480 万）/ C 类涨 8%（680 万）',
          '配合 AI 生成客户沟通材料，保核心客户关系',
          '综合评分 7.2，成本 skill + 寻源 skill + 决策 skill 一定推荐'
        ]
      }

      const planB = {
        title: '方案二：统一涨价 5%',
        score: '综合 4.8',
        cells: [
          { label: '覆盖度', value: '50%', sub: '（930）', color: '#f59e0b' },
          { label: '风险等级', value: '中', sub: '', color: '#f59e0b' },
          {
            label: 'A 类接受度',
            value: '低',
            sub: '（可能抵制）',
            color: '#dc2626'
          },
          {
            label: '成本传导',
            value: '50% 内部消化',
            sub: '',
            color: '#f59e0b'
          }
        ],
        bullets: [
          '所有客户统一涨 5% 简化沟通',
          'A 类大客户可能抵制，存在丢单风险',
          '覆盖度仅 50%，剩余 930 万需内部消化',
          '综合评分 4.8，适合客户结构均质场景'
        ]
      }

      const planC = {
        title: '方案三：内部消化（暂不调价）',
        score: '综合 3.6',
        cells: [
          { label: '覆盖度', value: '0%', sub: '（零传导）', color: '#94a3b8' },
          { label: '风险等级', value: '高', sub: '', color: '#dc2626' },
          { label: '客户关系', value: '短期维护', sub: '', color: '#16a34a' },
          {
            label: '毛利影响',
            value: '再降 1.1pct',
            sub: '',
            color: '#dc2626'
          }
        ],
        bullets: [
          '通过采购谈判 + 工艺优化 + 库存管理内部消化 1860 万',
          '毛利率将再降 1.1pct，经营压力大、不可持续',
          '综合评分 3.6，仅适合短期客户关系维护'
        ]
      }

      const refs = [
        {
          tag: '铜箔 LME 现货',
          time: '2026-06-25 07:00',
          desc: '9600 $/t 涨 18%'
        },
        {
          tag: '供应商涨价函',
          time: '2026-06-25 07:15',
          desc: 'MLCC+8% PCB+15%'
        },
        { tag: 'TCO 影响测算', time: '2026-06-25 07:30', desc: '1860 万/年' },
        { tag: '分级调价方案', time: '2026-06-25 07:45', desc: '覆盖 78%' },
        {
          tag: '产品库存',
          time: '2026-06-25 07:50',
          desc: '实时查看原材料在库 / 在途 / 可用天数',
          link: true
        }
      ]

      const overviewHtml = `
        <div id="${sectionIds[0]}" style="display:none;padding:14px 15px;border-radius:12px;background:linear-gradient(135deg,rgba(229,75,61,0.08),rgba(248,113,113,0.04));border:1px solid rgba(229,75,61,0.16);opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;">
            <div style="font-size:14px;font-weight:700;color:#1f2937;">客户流失初版风险预警</div>
            <span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:#FFF1F2;color:#F43F5E;font-size:11px;font-weight:700;border:1px solid #FECDD3;">利空</span>
          </div>
          <div id="${overviewId}" style="font-size:12px;color:#5a6b7e;line-height:1.85;min-height:22px;"></div>
        </div>
      `

      const tierHtml = `
        <div id="${
  sectionIds[1]
}" style="display:none;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:10px;flex-wrap:wrap;">
            <div style="font-size:13px;font-weight:700;color:#1f2937;">客户分级 · 调价矩阵</div>
            <span style="font-size:11px;color:#94a3b8;">数据来源：CRM + 客户合同 2026-06-25</span>
          </div>
          <div style="overflow-x:auto;background:#fff;border-radius:8px;border:1px solid #EEF2F7;">
            <table style="width:100%;border-collapse:collapse;font-size:12px;">
              <thead>
                <tr style="background:#F8FAFC;">
                  <th style="padding:8px 6px;text-align:left;color:#475569;font-weight:700;border-bottom:1px solid #e8ecf0;">客户层级</th>
                  <th style="padding:8px 6px;text-align:left;color:#475569;font-weight:700;border-bottom:1px solid #e8ecf0;">代表客户</th>
                  <th style="padding:8px 6px;text-align:center;color:#475569;font-weight:700;border-bottom:1px solid #e8ecf0;">调价幅度</th>
                  <th style="padding:8px 6px;text-align:center;color:#475569;font-weight:700;border-bottom:1px solid #e8ecf0;">年贡献量</th>
                  <th style="padding:8px 6px;text-align:left;color:#475569;font-weight:700;border-bottom:1px solid #e8ecf0;">沟通策略</th>
                </tr>
              </thead>
              <tbody>
                ${tierRows
    .map(
      (r) => `
                  <tr>
                    <td style="padding:8px 6px;border-bottom:1px solid #F1F5F9;color:#1f2937;font-weight:600;">${r.tier}</td>
                    <td style="padding:8px 6px;border-bottom:1px solid #F1F5F9;color:#475569;">${r.customer}</td>
                    <td style="padding:8px 6px;border-bottom:1px solid #F1F5F9;text-align:center;color:#4A5FF3;font-weight:700;">${r.price}</td>
                    <td style="padding:8px 6px;border-bottom:1px solid #F1F5F9;text-align:center;color:#1f2937;font-weight:600;">${r.volume}</td>
                    <td style="padding:8px 6px;border-bottom:1px solid #F1F5F9;color:#5a6b7e;">${r.risk}</td>
                  </tr>
                `
    )
    .join('')}
              </tbody>
            </table>
          </div>
        </div>
      `

      const evidenceHtml = `
        <div id="${
  sectionIds[2]
}" style="display:none;padding:14px;border-radius:12px;background:linear-gradient(180deg,#FAFBFF 0%,#F4F7FF 100%);border:1px solid rgba(74,95,243,0.18);opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:12px;">
            <div style="display:flex;align-items:center;gap:6px;">
              <span style="display:inline-block;width:4px;height:14px;border-radius:2px;background:linear-gradient(180deg,#4A5FF3,#7c3aed);"></span>
              <span style="font-size:13px;font-weight:700;color:#1f2937;">客户分级调价证据链</span>
            </div>
            <span style="font-size:11px;color:#94a3b8;">${timelineCount} 步</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:14px;position:relative;">
            ${evidence
    .map(
      (t, ti) => `
              <div id="cso-adjust-evidence-${ti}-${replyId}" style="display:none;opacity:0;transform:translateX(-6px);transition:all .32s ease;display:flex;gap:10px;">
                <div style="flex-shrink:0;width:42px;font-size:11px;color:#94a3b8;font-weight:600;text-align:right;padding-top:2px;">${
  t.time
}</div>
                <div style="flex-shrink:0;display:flex;flex-direction:column;align-items:center;">
                  <div style="width:14px;height:14px;border-radius:50%;background:${
  ti === evidence.length - 1 ? '#4A5FF3' : '#10b981'
};border:2px solid #fff;box-shadow:0 0 0 2px ${
  ti === evidence.length - 1
    ? 'rgba(74,95,243,0.25)'
    : 'rgba(16,185,129,0.25)'
};"></div>
                  ${
  ti < evidence.length - 1
    ? '<div style="width:2px;flex:1;background:linear-gradient(180deg,#10b98140,#10b98110);margin-top:4px;"></div>'
    : ''
}
                </div>
                <div style="flex:1;min-width:0;">
                  <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                    <span style="font-size:12px;font-weight:700;color:#1f2937;">${
  t.name
}</span>
                    <span style="font-size:11px;color:#94a3b8;font-family:Menlo,Monaco,monospace;">${
  t.skill
}</span>
                  </div>
                  <div style="margin-top:4px;padding:8px 10px;border-radius:8px;background:#fff;border:1px solid #EEF2F7;font-size:12px;color:#4b5563;line-height:1.7;">
                    ${t.text}
                  </div>
                  ${
  t.attachment
    ? `
                    <div style="margin-top:6px;display:inline-flex;align-items:center;gap:6px;padding:5px 10px;border-radius:6px;background:rgba(74,95,243,0.06);font-size:11px;color:#4A5FF3;">
                      <i class="fa fa-file-text-o" style="font-size:11px;"></i>
                      <span style="font-family:Menlo,Monaco,monospace;">${t.attachment}</span>
                    </div>
                  `
    : ''
}
                </div>
              </div>
            `
    )
    .join('')}
          </div>
        </div>
      `

      const buildPlan = (p, pid, bg, border) => `
        <div id="${pid}" style="display:none;border-radius:12px;background:${bg};border:1px solid ${border};padding:14px 15px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0;">
              <span style="font-size:14px;font-weight:700;color:#1f2937;">${
  p.title
}</span>
            </div>
            <div style="display:flex;align-items:center;gap:6px;flex-shrink:0;">
              ${
  p.recommended
    ? `<span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:linear-gradient(270deg,#4A5FF3 0%,#4E86FF 100%);color:#fff;font-size:11px;font-weight:700;">★ 推荐方案</span>`
    : `<span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:#F1F5F9;color:#94a3b8;font-size:11px;font-weight:700;">${
      p.score || ''
    }</span>`
}
            </div>
          </div>
          <div style="display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin-bottom:12px;">
            ${p.cells
    .map(
      (c) => `
              <div style="display:flex;flex-direction:column;align-items:center;text-align:center;padding:8px 6px;border-radius:10px;background:#fff;border:1px solid #E8ECF5;box-shadow:0 1px 3px rgba(15,23,42,0.04);min-height:74px;box-sizing:border-box;overflow:hidden;">
                <div style="font-size:11px;color:#94a3b8;line-height:1.3;width:100%;">${
  c.label
}</div>
                <div style="font-size:15px;font-weight:800;color:${
  c.color
};line-height:1.2;margin-top:6px;width:100%;word-break:break-all;overflow-wrap:break-word;">${
  c.value
}</div>
                ${
  c.sub
    ? `<div style="font-size:10px;color:#94a3b8;line-height:1.3;margin-top:3px;width:100%;word-break:break-all;overflow-wrap:break-word;">${c.sub}</div>`
    : ''
}
              </div>
            `
    )
    .join('')}
          </div>
          <div style="display:flex;flex-direction:column;gap:6px;">
            ${p.bullets
    .map(
      (b) => `
              <div style="display:flex;align-items:flex-start;gap:6px;font-size:12px;color:#4b5563;line-height:1.7;">
                <span style="color:${
  p.recommended ? '#4A5FF3' : '#94a3b8'
};line-height:1.7;flex-shrink:0;">•</span>
                <span>${b}</span>
              </div>
            `
    )
    .join('')}
          </div>
        </div>
      `

      const refsHtml = `
        <div id="${
  sectionIds[6]
}" style="display:none;padding:14px;border-radius:12px;background:#fff;border:1px solid #e8ecf0;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="font-size:13px;font-weight:700;color:#1f2937;margin-bottom:10px;display:flex;align-items:center;gap:6px;">
            <span style="color:#4A5FF3;">❝</span>引用来源
          </div>
          <div style="display:flex;flex-direction:column;">
            ${refs
    .map((r, ri) => {
      const refId = `cso-adjust-ref-${ri}-${replyId}`
      const isLink = !!r.link
      return `
              <div id="${refId}" ${
  isLink
    ? `onclick="window.open('http://10.206.128.103:21196/user/login','_blank')" style="cursor:pointer;display:none;display:flex;align-items:flex-start;gap:8px;padding:8px 0;${
      ri < refs.length - 1 ? 'border-bottom:1px solid #F1F5F9;' : ''
    }opacity:0;transform:translateX(-6px);transition:all .32s ease;"`
    : `style="display:none;display:flex;align-items:flex-start;gap:8px;padding:8px 0;${
      ri < refs.length - 1 ? 'border-bottom:1px solid #F1F5F9;' : ''
    }opacity:0;transform:translateX(-6px);transition:all .32s ease;"`
}>
                <span style="font-size:13px;color:#4A5FF3;line-height:1.5;flex-shrink:0;">❝</span>
                <div style="flex:1;min-width:0;">
                  <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
                    <span style="font-size:11px;color:#94a3b8;font-weight:600;">[${
  ri + 1
}]</span>
                    <span style="font-size:12px;font-weight:600;color:${
  isLink ? '#4A5FF3' : '#1f2937'
};${isLink ? 'text-decoration:underline;' : ''}">${r.tag}</span>
                    <span style="font-size:11px;color:#94a3b8;">${r.time}</span>
                    ${
  isLink
    ? '<span style="margin-left:auto;color:#4A5FF3;font-size:13px;line-height:1;">↗</span>'
    : ''
}
                  </div>
                  <div style="font-size:11px;color:#5a6b7e;margin-top:4px;padding-left:22px;">${
  r.desc
}</div>
                </div>
              </div>
            `
    })
    .join('')}
          </div>
        </div>
      `

      const chooseHtml = `
        <div id="${sectionIds[7]}" style="display:none;flex-direction:column;gap:10px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
          <div style="padding:12px 14px;border-radius:12px;background:#FFF8EC;border:1px solid #FDE7B5;">
            <div style="font-size:13px;font-weight:700;color:#B45309;margin-bottom:6px;display:flex;align-items:center;gap:6px;">
              <span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#F59E0B;color:#fff;font-size:11px;font-weight:700;line-height:1;">!</span>
              请选择客户调价方案
            </div>
            <div style="font-size:12px;color:#5a6b7e;line-height:1.7;">选定方案后，AI 将生成该方案的总结摘要与对应的客户沟通材料（可重新申请）。</div>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;margin-top:4px;">
            <button id="cso-adjust-pick-a-${replyId}" style="flex:1 1 calc(50% - 5px);min-width:0;padding:9px 12px;border:none;border-radius:10px;background:linear-gradient(270deg,#4A5FF3 0%,#4E86FF 100%);color:#fff;font-size:12px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:4px;white-space:nowrap;letter-spacing:0;" onclick="window.__csoAdjustPickA && window.__csoAdjustPickA()">
              选择方案一：分波调价（推荐）
            </button>
            <button id="cso-adjust-pick-b-${replyId}" style="flex:1 1 calc(50% - 5px);min-width:0;padding:9px 12px;border:1px solid #4A5FF3;border-radius:10px;background:#fff;color:#4A5FF3;font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:4px;white-space:nowrap;letter-spacing:0;" onclick="window.__csoAdjustPickB && window.__csoAdjustPickB()">
              选择方案二：统一涨价 5%
            </button>
            <button id="cso-adjust-pick-c-${replyId}" style="flex:1 1 calc(50% - 5px);min-width:0;padding:9px 12px;border:1px dashed #D9D9D9;border-radius:10px;background:#fff;color:#94a3b8;font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:4px;white-space:nowrap;letter-spacing:0;" onclick="window.__csoAdjustPickC && window.__csoAdjustPickC()">
              选择方案三：内部消化
            </button>
          </div>
        </div>
      `

      const cardHtml = `
        <div data-ceo-card="shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px 16px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div id="cso-adjust-stream-${replyId}" style="margin-top:0;display:flex;flex-direction:column;gap:12px;">
              ${overviewHtml}${tierHtml}${evidenceHtml}
              ${buildPlan(
    planA,
    sectionIds[3],
    'linear-gradient(135deg,#EEF2FF 0%,#F5F3FF 100%)',
    'rgba(74,95,243,0.25)'
  )}
              ${buildPlan(planB, sectionIds[4], '#fff', '#e8ecf0')}
              ${buildPlan(planC, sectionIds[5], '#fff', '#e8ecf0')}
              ${refsHtml}${chooseHtml}
            </div>
          </div>
        </div>
      `

      this.appendLocalMessage('assistant', cardHtml, true)

      // 串行淡入：与 BOM/Brief 卡片节奏一致 — 块边界 1s，卡内紧凑
      setTimeout(() => {
        this.revealSection(sectionIds[0], '')
        setTimeout(() => {
          this.typeWriterEffect(
            overviewId,
            '当前原材料涨势明显：铜箔 6.2%（传导 1.2pct）、MLCC/PCB 同步上行，预计全年新增成本 1860 万/年。已生成客户分级调价矩阵，请按方案选择：',
            () => {
              setTimeout(() => {
                this.revealSection(sectionIds[1], '')
                setTimeout(() => {
                  this.revealSection(sectionIds[2], '')
                  setTimeout(() => {
                    // 证据链逐项淡入（卡内 560ms 紧凑节奏）
                    let ei = 0
                    const showNextEvidence = () => {
                      if (ei >= evidence.length) {
                        // 全部证据完成后弹出方案一（块边界 1000ms）
                        setTimeout(() => {
                          this.revealSection(sectionIds[3], '')
                          setTimeout(() => {
                            this.revealSection(sectionIds[4], '')
                            setTimeout(() => {
                              this.revealSection(sectionIds[5], '')
                              setTimeout(() => {
                                this.revealSection(sectionIds[6], '')
                                setTimeout(() => {
                                  // 引用来源逐项淡入（参考 BOM 节奏：首条 200ms 衔接，后续 560ms 紧凑）
                                  let ri = 0
                                  const showNextRef = () => {
                                    if (ri >= refs.length) {
                                      setTimeout(() => {
                                        this.revealSection(sectionIds[7], 'flex')
                                      }, 1200)
                                      return
                                    }
                                    setTimeout(
                                      () => {
                                        const el = this._findVisibleEl(
                                          `cso-adjust-ref-${ri}-${replyId}`
                                        )
                                        if (el) {
                                          el.style.display = 'flex'
                                          el.style.opacity = '1'
                                          el.style.transform = 'translateX(0)'
                                        }
                                        ri++
                                        showNextRef()
                                      },
                                      ri === 0 ? 200 : 560
                                    )
                                  }
                                  showNextRef()
                                }, 200)
                              }, 1200)
                            }, 1200)
                          }, 1200)
                        }, 1200)
                        return
                      }
                      setTimeout(() => {
                        const el = this._findVisibleEl(
                          `cso-adjust-evidence-${ei}-${replyId}`
                        )
                        if (el) {
                          el.style.display = 'flex'
                          // 强制 reflow，触发 transition
                          void el.offsetHeight
                          requestAnimationFrame(() => {
                            el.style.opacity = '1'
                            el.style.transform = 'translateX(0)'
                          })
                        }
                        ei++
                        showNextEvidence()
                      }, 560)
                    }
                    showNextEvidence()
                  }, 300)
                }, 1200)
              }, 1200)
            }
          )
        }, 640)
      }, 520)

      const self = this
      const lockAndPick = (label, skill) => {
        self.appendLocalMessage('user', '选择方案：' + label)
      }

      window.__csoAdjustPickA = () => {
        lockAndPick('方案一 · 分波调价（推荐）', 'a')
        setTimeout(() => {
          const aid = 'cso-psum-' + Date.now()
          const reasonId = 'cso-psum-reason-' + aid
          const effectWrapId = 'cso-psum-effect-wrap-' + aid
          const introId = 'cso-psum-intro-' + aid
          const matWrapId = 'cso-psum-mat-wrap-' + aid
          self.renderCsoPlanSummaryCard({
            aid,
            reasonId,
            effectWrapId,
            introId,
            matWrapId,
            planKey: 'grade'
          })
        }, 320)
      }
      window.__csoAdjustPickB = () => {
        lockAndPick('方案二 · 统一涨价 5%', 'b')
        setTimeout(() => {
          self.renderCsoPlanSummaryCard('unified')
        }, 320)
      }
      window.__csoAdjustPickC = () => {
        lockAndPick('方案三 · 内部消化（暂不调价）', 'c')
        setTimeout(() => {
          self.renderCsoPlanSummaryCard('internal')
        }, 320)
      }
    },
    renderCsoCustomerAdjustOutcomeCard(config) {
      const html = `
        <div data-ceo-card="shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
              <span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:linear-gradient(270deg,#16a34a,#22c55e);color:#fff;font-size:11px;font-weight:700;">已选方案</span>
              <span style="font-size:14px;font-weight:700;color:#1f2937;">${config.title}</span>
            </div>
            <div style="font-size:12px;color:#5a6b7e;line-height:1.8;">${config.summary}</div>
          </div>
        </div>
      `
      this.appendLocalMessage('assistant', html, true)
    },
    renderPendingDecisionStreamCard(config) {
      this.$store.commit('cosmoClaw/SET_LOCAL_DEMO_MODE', false)
      const replyId = `pending-decision-stream-${Date.now()}`
      const summary = config.summary || {}
      const analysis = config.analysis || {}
      const keyFactors = config.keyFactors || []
      const cardHtml = `
        <div id="decision-stream-${replyId}" style="margin-top:2px;display:flex;flex-direction:column;gap:12px;">
          <div id="decision-section-1-${replyId}" style="display:none;padding:14px 15px;border-radius:12px;background:linear-gradient(135deg,rgba(114,46,209,0.08),rgba(139,92,246,0.05));border:1px solid rgba(114,46,209,0.16);opacity:0;transform:translateY(8px);transition:all .28s ease;">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px;">
              <div style="font-size:12px;font-weight:700;color:#1f2937;">${
  summary.title || '决策分析'
}</div>
              <span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:#f5f3ff;color:#722ed1;font-size:11px;font-weight:600;">经营决策智能体</span>
            </div>
            <div id="decision-summary-${replyId}" style="font-size:12px;color:#5a6b7e;line-height:1.8;min-height:44px;"></div>
          </div>
          <div id="decision-section-2-${replyId}" style="display:none;opacity:0;transform:translateY(8px);transition:all .28s ease;">
            <div style="padding:14px;border-radius:10px;background:#f8fafc;border:1px solid #e8ecf0;">
              <div style="font-size:12px;font-weight:600;color:#1f2937;margin-bottom:12px;">关键影响因素</div>
              <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:10px;">
                ${keyFactors
    .map(
      (f) => `
                  <div style="display:flex;align-items:center;justify-content:space-between;padding:10px;border-radius:8px;background:#ffffff;border:1px solid #e8ecf0;">
                    <span style="font-size:12px;color:#666;">${f.name}</span>
                    <span style="font-size:12px;font-weight:600;color:${f.color};">${f.value}</span>
                  </div>
                `
    )
    .join('')}
              </div>
            </div>
          </div>
          <div id="decision-section-3-${replyId}" style="display:none;padding:12px 14px;border-radius:10px;background:#f8fafc;border:1px dashed rgba(114,46,209,0.2);opacity:0;transform:translateY(8px);transition:all .28s ease;">
            <div style="font-size:12px;color:#5a6b7e;line-height:1.8;">
              ${analysis.paragraphs ? analysis.paragraphs.join('<br><br>') : ''}
            </div>
          </div>
          <div id="decision-section-4-${replyId}" style="display:none;flex-wrap:wrap;gap:10px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
            ${(config.actions || [])
    .map(
      (action) => `
              <button style="padding:10px 20px;border-radius:12px;font-size:13px;font-weight:600;border:2px solid #722ed1;cursor:pointer;color:#722ed1;background:#fff;white-space:nowrap;transition:all 0.2s;"
                onclick="this.style.background='#f5f3ff'">${action.label}</button>
            `
    )
    .join('')}
          </div>
        </div>
      `
      this.appendLocalMessage('assistant', cardHtml, true)
      setTimeout(() => {
        this.revealSection(`decision-section-1-${replyId}`, '')
        this.typeWriterEffect(
          `decision-summary-${replyId}`,
          '铺底报价订单涉及铺底规模合同毛利，需要整合CRM、运采等多维度数据进行综合分析。经营决策智能体已完成分析，正在输出决策建议。'
        )
      }, 180)
      setTimeout(() => this.revealSection(`decision-section-2-${replyId}`, ''), 760)
      setTimeout(() => this.revealSection(`decision-section-3-${replyId}`, ''), 1220)
      setTimeout(() => this.revealSection(`decision-section-4-${replyId}`, 'flex'), 1660)
    },
    renderCoreMetricStreamCard(config) {
      this.$store.commit('cosmoClaw/SET_LOCAL_DEMO_MODE', false)
      const replyId = `core-metric-report-${Date.now()}`
      const statusTone =
        config.riskLevel === '中高'
          ? 'background:#fff7ed;color:#c2410c;border:1px solid rgba(249,115,22,0.18);'
          : config.riskLevel === '低中'
            ? 'background:#f0fdf4;color:#15803d;border:1px solid rgba(34,197,94,0.18);'
            : 'background:#eef4ff;color:#4A5FF3;border:1px solid rgba(74,95,243,0.18);'
      const metricGrid = [
        { label: '当前值', value: config.currentValue },
        { label: '波动', value: config.delta },
        { label: '关键观察', value: config.benchmark }
      ]
        .map((item) => {
          return `<div style="padding:12px;border-radius:12px;background:#ffffff;border:1px solid #e8ecf0;box-shadow:0 1px 3px rgba(15,23,42,0.04);">
          <div style="font-size:11px;color:#8c9bab;margin-bottom:6px;">${item.label}</div>
          <div style="font-size:16px;font-weight:700;color:#1f2937;line-height:1.35;">${item.value}</div>
        </div>`
        })
        .join('')
      const tagsHtml = (config.tags || [])
        .map((tag) => {
          return `<span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#eef4ff;border:1px solid rgba(74,95,243,0.16);font-size:11px;color:#4A5FF3;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${tag}</span>`
        })
        .join('')
      const actionsHtml = (config.actions || [])
        .map((action) => {
          return `<div style="display:flex;align-items:flex-start;gap:8px;font-size:12px;color:#475569;line-height:1.75;">
          <span style="margin-top:6px;width:6px;height:6px;border-radius:999px;background:#4A5FF3;flex:none;"></span>
          <span>${action}</span>
        </div>`
        })
        .join('')
      this.followUpRegistry = this.followUpRegistry || {}
      this.followUpRegistry[replyId] = {
        title: `追问${config.label}核心指标分析`,
        summary: '已完成指标查询、结构拆解与经营评估。',
        prompt: `基于刚才「${config.label}核心指标分析」的内容，请继续展开关键原因、影响范围、潜在风险和下一步建议。`
      }
      const cardHtml = `
        <div data-ceo-card="shell" id="${replyId}" style="display:none;margin-top:2px;opacity:0;transform:translateY(8px);transition:all .28s ease;background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px 16px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;min-width:280px;box-sizing:border-box;">
          <div style="min-width:0;width:100%;box-sizing:border-box;">
            <div style="font-size:15px;font-weight:700;color:#1f2937;">${config.label}核心指标分析</div>
            <div style="margin-top:8px;font-size:13px;line-height:1.7;color:#5a6b7e;width:100%;">已完成指标查询、结构拆解与经营评估。</div>
            <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:12px;">
              <div style="font-size:12px;font-weight:700;color:#1f2937;">核心结论</div>
              <span style="display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:600;${statusTone}">${config.riskLevel}</span>
            </div>
            <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:12px;">${metricGrid}</div>
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:12px;">${tagsHtml}</div>
            <div style="margin-top:12px;padding:12px 14px;border-radius:12px;background:linear-gradient(135deg,rgba(74,95,243,0.06),rgba(14,165,233,0.04));border:1px solid rgba(74,95,243,0.14);">
              <div style="font-size:11px;color:#64748b;margin-bottom:6px;">经营判断</div>
              <div id="core-metric-headline-${replyId}" style="font-size:12px;color:#334155;line-height:1.8;min-height:22px;"></div>
            </div>
            <div style="margin-top:12px;padding:12px 14px;border-radius:12px;background:#ffffff;border:1px solid #e8ecf0;">
              <div style="font-size:11px;color:#64748b;margin-bottom:6px;">详细分析</div>
              <div id="core-metric-detail-${replyId}" style="font-size:12px;color:#475569;line-height:1.8;min-height:44px;"></div>
            </div>
            <div style="margin-top:12px;padding:12px 14px;border-radius:12px;background:#f8fafc;border:1px dashed rgba(74,95,243,0.2);">
              <div style="font-size:11px;color:#64748b;margin-bottom:8px;">建议动作</div>
              <div style="display:flex;flex-direction:column;gap:8px;">${actionsHtml}</div>
            </div>
            <div id="core-metric-followup-${replyId}" style="margin-top:12px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
              <span style="font-size:11px;color:#4A5FF3;cursor:pointer;text-decoration:underline;" onclick="window.triggerCeoFollowUp('${replyId}')">追问</span>
            </div>
          </div>
        </div>
      `
      this.appendLocalMessage('assistant', cardHtml, true)
      setTimeout(() => {
        const el = this._findVisibleEl(replyId)
        if (el) {
          el.style.display = 'block'
          void el.offsetHeight
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
        }
        this.typeWriterEffect(`core-metric-headline-${replyId}`, config.headline || '')
        setTimeout(() => {
          this.typeWriterEffect(`core-metric-detail-${replyId}`, config.detail || '')
        }, 420)
        setTimeout(() => {
          const followUpEl = this._findVisibleEl(`core-metric-followup-${replyId}`)
          if (followUpEl) {
            followUpEl.style.opacity = '1'
            followUpEl.style.transform = 'translateY(0)'
          }
        }, 1500)
      }, 180)
    },
    renderGrossMarginStreamCard(config) {
      const replyId = `gross-margin-stream-${Date.now()}`
      const analysis = config.analysis || {}
      const cardHtml = `
        <div id="gross-margin-stream-${replyId}" style="margin-top:2px;display:flex;flex-direction:column;gap:12px;">
          <div id="gross-margin-section-1-${replyId}" style="display:none;padding:14px 15px;border-radius:12px;background:linear-gradient(135deg,rgba(220,38,38,0.08),rgba(239,68,68,0.05));border:1px solid rgba(220,38,38,0.16);opacity:0;transform:translateY(8px);transition:all .28s ease;">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px;">
              <div style="font-size:12px;font-weight:700;color:#1f2937;">毛利率预警分析</div>
              <span style="display:inline-flex;align-items:center;padding:3px 10px;border-radius:999px;background:#fef2f2;color:#dc2626;font-size:11px;font-weight:600;">CFO数字人推送</span>
            </div>
            <div id="gross-margin-summary-${replyId}" style="font-size:12px;color:#5a6b7e;line-height:1.8;min-height:44px;"></div>
          </div>
          <div id="gross-margin-section-2-${replyId}" style="display:none;gap:6px;flex-wrap:wrap;opacity:0;transform:translateY(8px);transition:all .28s ease;">
            <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#fef2f2;border:1px solid rgba(229,75,61,0.18);font-size:12px;color:#e54b3d;font-weight:700;">毛利率预警</span>
            <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#eef2ff;border:1px solid rgba(74,95,243,0.18);font-size:12px;color:#4A5FF3;">收入稳定</span>
            <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#fffbeb;border:1px solid rgba(245,158,11,0.18);font-size:12px;color:#d97706;">利润承压</span>
            <span style="display:inline-flex;align-items:center;padding:5px 10px;border-radius:999px;background:#fef2f2;border:1px solid rgba(220,38,38,0.18);font-size:12px;color:#dc2626;">结构预警</span>
          </div>
          <div id="gross-margin-section-3-${replyId}" style="display:none;flex-direction:column;gap:8px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
            <div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:#fef2f2;border:1px solid #fecaca;"><span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:#e54b3d;flex-shrink:0;"></span><span style="font-size:12px;color:#4b5563;line-height:1.7;"><b style="color:#e54b3d;">毛利率 17.1%，↓1.6pct，低于目标值。</b>低价订单结构是利润压缩的主因——<b style="color:#e54b3d;">毛利率异常下降为当前最大经营风险</b>，近5个月从18.0%连续下滑至14.5%，累计下滑3.5pct。</span></div>
            <div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;"><span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:#16a34a;flex-shrink:0;"></span><span style="font-size:12px;color:#4b5563;line-height:1.7;">本月营收 8.2亿，核心客户拉动明显，收入端总体稳定。</span></div>
            <div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;"><span style="margin-top:6px;width:6px;height:6px;border-radius:50%;background:#4A5FF3;flex-shrink:0;"></span><span style="font-size:12px;color:#4b5563;line-height:1.7;">本月利润 1.4亿，同比下降，毛利率下滑直接传导至利润端，<b>修复毛利率是保利润的关键。</b></span></div>
          </div>
          <div id="gross-margin-section-4-${replyId}" style="display:none;padding:12px 14px;border-radius:10px;background:#f8fafc;border:1px dashed rgba(220,38,38,0.2);opacity:0;transform:translateY(8px);transition:all .28s ease;">
            <div style="font-size:12px;color:#5a6b7e;line-height:1.8;">
              ${analysis.paragraphs ? analysis.paragraphs.join('<br><br>') : ''}
            </div>
          </div>
          <div id="gross-margin-section-5-${replyId}" style="display:none;flex-wrap:wrap;gap:10px;opacity:0;transform:translateY(8px);transition:all .28s ease;">
            ${(config.actions || [])
    .map(
      (action) => `
              <button style="padding:10px 20px;border-radius:12px;font-size:13px;font-weight:600;border:2px solid #dc2626;cursor:pointer;color:#dc2626;background:#fff;white-space:nowrap;transition:all 0.2s;"
                onclick="this.style.background='#fef2f2'">${action.label}</button>
            `
    )
    .join('')}
          </div>
        </div>
      `
      this.appendLocalMessage('assistant', cardHtml, true)
      setTimeout(() => {
        this.revealSection(`gross-margin-section-1-${replyId}`, '')
        this.typeWriterEffect(
          `gross-margin-summary-${replyId}`,
          '毛利率异常下降为当前最大经营风险。近5个月从18.0%连续下滑至14.5%，累计下滑3.5个百分点。CFO数字人已完成分析，正在输出详细报告。'
        )
      }, 180)
      setTimeout(
        () => this.revealSection(`gross-margin-section-2-${replyId}`, 'flex'),
        760
      )
      setTimeout(
        () => this.revealSection(`gross-margin-section-3-${replyId}`, 'flex'),
        1220
      )
      setTimeout(() => this.revealSection(`gross-margin-section-4-${replyId}`, ''), 1660)
      setTimeout(
        () => this.revealSection(`gross-margin-section-5-${replyId}`, 'flex'),
        2100
      )
    },
    getPendingDecisionAnalysisConfig() {
      return {
        summary: {
          title: '铺底报价订单决策分析',
          description: '正在分析铺底报价订单相关数据...',
          statusText: this.$t('workbench.statusDeepThinking'),
          resultText: '需要整合CRM、运采等多维度数据进行综合分析'
        },
        analysis: {
          title: '决策分析结论',
          paragraphs: [
            '当前铺底报价订单涉及铺底规模合同毛利，需要综合分析多维度数据。',
            'CRM数据分析：已获取相关客户历史合作数据和报价记录。',
            '运采数据分析：正在评估原材料成本和运输费用对报价的影响。',
            '毛利预估：初步估算该订单毛利率约为6.8%，低于公司平均水平。',
            '风险评估：存在一定的价格竞争风险，建议谨慎接单。',
            '建议方案：建议重新评估报价策略，考虑适当提高报价或降低成本。'
          ]
        },
        keyFactors: [
          { name: '客户重要性', value: '高', color: '#4A5FF3' },
          { name: '毛利水平', value: '低', color: '#F44F4F' },
          { name: '竞争程度', value: '高', color: '#F4B13D' },
          { name: '战略价值', value: '中', color: '#73B950' }
        ],
        actions: [
          {
            label: '生成报价分析报告',
            actionKey: 'generate_report',
            style: 'primary'
          },
          {
            label: '查看历史报价记录',
            actionKey: 'view_history',
            style: 'link'
          }
        ]
      }
    },
    getPendingDecisionThinkingSteps(config) {
      return [
        '正在检索订单基本信息...',
        '正在分析CRM客户数据...',
        '正在评估运采成本数据...',
        config.summary?.description || '正在进行综合决策分析...',
        config.summary?.resultText || '正在生成决策建议...'
      ]
    },
    getPendingDecisionAnalysisContent(config) {
      const analysis = config.analysis || {}
      const keyFactors = config.keyFactors || []
      const factorsHtml = keyFactors
        .map((f) => {
          return `<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 12px;background:#f8fafc;border-radius:6px;margin-bottom:6px;">
          <span style="font-size:12px;color:#666;">${f.name}</span>
          <span style="font-size:12px;font-weight:600;color:${f.color};">${f.value}</span>
        </div>`
        })
        .join('')
      const paragraphs = (analysis.paragraphs || []).map((p) => `> ${p}`).join('\n\n')
      return [
        `### ${analysis.title || '决策分析'}`,
        '',
        '**关键影响因素**',
        '',
        factorsHtml,
        '',
        '**分析结论与建议**',
        '',
        paragraphs,
        '',
        '---',
        '',
        this.getGrossMarginActionHtml(config.actions || [])
      ].join('\n')
    },
    getCoreMetricAnalysisConfig(metricItem) {
      const metricName = metricItem.name || '核心指标'
      const isRevenue = metricName.includes('收入') || metricName.includes('营收')
      const isGrossMargin = metricName.includes('毛利率')
      const isProfit = metricName.includes('利润')
      const isCost = metricName.includes('成本')

      const CORE_METRIC_CONFIGS = {
        revenue: {
          key: 'revenue',
          label: '本月收入',
          currentValue: '8.2亿',
          delta: '较预算 +9.5%',
          benchmark: '高价值订单兑现率 92%',
          riskLevel: '中',
          tags: ['收入兑现', '订单结构'],
          summary: '正在查询收入定义、预算达成和结构波动，生成核心指标分析结论。',
          simplifiedSteps: [
            '正在检索本月收入的统计口径、预算值与预警阈值...',
            '正在拉取本月收入、重点客户交付金额与回款节奏...',
            '正在拆解收入构成，核对高毛利订单占比与低价订单占比变化...',
            '正在评估收入增长的可持续性，识别月底验收与利润侵蚀风险...'
          ],
          headline:
            '本月收入整体好于预算，主要由动力客户集中交付带动，但订单结构分化仍在扩大。',
          detail:
            '当前最需要盯住的是高毛利订单兑现和月底验收节奏。如果低价订单继续拉高占比，收入增长将难以同步转化为利润增长。',
          actions: [
            '锁定本周重点客户验收节点，确保已排产订单按期转收入。',
            '销售与财务联合复盘低价订单占比，设置收入增长与毛利底线联动阈值。',
            '对延期交付订单建立日跟踪清单，避免月底集中波动。'
          ]
        },
        grossMargin: {
          key: 'grossMargin',
          label: '毛利率',
          currentValue: '17.1%',
          delta: '较目标 -1.6pct',
          benchmark: '低价订单占比 42%',
          riskLevel: '中高',
          tags: ['毛利修复', '结构优化'],
          summary: '正在查询毛利率定义、目标达成和结构波动，生成核心指标分析结论。',
          simplifiedSteps: [
            '正在检索毛利率的统计口径、目标值与预警阈值...',
            '正在拉取本月毛利率、产品结构与成本传导数据...',
            '正在拆解毛利率构成，核对低价订单占比与成本波动变化...',
            '正在评估毛利修复路径，识别结构优化与定价策略风险...'
          ],
          headline:
            '毛利率已连续三个月低于目标值，低价订单占比攀升和成本传导滞后是主要原因。',
          detail:
            '当前最需要盯住的是低价订单占比红线和成本传导节奏。如果低价订单继续扩张，毛利率修复将更加困难。',
          actions: [
            '设置低价订单占比红线（不超过35%），严控新增低价订单。',
            '推动与核心客户的价格联动机制，加快成本传导。',
            '建立基地良率周跟踪看板，降低制造损耗。'
          ]
        },
        profit: {
          key: 'profit',
          label: '本月利润',
          currentValue: '1.4亿',
          delta: '环比 -1.6%',
          benchmark: '利润达成率 94%',
          riskLevel: '中高',
          tags: ['利润修复', '费用效率'],
          summary: '正在查询利润达成、驱动因子和异常波动来源，生成核心指标分析结论。',
          simplifiedSteps: [
            '正在检索本月利润定义、预算目标和预警线...',
            '正在拉取利润实际值、毛利贡献和期间费用波动...',
            '正在拆解利润与收入、成本、销售费用之间的原子指标链路...',
            '正在评估利润承压主因，输出修复节奏和优先动作...'
          ],
          headline:
            '本月利润承压的主因不是规模不足，而是毛利修复慢于收入增长、费用投放前置。',
          detail:
            '利润绝对值仍处在可控区间，但利润兑现质量偏弱。需要同步收紧低效费用投放，并把重点资源转向高贡献客户和高毛利产品。',
          actions: [
            '逐项核对毛利下滑订单，明确利润侵蚀最大的客户与产品组合。',
            '压降低转化销售费用投放，把预算向已验证的重点客户倾斜。',
            '建立利润修复周看板，联动收入、毛利率和销售费用三项指标。'
          ]
        },
        cost: {
          key: 'cost',
          label: '本月成本',
          currentValue: '6.1亿',
          delta: '环比 持平',
          benchmark: '单吨成本偏高 1.8%',
          riskLevel: '中',
          tags: ['原料波动', '制造损耗'],
          summary: '正在查询成本结构、材料波动和制造损耗，生成核心指标分析结论。',
          simplifiedSteps: [
            '正在检索本月成本统计口径、预算线与预警阈值...',
            '正在拉取原料、制造、物流与质量损耗四类成本数据...',
            '正在拆解单吨成本组成，识别异常原子成本项和波动链路...',
            '正在评估成本压力对利润与排产策略的影响...'
          ],
          headline:
            '本月成本总量保持平稳，但原料波动和制造损耗仍在持续挤压单吨盈利空间。',
          detail:
            '成本没有继续明显抬升是积极信号，但当前改善更多来自采购节奏控制，不代表结构性风险已经解除。需要继续盯住锂盐、辅材和良率损耗。',
          actions: [
            '建立原材料价格监测机制，提前锁定关键原料采购价格。',
            '优化采购策略，推进集中采购和长期协议。',
            '推进降本增效措施，降低制造损耗和物流成本。'
          ]
        }
      }

      let config = null
      if (isRevenue) {
        config = CORE_METRIC_CONFIGS.revenue
      } else if (isGrossMargin) {
        config = CORE_METRIC_CONFIGS.grossMargin
      } else if (isProfit) {
        config = CORE_METRIC_CONFIGS.profit
      } else if (isCost) {
        config = CORE_METRIC_CONFIGS.cost
      } else {
        config = {
          key: 'other',
          label: metricName,
          currentValue: `${metricItem.value || '--'}${metricItem.unit || ''}`,
          delta: metricItem.trendValue || '--',
          benchmark: '数据完整',
          riskLevel: '中',
          tags: ['指标分析'],
          summary: `正在分析${metricName}...`,
          simplifiedSteps: [
            `正在检索${metricName}的统计口径、目标值与预警阈值...`,
            `正在拉取${metricName}最新数据...`,
            `正在拆解${metricName}构成...`,
            `正在评估${metricName}变化趋势与应对建议...`
          ],
          headline: `${metricName}当前值为${metricItem.value || '--'}${
            metricItem.unit || ''
          }，${metricItem.trendValue || ''}。`,
          detail: `${metricName}各项构成指标数据完整，建议持续关注变化趋势。`,
          actions: [
            `持续跟踪${metricName}变化趋势。`,
            `定期分析${metricName}构成变化。`,
            `建立${metricName}预警机制。`
          ]
        }
      }

      return {
        ...config,
        agents: []
      }
    },
    getCoreMetricAnalysisContent(config) {
      const analysis = config.analysis || {}
      const indicator = config.indicator || {}
      const trendColor = indicator.trendColor || '#333'
      return [
        `### ${analysis.title || '指标分析'}`,
        '',
        `**当前值**：<span style="font-size:24px;font-weight:700;color:${trendColor};">${
          indicator.value
        }${indicator.unit || ''}</span>`,
        '',
        `**变化趋势**：${indicator.trendText || ''} ${indicator.trendValue || ''}`,
        '',
        '**分析结论**',
        '',
        ...(analysis.paragraphs || []).map((p) => `> ${p}`),
        '',
        '---',
        '',
        this.getGrossMarginActionHtml(config.actions || [])
      ].join('\n')
    },
    getThinkingContent({ title, summary, step, statusText }) {
      const displayStatus = statusText || this.$t('workbench.statusDeepThinking')
      const escapedStep = (step || '')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
      const escapedSummary = (summary || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const escapedTitle = (title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return `<div data-ceo-card="thinking-shell" style="background:#ffffff;border:1px solid #e8ecf0;border-radius:14px;padding:16px;box-shadow:0 4px 16px rgba(0,0,0,0.04);width:100%;box-sizing:border-box;">
  <div style="min-width:0;width:100%;box-sizing:border-box;">
    <div style="font-size:15px;font-weight:700;color:#1f2937;">${escapedTitle}</div>
    <div style="margin-top:8px;font-size:13px;line-height:1.7;color:#5a6b7e;">${escapedSummary}</div>
    <div style="margin-top:12px;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <div style="width:18px;height:18px;border-radius:50%;background:linear-gradient(135deg,#667eea,#764ba2);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        </div>
        <span style="font-size:12px;color:#667eea;font-weight:500;">${displayStatus}</span>
      </div>
      <div style="padding:12px 14px;border-radius:10px;background:linear-gradient(135deg,rgba(102,126,234,0.04),rgba(118,75,162,0.04));border:1px solid rgba(102,126,234,0.12);">
        <div style="font-size:12px;color:#5a6b7e;line-height:1.8;">${escapedStep}</div>
      </div>
    </div>
  </div>
</div>`
    },
    async startPvmcAnalysis() {
      this.clearSmartAnalysisTimers()
      const pvmcConfig = await this.fetchPvmcAnalysisConfig()
      const thinkingSteps = [
        this.$t('workbench.stepPvmcDataLoad'),
        this.$t('workbench.stepPvmcDataComplete'),
        this.$t('workbench.stepPvmcStartAnalysis'),
        this.$t('workbench.stepPvmcPriceAnalysis'),
        this.$t('workbench.stepPvmcVolumeAnalysis'),
        this.$t('workbench.stepPvmcMixAnalysis'),
        this.$t('workbench.stepPvmcCostAnalysis'),
        this.$t('workbench.stepPvmcCrossValidate'),
        this.$t('workbench.stepPvmcValidateResult'),
        this.$t('workbench.stepPvmcGeneratingReport')
      ]
      this.runSmartFlowProgress({
        title: this.$t('workbench.pvmcAnalysisTitle'),
        status: this.$t('workbench.pvmcAnalysisStatus'),
        steps: thinkingSteps,
        interval: 800,
        finalBody: this.getPvmcReportContent(pvmcConfig),
        finalOptions: {
          status: this.$t('workbench.statusPending'),
          currentStep: this.$t('workbench.statusPvmcReportGenerated')
        }
      })
    },
    async fetchPvmcAnalysisConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['PVMC模型分析毛利波动原因'])
        return this.normalizePvmcAnalysisConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchPvmcAnalysisConfig failed:', e)
        return this.normalizePvmcAnalysisConfig()
      }
    },
    normalizePvmcAnalysisConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse pvmc remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultPvmcAnalysisConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        report: {
          ...defaultConfig.report,
          ...((config || {}).report || {})
        },
        pvmcImpact: {
          ...defaultConfig.pvmcImpact,
          ...((config || {}).pvmcImpact || {}),
          items: Array.isArray((config || {}).pvmcImpact?.items)
            ? config.pvmcImpact.items
            : defaultConfig.pvmcImpact.items
        },
        rootCause: {
          ...defaultConfig.rootCause,
          ...((config || {}).rootCause || {}),
          items: Array.isArray((config || {}).rootCause?.items)
            ? config.rootCause.items
            : defaultConfig.rootCause.items
        },
        conclusion: {
          ...defaultConfig.conclusion,
          ...((config || {}).conclusion || {})
        }
      }
    },
    getDefaultPvmcAnalysisConfig() {
      return {
        report: {
          title: '毛利波动原因分析报告',
          summary:
            '基于PVMC模型分析，已完成对毛利下降原因的深度拆解。分析结果显示，销量下滑是首要驱动因素，其次是成本上升和产品结构变化。'
        },
        pvmcImpact: {
          title: 'PVMC四维度影响分析',
          items: [
            {
              name: '价格影响',
              code: 'P',
              value: '-1.8%',
              description: '行业价格战加剧，平均售价同比下降'
            },
            {
              name: '销量影响',
              code: 'V',
              value: '-5.2%',
              description: '大客户流失严重，订单量下降6.2%'
            },
            {
              name: '产品结构',
              code: 'M',
              value: '-1.4%',
              description: '高毛利产品占比从35%降至22%'
            },
            {
              name: '成本影响',
              code: 'C',
              value: '+3.2%',
              description: '原材料价格上涨，成本压力增大'
            }
          ],
          summary:
            '四大因素共同作用导致毛利率整体下降1.6个百分点，其中销量下滑是首要驱动因素，贡献超过60%的影响，其次是成本上升和价格竞争压力。'
        },
        rootCause: {
          title: '核心发现与根因分析',
          items: [
            {
              title: '销量下滑',
              contribution: '贡献 -5.2%',
              description:
                '华南区3家核心大客户流失，导致订单量下降6.2%；老客户复购率同比下降28.7%；市场份额从12.5%降至9.8%，竞品抢占明显。'
            },
            {
              title: '成本上升',
              contribution: '贡献 +3.2%',
              description:
                '钢材原材料价格上涨8.5%，电子元器件涨幅达13-28%；产能利用率从78%降至62%，固定成本分摊增加25%；废品率从1.8%升至2.9%。'
            },
            {
              title: '产品结构变化',
              contribution: '贡献 -1.4%',
              description:
                '高毛利产品销售占比从35%降至22%；低毛利特价产品销量增长143.8%，清库折扣达6.5折；定制化非标订单占比从15%升至28%，毛利率仅11.5%。'
            },
            {
              title: '价格竞争',
              contribution: '贡献 -1.8%',
              description:
                '行业价格战加剧，竞品频繁降价促销；为保证订单量被迫采取跟随降价策略，产品平均售价同比下降。'
            }
          ]
        },
        conclusion: {
          description:
            '综合分析显示，销量下滑是毛利下降的首要驱动因素，贡献超过60%的毛利下降影响。建议优先关注客户流失问题和市场份额变化，立即启动大客户挽留计划。其次，成本上升也是重要因素，建议通过供应链优化、与核心供应商谈判锁定价格、开发备选供应商等方式来应对原材料涨价压力。当前毛利率17.1%已低于行业平均水平2.3个百分点，建议在3个工作日内完成方案评估并启动实施。'
        }
      }
    },
    async showPvmcSummary() {
      this.clearSmartAnalysisTimers()
      const pvmcSummaryConfig = await this.fetchPvmcSummaryConfig()
      this.scheduleSmartAnalysis(() => {
        this.setSmartFlowCardContent(this.getPvmcSummaryContent(pvmcSummaryConfig), {
          title: this.$t('workbench.smartPvmcSummary'),
          status: this.$t('workbench.statusPending'),
          currentStep: this.$t('workbench.statusPvmcSummaryOpened')
        })
      }, 500)
    },
    async fetchPvmcSummaryConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['什么是PVMC'])
        return this.normalizePvmcSummaryConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchPvmcSummaryConfig failed:', e)
        return this.normalizePvmcSummaryConfig()
      }
    },
    normalizePvmcSummaryConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse pvmc summary remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultPvmcSummaryConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        pvmcMethod: {
          ...defaultConfig.pvmcMethod,
          ...((config || {}).pvmcMethod || {}),
          dimensions: Array.isArray((config || {}).pvmcMethod?.dimensions)
            ? config.pvmcMethod.dimensions
            : defaultConfig.pvmcMethod.dimensions,
          applicationValue: {
            ...defaultConfig.pvmcMethod.applicationValue,
            ...((config || {}).pvmcMethod?.applicationValue || {})
          }
        }
      }
    },
    getDefaultPvmcSummaryConfig() {
      return {
        pvmcMethod: {
          title: 'PVMC分析方法',
          summary: 'PVMC是一种系统化的毛利分析框架，用于深度拆解利润变化的驱动因素。',
          dimensions: [
            {
              code: 'P',
              title: 'Price（价格）',
              description:
                '产品售价变化对毛利的影响，包括定价策略调整、折扣促销活动、市场价格竞争等因素'
            },
            {
              code: 'V',
              title: 'Volume（销量）',
              description:
                '销售数量变化对毛利的影响，包括客户需求变化、市场份额变动、渠道覆盖情况等'
            },
            {
              code: 'M',
              title: 'Mix（产品结构）',
              description:
                '不同毛利率产品占比变化，包括高毛利产品推广效果、产品组合优化等'
            },
            {
              code: 'C',
              title: 'Cost（成本）',
              description:
                '单位成本变化对毛利的影响，包括原材料采购成本、生产成本、运营成本等'
            }
          ],
          applicationValue: {
            title: '应用价值',
            description:
              '通过PVMC模型，可以将毛利变化精准拆解为四个维度，量化每个维度的影响程度，从而定位利润下降的核心原因，为管理层提供数据驱动的决策支撑。该模型广泛应用于制造业、零售业等行业的经营分析中。'
          }
        }
      }
    },
    async startQuadrantPlanAnalysis() {
      this.clearSmartAnalysisTimers()
      const quadrantConfig = await this.fetchQuadrantPlanConfig()
      const percents = [18, 36, 58, 76, 92, 100]
      percents.forEach((percent, index) => {
        this.scheduleSmartAnalysis(() => {
          this.setSmartFlowCardContent(this.getQuadrantProgressContent(percent), {
            title: this.$t('workbench.smartQuadrantPlan'),
            status: this.$t('workbench.statusAnalyzing'),
            currentStep: this.$t('workbench.quadrantProgress', {
              percent: `${percent}%`
            })
          })
        }, index * 450)
      })
      this.scheduleSmartAnalysis(() => {
        this.setSmartFlowCardContent(this.getQuadrantPlanContent(quadrantConfig), {
          title: this.$t('workbench.smartQuadrantPlan'),
          status: this.$t('workbench.statusPending'),
          currentStep: this.$t('workbench.statusQuadrantPlanGenerated')
        })
      }, percents.length * 450 + 600)
    },
    async fetchQuadrantPlanConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['四象限分析生成提升方案'])
        return this.normalizeQuadrantPlanConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchQuadrantPlanConfig failed:', e)
        return this.normalizeQuadrantPlanConfig()
      }
    },
    normalizeQuadrantPlanConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse quadrant plan remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultQuadrantPlanConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        solution: {
          ...defaultConfig.solution,
          ...((config || {}).solution || {}),
          plans: Array.isArray((config || {}).solution?.plans)
            ? config.solution.plans
            : defaultConfig.solution.plans
        }
      }
    },
    getDefaultQuadrantPlanConfig() {
      return {
        solution: {
          title: '智能提升方案',
          summary:
            '基于四象限分析，已生成4套可执行的毛利率提升方案。面对BOM成本持续上涨（PCB+8.2%/铜+5.4%/锡+4.1%），建议优先从调整产品价格开始。',
          planCount: '4套',
          plans: [
            {
              title: '调整产品价格',
              subtitle: '推荐优先执行',
              items: [
                '主力产品A-1000/B-2000提价5-8%，向客户传导BOM成本上涨',
                '建立价格联动机制，按原材料指数自动调价',
                '跟踪调价执行进度，确保主力产品覆盖率≥80%'
              ],
              expectedImprove: '+1.2%',
              difficulty: '低',
              actionLabel: '应用调整产品价格方案'
            },
            {
              title: '产品结构优化',
              subtitle: '协同推进',
              items: [
                '提升高毛利产品销售占比，目标从38%提升至45%',
                '优化产品组合，建立分级销售激励机制',
                '加强产品生命周期管理，减少低毛利产品库存'
              ],
              expectedImprove: '+0.8%',
              difficulty: '中',
              actionLabel: '应用产品结构优化方案'
            },
            {
              title: '材料降本',
              subtitle: '供应链优化',
              items: [
                '与核心供应商签订锁价协议，锁定3个月价格',
                '开发2-3家备选供应商引入竞争机制',
                '推进国产替代计划，降低进口原材料依赖'
              ],
              expectedImprove: '+0.6%',
              difficulty: '高',
              actionLabel: '应用材料降本方案'
            },
            {
              title: '制造成本优化',
              subtitle: '精益生产',
              items: [
                '优化生产排程，提升产能利用率至75%+',
                '工艺改进降低废品率，从2.9%降至2.0%',
                '智能化生产提升效率，降低单位产品工时'
              ],
              expectedImprove: '+0.4%',
              difficulty: '中高',
              actionLabel: '应用制造成本优化方案'
            }
          ]
        }
      }
    },
    async startPlanApplyFlow(plan) {
      this.clearSmartAnalysisTimers()
      const isProductStructure = plan === this.$t('workbench.planProductStructure')
      const userText = isProductStructure
        ? this.$t('workbench.planApplyUserText')
        : this.$t('workbench.actionApplyPlan', { plan })
      const planResultName =
        plan === this.$t('workbench.planPricingStrategy') ||
        plan === this.$t('workbench.planManufacturingCostOptimization')
          ? this.$t('workbench.actionApplyPlan', { plan })
          : userText
      const planResultConfig = isProductStructure
        ? await this.fetchIndicatorCombinationConfig(userText)
        : await this.fetchPlanApplyResultConfig(planResultName)
      const percents = [20, 42, 64, 82, 100]
      percents.forEach((percent, index) => {
        this.scheduleSmartAnalysis(() => {
          this.setSmartFlowCardContent(this.getPlanApplyProgressContent(plan, percent), {
            title: this.$t('workbench.smartPlanApply'),
            status: this.$t('workbench.statusProcessing'),
            currentStep: this.$t('workbench.actionApplyPlan', {
              plan: `${plan} ${percent}%`
            })
          })
        }, index * 450)
      })
      this.scheduleSmartAnalysis(() => {
        this.setSmartFlowCardContent(
          isProductStructure
            ? this.getIndicatorCombinationContent(plan, planResultConfig)
            : this.getPlanApplyResultContent(planResultConfig),
          {
            title: this.$t('workbench.smartPlanApply'),
            status: this.$t('workbench.statusPending'),
            currentStep: isProductStructure
              ? this.$t('workbench.statusIndicatorGenerated')
              : this.$t('workbench.statusPlanApplied')
          }
        )
      }, percents.length * 450 + 650)
    },
    async fetchPlanApplyResultConfig(name) {
      try {
        const item = await this.fetchIndBaseDataByNames([name])
        return this.normalizePlanApplyResultConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchPlanApplyResultConfig failed:', e)
        return this.normalizePlanApplyResultConfig()
      }
    },
    normalizePlanApplyResultConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse plan apply result remark failed:', e)
          config = null
        }
      }
      return {
        ...this.getDefaultPlanApplyResultConfig(),
        ...(config || {})
      }
    },
    getDefaultPlanApplyResultConfig() {
      return {
        title: '应用调整产品价格',
        strategy: '主力产品提价5-8%',
        expectedImprove: '+1.2%',
        executor: '销售部-李明'
      }
    },
    async fetchIndicatorCombinationConfig(name) {
      try {
        const item = await this.fetchIndBaseDataByNames([name])
        return this.normalizeIndicatorCombinationConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchIndicatorCombinationConfig failed:', e)
        return this.normalizeIndicatorCombinationConfig()
      }
    },
    normalizeIndicatorCombinationConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse indicator combination remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultIndicatorCombinationConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        existingIndicators: {
          ...defaultConfig.existingIndicators,
          ...((config || {}).existingIndicators || {}),
          items: Array.isArray((config || {}).existingIndicators?.items)
            ? config.existingIndicators.items
            : defaultConfig.existingIndicators.items
        },
        newIndicators: {
          ...defaultConfig.newIndicators,
          ...((config || {}).newIndicators || {}),
          items: Array.isArray((config || {}).newIndicators?.items)
            ? config.newIndicators.items
            : defaultConfig.newIndicators.items
        }
      }
    },
    getDefaultIndicatorCombinationConfig() {
      return {
        title: '提升毛利率的组合指标',
        summary:
          '基于【调整产品价格】方案，已生成关键指标组合，建议优先应用核心指标。面对BOM成本上升，通过产品调价传导成本压力是恢复毛利率的核心手段，建议重点追踪产品调价覆盖率和成本传导率。',
        existingIndicators: {
          title: '现有指标',
          items: [
            {
              name: '毛利率',
              tag: '核心指标',
              currentValue: '17.1%',
              targetValue: '18.7%',
              owner: '销售部 李明'
            },
            {
              name: '平均销售单价（ASP）',
              tag: '辅助指标',
              currentValue: '¥2,380',
              targetValue: '¥2,560',
              owner: '销售部 李明'
            }
          ]
        },
        newIndicators: {
          title: '新指标（待创建）',
          items: [
            {
              name: '产品调价覆盖率',
              tag: '新指标',
              description:
                '已执行调价产品数量占比，目标覆盖主力产品线80%以上，追踪价格传导进度',
              owner: '销售部 李明'
            },
            {
              name: '成本传导率',
              tag: '新指标',
              description:
                '衡量BOM成本上涨通过调价传导给客户的比例，目标传导率≥70%，确保利润不受侵蚀',
              owner: '销售部 李明'
            }
          ]
        },
        recommendation:
          '建议优先应用【毛利率】和【产品调价覆盖率】两个核心指标，这将直接反映产品调价策略的执行效果和成本传导进度。'
      }
    },
    applyRecommendedIndicators() {
      this.appendLocalMessage('user', '应用推荐方案')
      setTimeout(() => {
        const thinkingSteps = [
          '正在解析推荐指标组合...',
          '识别核心指标：毛利率、产品调价覆盖率',
          '正在计算目标值并与当前值对比...',
          '正在匹配执行负责人并准备下发KPI...'
        ]
        const thinkingId = 'thinking-apply-rec-' + Date.now()
        const thinkingHtml = this.renderCeoThinkingCard(
          thinkingId,
          thinkingSteps[0],
          '应用推荐方案',
          '正在应用方案的核心指标组合...',
          thinkingSteps.length
        )
        this.appendLocalMessage('assistant', thinkingHtml, true)
        let stepIndex = 1
        const thinkTimer = setInterval(() => {
          if (stepIndex < thinkingSteps.length) {
            this.updateCeoThinkingCard(thinkingId, thinkingSteps, stepIndex)
            stepIndex++
          } else {
            clearInterval(thinkTimer)
            setTimeout(() => {
              this.removeThinkingCard(thinkingId)
              setTimeout(() => {
                const replyId = 'apply-rec-result-' + Date.now()
                const resultHtml = `<div style="padding:14px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;">
                  <div style="font-size:13px;font-weight:600;color:#1f2937;margin-bottom:10px;">推荐方案应用成功</div>
                  <div id="${replyId}" style="font-size:12px;color:#5a6b7e;line-height:1.8;min-height:22px;"></div>
                </div>`
                this.appendLocalMessage('assistant', resultHtml, true)
                setTimeout(() => {
                  this.smartTypeWriterEffect(
                    replyId,
                    '正在应用方案的核心指标组合：<br><br>1.「<span style="color:#4A5FF3;font-weight:600;">毛利率</span>」从 <span style="color:#DC2626;font-weight:600;">17.1%</span> → <span style="color:#059669;font-weight:600;">18.7%</span>，已生成KPI「<span style="color:#4A5FF3;font-weight:600;">月度毛利率达标率≥95%</span>」，下发给「<span style="color:#004baa;font-weight:600;">销售部-李明</span>」<br><br>2.「<span style="color:#4A5FF3;font-weight:600;">产品调价覆盖率</span>」从 <span style="color:#DC2626;font-weight:600;">52%</span> → <span style="color:#059669;font-weight:600;">80%</span>，已生成KPI「<span style="color:#4A5FF3;font-weight:600;">主力产品调价覆盖率≥80%</span>」，下发给「<span style="color:#004baa;font-weight:600;">销售部-李明</span>」<br><br><span style="border-top:1px solid #e8ecf0;padding-top:10px;display:block;">预计执行后，通过主力产品调价传导BOM成本上涨压力，毛利率将恢复至28.6%以上。</span>'
                  )
                }, 200)
              }, 320)
            }, 500)
          }
        }, 800)
        this.smartAnalysisTimers.push(thinkTimer)
      }, 500)
    },
    async applySingleIndicator(indicatorName = '毛利率') {
      this.clearSmartAnalysisTimers()
      const requestName = this.$t('workbench.actionApplySingleIndicator', {
        indicator: indicatorName
      })
      const singleIndicatorConfig = await this.fetchSingleIndicatorApplyConfig(
        requestName,
        indicatorName
      )
      const appliedIndicator = singleIndicatorConfig.indicatorName || indicatorName
      this.setSmartFlowCardContent(
        this.getThinkingContent({
          title: this.$t('workbench.smartSingleIndicatorApply'),
          summary: this.$t('workbench.actionApplySingleIndicator', {
            indicator: appliedIndicator
          }),
          step: this.$t('workbench.statusPending'),
          statusText: this.$t('workbench.statusAnalyzing')
        }),
        {
          status: this.$t('workbench.statusProcessing'),
          richHtml: true
        }
      )
      this.scheduleSmartAnalysis(() => {
        this.setSmartFlowCardContent(
          this.getSingleIndicatorApplyContent(singleIndicatorConfig),
          {
            title: this.$t('workbench.smartSingleIndicatorApply'),
            status: this.$t('workbench.statusCompleted'),
            currentStep: this.$t('workbench.actionApplySingleIndicator', {
              indicator: appliedIndicator
            })
          }
        )
      }, 500)
    },
    async fetchSingleIndicatorApplyConfig(name, indicatorName = '毛利率') {
      try {
        const item = await this.fetchIndBaseDataByNames([name])
        return this.normalizeSingleIndicatorApplyConfig(item.remark, indicatorName)
      } catch (e) {
        console.warn('[IndexHomePage] fetchSingleIndicatorApplyConfig failed:', e)
        return this.normalizeSingleIndicatorApplyConfig(null, indicatorName)
      }
    },
    normalizeSingleIndicatorApplyConfig(remark, indicatorName = '毛利率') {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse single indicator apply remark failed:', e)
          config = null
        }
      }
      return {
        ...this.getDefaultSingleIndicatorApplyConfig(indicatorName),
        ...(config || {})
      }
    },
    getDefaultSingleIndicatorApplyConfig(indicatorName = '毛利率') {
      const configMap = {
        产品调价覆盖率: {
          title: '指标应用成功',
          indicatorName: '产品调价覆盖率',
          currentValue: '52%',
          targetValue: '80%',
          kpiName: '主力产品调价覆盖率',
          kpiTarget: '≥80%',
          receiver: '销售部-李明'
        },
        平均销售单价: {
          title: '指标应用成功',
          indicatorName: '平均销售单价（ASP）',
          currentValue: '¥2,380',
          targetValue: '¥2,560',
          kpiName: 'ASP提升率',
          kpiTarget: '≥7.5%',
          receiver: '销售部-李明'
        }
      }
      if (configMap[indicatorName]) return configMap[indicatorName]
      return {
        title: '指标应用成功',
        indicatorName: '毛利率',
        currentValue: '17.1%',
        targetValue: '18.7%',
        kpiName: '月度毛利率达标率',
        kpiTarget: '≥95%',
        receiver: '销售部-李明'
      }
    },
    async createSingleIndicator(indicatorName = '产品结构优化率') {
      this.clearSmartAnalysisTimers()
      const requestName = this.$t('workbench.actionCreateSingleIndicator', {
        indicator: indicatorName
      })
      const createConfig = await this.fetchSingleIndicatorCreateConfig(
        requestName,
        indicatorName
      )
      this.setSmartFlowCardContent(
        this.getSingleIndicatorCreateThinkingContent(indicatorName),
        {
          title: this.$t('workbench.smartCreateIndicator'),
          status: this.$t('workbench.statusProcessing'),
          currentStep: this.$t('workbench.actionCreateSingleIndicator', {
            indicator: indicatorName
          })
        }
      )
      this.scheduleSmartAnalysis(() => {
        this.setSmartFlowCardContent(
          this.getSingleIndicatorCreateResultContent(createConfig),
          {
            title: this.$t('workbench.smartCreateIndicator'),
            status: this.$t('workbench.statusCompleted'),
            currentStep: this.$t('workbench.actionCreateSingleIndicator', {
              indicator: createConfig.indicatorName || indicatorName
            })
          }
        )
      }, 900)
    },
    async fetchSingleIndicatorCreateConfig(name, indicatorName = '产品结构优化率') {
      try {
        const item = await this.fetchIndBaseDataByNames([name])
        return this.normalizeSingleIndicatorCreateConfig(item.remark, indicatorName)
      } catch (e) {
        console.warn('[IndexHomePage] fetchSingleIndicatorCreateConfig failed:', e)
        return this.normalizeSingleIndicatorCreateConfig(null, indicatorName)
      }
    },
    normalizeSingleIndicatorCreateConfig(remark, indicatorName = '产品结构优化率') {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse single indicator create remark failed:', e)
          config = null
        }
      }
      return {
        ...this.getDefaultSingleIndicatorCreateConfig(indicatorName),
        ...(config || {})
      }
    },
    getDefaultSingleIndicatorCreateConfig(indicatorName = '产品结构优化率') {
      return {
        title: '指标创建成功',
        message: `新指标「${indicatorName}」已成功添加到指标库`,
        indicatorName,
        indicatorId: 'IND-MPNVBEFZ',
        createdTime: '2026/5/27 17:34:50',
        status: '已启用',
        calculationLogic: '根据业务规则自动计算',
        warningThreshold: '已配置默认阈值'
      }
    },
    async fetchRecommendedIndicatorsConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['应用推荐方案'])
        return this.normalizeRecommendedIndicatorsConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchRecommendedIndicatorsConfig failed:', e)
        return this.normalizeRecommendedIndicatorsConfig()
      }
    },
    normalizeRecommendedIndicatorsConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse recommended indicators remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultRecommendedIndicatorsConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        items: Array.isArray((config || {}).items) ? config.items : defaultConfig.items
      }
    },
    getDefaultRecommendedIndicatorsConfig() {
      return {
        title: '推荐方案应用成功',
        summary: '正在应用方案的核心指标组合：',
        items: [
          {
            index: '1',
            indicatorName: '毛利率',
            currentValue: '17.1%',
            targetValue: '18.7%',
            kpiName: '月度毛利率达标率',
            kpiTarget: '≥95%',
            receiver: '销售部-李明'
          },
          {
            index: '2',
            indicatorName: '高毛利产品占比',
            currentValue: '38%',
            targetValue: '45%',
            kpiName: '高毛利产品销售占比月度达标率',
            kpiTarget: '≥90%',
            receiver: '产品部-王芳'
          }
        ],
        footer: '预计执行后，毛利率将提升至18.7%以上，达到行业平均水平。'
      }
    },
    applyAllIndicators() {
      this.appendLocalMessage('user', '全部应用')
      setTimeout(() => {
        const thinkingSteps = [
          '正在解析全部指标列表...',
          '正在逐项计算目标值与当前值差距...',
          '正在匹配执行负责人：销售部-李明',
          '正在下发全部KPI任务...'
        ]
        const thinkingId = 'thinking-apply-all-' + Date.now()
        const thinkingHtml = this.renderCeoThinkingCard(
          thinkingId,
          thinkingSteps[0],
          '全部应用指标',
          '正在应用全部指标组合并下发KPI...',
          thinkingSteps.length
        )
        this.appendLocalMessage('assistant', thinkingHtml, true)
        let stepIndex = 1
        const thinkTimer = setInterval(() => {
          if (stepIndex < thinkingSteps.length) {
            this.updateCeoThinkingCard(thinkingId, thinkingSteps, stepIndex)
            stepIndex++
          } else {
            clearInterval(thinkTimer)
            setTimeout(() => {
              this.removeThinkingCard(thinkingId)
              setTimeout(() => {
                const replyId = 'apply-all-result-' + Date.now()
                const resultHtml = `<div style="padding:14px;border-radius:10px;background:#fff;border:1px solid #e8ecf0;">
                  <div style="font-size:13px;font-weight:600;color:#1f2937;margin-bottom:10px;">全部指标应用成功</div>
                  <div id="${replyId}" style="font-size:12px;color:#5a6b7e;line-height:1.8;min-height:22px;"></div>
                </div>`
                this.appendLocalMessage('assistant', resultHtml, true)
                setTimeout(() => {
                  this.smartTypeWriterEffect(
                    replyId,
                    '已成功应用所有3个指标并生成对应KPI：<br><br>1.「<span style="color:#4A5FF3;font-weight:600;">毛利率</span>」17.1% → 18.7%，KPI「月度毛利率达标率≥95%」，下发给「<span style="color:#004baa;font-weight:600;">销售部-李明</span>」<br><br>2.「<span style="color:#4A5FF3;font-weight:600;">产品调价覆盖率</span>」52% → 80%，KPI「主力产品调价覆盖率≥80%」，下发给「<span style="color:#004baa;font-weight:600;">销售部-李明</span>」<br><br>3.「<span style="color:#4A5FF3;font-weight:600;">平均销售单价（ASP）</span>」¥2,380 → ¥2,560，KPI「ASP提升率≥7.5%」，下发给「<span style="color:#004baa;font-weight:600;">销售部-李明</span>」<br><br><span style="border-top:1px solid #e8ecf0;padding-top:10px;display:block;">所有KPI已成功下发，通过产品调价策略传导BOM成本压力，推动毛利率恢复。</span>'
                  )
                }, 200)
              }, 320)
            }, 500)
          }
        }, 800)
        this.smartAnalysisTimers.push(thinkTimer)
      }, 500)
    },
    async fetchAllIndicatorsConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['全部应用'])
        return this.normalizeAllIndicatorsConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchAllIndicatorsConfig failed:', e)
        return this.normalizeAllIndicatorsConfig()
      }
    },
    normalizeAllIndicatorsConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse all indicators remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultAllIndicatorsConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        items: Array.isArray((config || {}).items) ? config.items : defaultConfig.items
      }
    },
    getDefaultAllIndicatorsConfig() {
      return {
        title: '全部指标应用成功',
        summary: '已成功应用所有3个指标并生成对应KPI：',
        items: [
          {
            index: '1',
            indicatorName: '毛利率',
            currentValue: '17.1%',
            targetValue: '18.7%',
            kpiName: '月度毛利率达标率',
            kpiTarget: '≥95%',
            receiver: '销售部-李明'
          },
          {
            index: '2',
            indicatorName: '高毛利产品占比',
            currentValue: '38%',
            targetValue: '45%',
            kpiName: '高毛利产品销售占比月度达标率',
            kpiTarget: '≥90%',
            receiver: '产品部-王芳'
          },
          {
            index: '3',
            indicatorName: '单位产品毛利',
            currentValue: '¥128',
            targetValue: '¥145',
            kpiName: '单位产品毛利达标率',
            kpiTarget: '≥90%',
            receiver: '财务部-张华'
          }
        ],
        footer: '所有KPI已成功下发，相关责任人可在个人工作台查看并执行。'
      }
    },
    async startMissingIndicatorAnalysis() {
      this.clearSmartAnalysisTimers()
      const missingConfig = await this.fetchMissingIndicatorConfig()
      this.missingIndicatorConfig = missingConfig
      const thinkingSteps = [
        this.$t('workbench.stepMissingIndicatorAnalyze'),
        this.$t('workbench.stepMissingIndicatorIdentify'),
        this.$t('workbench.stepMissingIndicatorComplete'),
        this.$t('workbench.stepMissingIndicatorGenerate')
      ]
      this.runSmartFlowProgress({
        title: this.$t('workbench.smartCreateIndicator'),
        status: this.$t('workbench.stepMissingIndicatorAnalyze'),
        steps: thinkingSteps,
        interval: 650,
        finalBody: this.getCreateIndicatorsContent(undefined, undefined, missingConfig),
        finalOptions: {
          status: this.$t('workbench.statusPending'),
          currentStep: this.$t('workbench.statusNewIndicatorGenerated')
        }
      })
    },
    async fetchMissingIndicatorConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['生成缺失指标，丰富指标库'])
        return this.normalizeMissingIndicatorConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchMissingIndicatorConfig failed:', e)
        return this.normalizeMissingIndicatorConfig()
      }
    },
    normalizeMissingIndicatorConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse missing indicator remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultMissingIndicatorConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        items: Array.isArray((config || {}).items) ? config.items : defaultConfig.items
      }
    },
    getDefaultMissingIndicatorConfig() {
      return {
        title: '创建新指标',
        summary: '基于对话内容分析，已识别出3个可创建的新指标',
        items: [
          {
            name: '产品结构优化率',
            tag: '新指标',
            formulaTitle: '计算公式',
            formula: '产品结构优化率 =（高毛利产品销售额 / 总销售额）× 100%',
            description:
              '衡量高毛利产品在销售结构中的占比，反映产品结构优化效果。目标值建议设定为≥35%。'
          },
          {
            name: '客户毛利贡献度',
            tag: '新指标',
            formulaTitle: '计算公式',
            formula: '客户毛利贡献度 =（客户贡献毛利 / 总毛利）× 100%',
            description:
              '衡量单个客户对整体毛利的贡献程度，用于客户价值分层和重点客户识别。'
          },
          {
            name: '毛利敏感度指数',
            tag: '新指标',
            formulaTitle: '计算公式',
            formula: '毛利敏感度指数 =（毛利变化率 / 价格变化率）',
            description:
              '衡量毛利对价格变动的敏感程度，用于定价决策分析和利润预测。指数越高表示价格变动对毛利影响越大。'
          }
        ]
      }
    },
    startCreateIndicators() {
      this.clearSmartAnalysisTimers()
      const indicatorConfig =
        this.missingIndicatorConfig || this.getDefaultMissingIndicatorConfig()
      this.missingIndicatorConfig = indicatorConfig
      const createStates = [
        {
          done: [true, false, false],
          status: this.$t('workbench.statusProcessing')
        },
        {
          done: [true, true, false],
          status: this.$t('workbench.statusProcessing')
        },
        {
          done: [true, true, true],
          status: this.$t('workbench.statusCompleted')
        }
      ]
      createStates.forEach((state, index) => {
        this.scheduleSmartAnalysis(() => {
          this.setSmartFlowCardContent(
            this.getCreateIndicatorsContent(state.done, state.status, indicatorConfig),
            {
              title: this.$t('workbench.smartCreateIndicator'),
              status: state.done.every(Boolean)
                ? this.$t('workbench.statusCompleted')
                : this.$t('workbench.statusProcessing'),
              currentStep: state.status
            }
          )
        }, index * 900)
      })
    },
    async startSkillDeposit() {
      this.clearSmartAnalysisTimers()
      const skillConfig = await this.fetchSkillDepositConfig()
      const thinkingSteps = [
        '正在分析对话内容，提取可沉淀的方法论...',
        '识别到PVMC四维度毛利分析框架...',
        '正在抽取分析步骤、输入指标和输出模板...',
        '生成skill...'
      ]
      this.setSmartFlowCardContent(
        this.getSkillDepositThinkingContent(thinkingSteps[0]),
        {
          title: '方法论沉淀',
          status: '处理中',
          currentStep: thinkingSteps[0]
        }
      )
      thinkingSteps.slice(1).forEach((step, index) => {
        this.scheduleSmartAnalysis(() => {
          this.setSmartFlowCardContent(this.getSkillDepositThinkingContent(step), {
            title: '方法论沉淀',
            status: '处理中',
            currentStep: step
          })
        }, (index + 1) * 650)
      })
      this.scheduleSmartAnalysis(() => {
        this.setSmartFlowCardContent(this.getSkillDepositResultContent(skillConfig), {
          title: '方法论沉淀',
          status: '待操作',
          currentStep: 'Skill已生成，可继续查看分析结果'
        })
      }, thinkingSteps.length * 650 + 500)
    },
    async fetchSkillDepositConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['将方法论沉淀skill'])
        return this.normalizeSkillDepositConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchSkillDepositConfig failed:', e)
        return this.normalizeSkillDepositConfig()
      }
    },
    normalizeSkillDepositConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse skill deposit remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultSkillDepositConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        skill: {
          ...defaultConfig.skill,
          ...((config || {}).skill || {})
        }
      }
    },
    getDefaultSkillDepositConfig() {
      return {
        title: '方法论沉淀结果',
        summary: '基于对话分析，沉淀PVMC毛利分析skill',
        skill: {
          name: 'PVMC毛利分析Skill',
          description:
            '基于PVMC模型的毛利下降原因分析方法，可快速定位价格、销量、产品结构、成本四大维度的影响'
        },
        footer: '技能创建完成，下次将根据沉淀的Skill和指标快速处理业务！'
      }
    },
    async startSkillAnalysis() {
      this.clearSmartAnalysisTimers()
      const skillAnalysisConfig = await this.fetchSkillAnalysisConfig()
      const thinkingSteps = [
        '正在使用PVMC毛利分析Skill进行分析...',
        '读取毛利率、销售额、产品结构和成本指标...',
        '进行价格维度分析...',
        '进行销量维度分析...',
        '进行产品结构与成本维度分析...',
        '生成最终分析报告...'
      ]
      this.runSmartFlowProgress({
        title: 'Skill分析',
        status: '正在使用PVMC毛利分析Skill进行分析...',
        steps: thinkingSteps,
        interval: 700,
        finalBody: this.getSkillAnalysisResultContent(skillAnalysisConfig),
        finalOptions: {
          status: '已完成',
          currentStep: 'Skill分析报告已生成'
        }
      })
    },
    async fetchSkillAnalysisConfig() {
      try {
        const item = await this.fetchIndBaseDataByNames(['查看PVMC毛利分析Skill'])
        return this.normalizeSkillAnalysisConfig(item.remark)
      } catch (e) {
        console.warn('[IndexHomePage] fetchSkillAnalysisConfig failed:', e)
        return this.normalizeSkillAnalysisConfig()
      }
    },
    normalizeSkillAnalysisConfig(remark) {
      let config = remark
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config)
        } catch (e) {
          console.warn('[IndexHomePage] parse skill analysis remark failed:', e)
          config = null
        }
      }
      const defaultConfig = this.getDefaultSkillAnalysisConfig()
      return {
        ...defaultConfig,
        ...(config || {}),
        pvmcImpact: {
          ...defaultConfig.pvmcImpact,
          ...((config || {}).pvmcImpact || {}),
          items: Array.isArray((config || {}).pvmcImpact?.items)
            ? config.pvmcImpact.items
            : defaultConfig.pvmcImpact.items
        },
        rootCause: {
          ...defaultConfig.rootCause,
          ...((config || {}).rootCause || {}),
          items: Array.isArray((config || {}).rootCause?.items)
            ? config.rootCause.items
            : defaultConfig.rootCause.items
        }
      }
    },
    getDefaultSkillAnalysisConfig() {
      return {
        title: 'PVMC毛利分析Skill分析结果',
        summary: 'PVMC毛利分析Skill分析完成，以下是详细分析报告',
        pvmcImpact: {
          title: 'PVMC四维度影响分析',
          items: [
            {
              name: '价格影响',
              code: 'P',
              value: '-1.8%',
              content: '行业价格战加剧，平均售价同比下降'
            },
            {
              name: '销量影响',
              code: 'V',
              value: '-5.2%',
              content: '大客户流失严重，订单量下降6.2%'
            },
            {
              name: '产品结构',
              code: 'M',
              value: '-1.4%',
              content: '高毛利产品占比从35%降至22%'
            },
            {
              name: '成本影响',
              code: 'C',
              value: '+3.2%',
              content: '原材料价格上涨，成本压力增大'
            }
          ],
          summaryText:
            '四大因素共同作用导致毛利率整体下降1.6个百分点，其中销量下滑是首要驱动因素，贡献超过60%的影响，其次是成本上升和价格竞争压力。'
        },
        rootCause: {
          title: '核心发现与根因分析',
          items: [
            {
              title: '销量下滑',
              contribution: '贡献 -5.2%',
              content:
                '华南区域3家核心大客户流失，导致订单量下降6.2%；老客户复购率同比下降28.7%；市场份额从12.5%降至9.8%，竞品抢占明显。'
            },
            {
              title: '成本上升',
              contribution: '贡献 +3.2%',
              content:
                '钢材原材料价格上涨8.5%，电子元器件涨幅达13-28%；产能利用率从78%降至62%，固定成本分摊增加25%；废品率从1.8%升至2.9%。'
            },
            {
              title: '产品结构变化',
              contribution: '贡献 -1.4%',
              content:
                '高毛利产品销售占比从35%降至22%；低毛利特价产品销量增长143.8%；清库折扣达6.5折；定制化非标订单占比从15%升至28%，毛利率仅11.5%。'
            },
            {
              title: '价格竞争',
              contribution: '贡献 -1.8%',
              content:
                '行业价格战加剧，竞品频繁降价促销；为保证订单量被迫采取跟随降价策略，产品平均售价同比下降。'
            }
          ]
        }
      }
    },
    getChartNumber(value) {
      const number = Number(String(value).replace('%', ''))
      return Number.isFinite(number) ? number : 0
    },
    getChartAxisRange(series) {
      const values = (series || []).flatMap((item) =>
        (item.data || []).map(this.getChartNumber)
      )
      if (!values.length) return { min: 0, max: 100, interval: 20 }
      const minValue = Math.min(...values)
      const maxValue = Math.max(...values)
      const min = Math.max(0, Math.floor((minValue - 5) / 5) * 5)
      const max = Math.ceil((maxValue + 5) / 5) * 5
      return { min, max, interval: Math.max(1, Math.ceil((max - min) / 4)) }
    },
    getGrossMarginActionHtml(actions = []) {
      const actionMap = {
        pvmc_analysis: 'pvmc-analysis',
        pvmc_help: 'pvmc-summary'
      }
      return actions
        .map((action) => {
          const smartAction = actionMap[action.actionKey] || action.actionKey || ''
          if (action.style === 'link') {
            return `<button data-smart-action="${smartAction}" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;text-decoration:underline;cursor:pointer;padding:0;">${action.label}</button>`
          }
          return `<button data-smart-action="${smartAction}" style="height:48px;padding:0 26px;border-radius:12px;border:2px solid #4A5FF3;background:#fff;color:#4A5FF3;font-size:16px;font-weight:700;cursor:pointer;box-shadow:0 10px 24px rgba(74,95,243,0.08);">${action.label}</button>`
        })
        .join('\n')
    },
    getGrossMarginAnalysisContent(config = this.getDefaultGrossMarginAnalysisConfig()) {
      const chart = config.chart || {}
      const chartSeries = Array.isArray(chart.series) ? chart.series : []
      const axisRange = this.getChartAxisRange(chartSeries)
      const chartOption = {
        tooltip: { trigger: 'axis' },
        legend: {
          left: 12,
          bottom: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { color: '#667085', fontSize: 12 },
          data: chartSeries.map((item) => item.name)
        },
        grid: { top: 42, left: 46, right: 20, bottom: 54 },
        xAxis: {
          type: 'category',
          data: chart.xAxis || [],
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#E5EAF3' }},
          axisLabel: { color: '#667085' }
        },
        yAxis: {
          type: 'value',
          min: axisRange.min,
          max: axisRange.max,
          interval: axisRange.interval,
          axisLabel: { color: '#8AA0BD', formatter: '{value}%' },
          splitLine: { lineStyle: { color: '#EEF2F7' }}
        },
        series: chartSeries.flatMap((item, index) => {
          const isPrimary = index === 0
          const color = isPrimary ? '#4A5FF3' : '#FF6B6B'
          const softColor = isPrimary
            ? 'rgba(74, 95, 243, 0.25)'
            : 'rgba(255, 107, 107, 0.18)'
          const data = (item.data || []).map(this.getChartNumber)
          return [
            {
              name: item.name,
              type: 'bar',
              barWidth: 46,
              barGap: index === 0 ? undefined : '-100%',
              data,
              itemStyle: {
                borderRadius: [5, 5, 4, 4],
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color },
                    { offset: 1, color: softColor }
                  ]
                }
              },
              label: {
                show: true,
                position: 'top',
                color,
                fontWeight: 700,
                formatter: '{c}%'
              }
            },
            {
              name: `${item.name}趋势`,
              type: 'line',
              data,
              symbolSize: 12,
              lineStyle: { color, width: 2 },
              itemStyle: {
                color: '#FFFFFF',
                borderColor: color,
                borderWidth: 3
              },
              tooltip: { show: false }
            }
          ]
        })
      }
      const title = chart.rangeLabel
        ? `${chart.title || '毛利率趋势对比'}（${chart.rangeLabel}）`
        : chart.title || '毛利率趋势对比'

      return [
        `### ${config.indicator?.title || '毛利率'}`,
        config.indicator?.definition || '',
        '',
        '```json',
        JSON.stringify({
          component: 'chart',
          title,
          option: chartOption
        }),
        '```',
        '',
        `#### ${config.analysis?.title || '数据分析结论'}`,
        ...(config.analysis?.paragraphs || []).flatMap((paragraph) => [paragraph, '']),
        `<div style="margin-top:18px;display:flex;align-items:center;gap:18px;flex-wrap:wrap;">
          ${this.getGrossMarginActionHtml(config.actions)}
        </div>`
      ].join('\n')
    },
    getPvmcSummaryContent(config = this.getDefaultPvmcSummaryConfig()) {
      const method = config.pvmcMethod || {}
      const dimensions = Array.isArray(method.dimensions) ? method.dimensions : []
      const applicationValue = method.applicationValue || {}
      return [
        `### ${method.title || 'PVMC分析方法'}`,
        method.summary || '',
        '',
        `<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;margin-top:18px;">
          ${dimensions
    .map((item) =>
      this.getPvmcSummaryBlock(
        item.code,
        item.title ||
                  `${item.name || ''}${item.nameCn ? `（${item.nameCn}）` : ''}`,
        item.description
      )
    )
    .join('')}
        </div>
        <div style="margin-top:18px;padding:18px 20px;border-radius:12px;background:#f8fafc;border:1px solid #e3e9f4;">
          <div style="font-size:16px;font-weight:700;color:#004baa;margin-bottom:12px;">${
  applicationValue.title || '应用价值'
}</div>
          <div style="font-size:15px;color:#52657a;line-height:1.9;">${
  applicationValue.description || ''
}</div>
        </div>`
      ].join('\n')
    },
    getPvmcSummaryBlock(letter, title, content) {
      return `<div style="padding:24px 20px;border-radius:12px;background:#fff;border:1px solid #e3e9f4;min-height:136px;">
        <div style="font-size:22px;font-weight:800;color:#004baa;margin-bottom:18px;">${letter}</div>
        <div style="font-size:15px;font-weight:800;color:#1f2937;margin-bottom:12px;">${title}</div>
        <div style="font-size:15px;color:#52657a;line-height:1.75;">${content}</div>
      </div>`
    },
    getPvmcImpactBlock(item) {
      return `<div style="padding:18px;border-radius:12px;background:#fff;border:1px solid #dfe7f5;">
        <div style="font-size:14px;color:#8aa0b8;margin-bottom:12px;font-weight:600;">${
  item.name || ''
}${item.code ? ` (${item.code})` : ''}</div>
        <div style="font-size:28px;font-weight:800;color:#4A5FF3;margin-bottom:10px;">${
  item.value || ''
}</div>
        <div style="font-size:14px;color:#52657a;">${item.description || ''}</div>
      </div>`
    },
    getPvmcReportContent(config = this.getDefaultPvmcAnalysisConfig()) {
      const report = config.report || {}
      const pvmcImpact = config.pvmcImpact || {}
      const rootCause = config.rootCause || {}
      const impactItems = Array.isArray(pvmcImpact.items) ? pvmcImpact.items : []
      const rootCauseItems = Array.isArray(rootCause.items) ? rootCause.items : []
      return [
        `### ${report.title || '毛利波动原因分析报告'}`,
        report.summary || '',
        '',
        `<div style="margin-top:14px;padding:18px;border-radius:12px;background:#f8fafc;border:1px solid #e3e9f4;">
          <div style="font-size:16px;font-weight:700;color:#4A5FF3;margin-bottom:16px;">${
  pvmcImpact.title || 'PVMC四维度影响分析'
}</div>
          <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;">
            ${impactItems.map(this.getPvmcImpactBlock).join('')}
          </div>
          <div style="margin-top:16px;padding:14px 16px;border-radius:10px;background:#eef4ff;border:1px solid #c7d6ff;color:#52657a;line-height:1.8;font-size:14px;">
            <strong style="color:#4A5FF3;">总影响分析：</strong>${
  pvmcImpact.summary || ''
}
          </div>
        </div>`,
        '',
        `<div style="margin-top:16px;padding:18px;border-radius:12px;background:#fff;border:1px solid #e3e9f4;">
          <div style="font-size:16px;font-weight:700;color:#4A5FF3;margin-bottom:14px;">${
  rootCause.title || '核心发现与根因分析'
}</div>
          ${rootCauseItems
    .map((item) =>
      this.getCauseBlock(
        `${item.title || ''}${
          item.contribution ? `（${item.contribution}）` : ''
        }`,
        item.description || ''
      )
    )
    .join('')}
        </div>`,
        '',
        `<div style="font-size:15px;line-height:1.9;color:#52657a;margin-top:16px;">
          ${config.conclusion?.description || ''}
        </div>`,
        '',
        '<button data-smart-action="quadrant-plans" style="margin-top:18px;padding:12px 24px;border-radius:12px;font-size:15px;font-weight:700;border:2px solid #4A5FF3;cursor:pointer;color:#4A5FF3;background:#fff;">四象限分析生成提升方案</button>'
      ].join('\n')
    },
    getCauseBlock(title, content) {
      return `<div style="padding:16px 18px;border-radius:10px;background:#edf5ff;border-left:4px solid #4A5FF3;margin-bottom:12px;">
        <div style="font-size:15px;font-weight:700;color:#3157ff;margin-bottom:8px;">${title}</div>
        <div style="font-size:14px;color:#52657a;line-height:1.85;">${content}</div>
      </div>`
    },
    getQuadrantProgressContent(percent) {
      return [
        '**深度思考中...**',
        '',
        `<div style="margin-top:8px;padding:16px;border-radius:12px;background:#fff;border:1px solid #dfe7f5;">
          <div style="font-size:14px;color:#52657a;margin-bottom:12px;">正在基于四象限分析模型生成智能提升方案... (${percent}%)</div>
          <div style="height:5px;background:#e8ecf0;border-radius:999px;overflow:hidden;">
            <div style="height:100%;width:${percent}%;background:linear-gradient(90deg,#667eea,#764ba2);transition:width 0.3s ease;"></div>
          </div>
        </div>`
      ].join('\n')
    },
    getPlanApplyProgressContent(plan, percent) {
      return [
        '**深度思考中...**',
        '',
        `<div style="margin-top:8px;padding:16px;border-radius:12px;background:#fff;border:1px solid #dfe7f5;">
          <div style="font-size:14px;color:#52657a;margin-bottom:12px;">正在生成基于【${plan}】方案的指标组合... (${percent}%)</div>
          <div style="height:5px;background:#e8ecf0;border-radius:999px;overflow:hidden;">
            <div style="height:100%;width:${percent}%;background:linear-gradient(90deg,#667eea,#764ba2);transition:width 0.3s ease;"></div>
          </div>
        </div>`
      ].join('\n')
    },
    getPlanApplyResultContent(config = this.getDefaultPlanApplyResultConfig()) {
      if (config.capacityUtilizationTarget || config.defectRateTarget) {
        return `<div style="padding:18px 20px;border-radius:12px;background:#fff;border:1px solid #dfe7f5;color:#52657a;font-size:16px;line-height:1.9;">
          已生成${
  config.title || '制造成本优化'
}方案：优化生产排程提升产能利用率至<strong style="color:#3157ff;">${
  config.capacityUtilizationTarget || '-'
}</strong>，工艺改进降低废品率至<strong style="color:#3157ff;">${
  config.defectRateTarget || '-'
}</strong>。预计可提升毛利<strong style="color:#3157ff;">${
  config.expectedImprove || '-'
}</strong>，方案已同步给「<strong style="color:#3157ff;">${
  config.executor || '-'
}</strong>」执行。
        </div>`
      }
      if (
        config.lockPricePeriod ||
        config.backupSupplierCount ||
        config.expectedCostReduction
      ) {
        return `<div style="padding:18px 20px;border-radius:12px;background:#fff;border:1px solid #dfe7f5;color:#52657a;font-size:16px;line-height:1.9;">
          已生成${
  config.title || '材料降本'
}方案：与核心供应商签订<strong style="color:#3157ff;">${
  config.lockPricePeriod || '-'
}</strong>锁价协议，开发<strong style="color:#3157ff;">${
  config.backupSupplierCount || '-'
}</strong>备选供应商引入竞争机制。预计可降低原材料成本<strong style="color:#3157ff;">${
  config.expectedCostReduction || '-'
}</strong>，提升毛利<strong style="color:#3157ff;">${
  config.expectedImprove || '-'
}</strong>，方案已同步给「<strong style="color:#3157ff;">${
  config.executor || '-'
}</strong>」执行。
        </div>`
      }
      return `<div style="padding:18px 20px;border-radius:12px;background:#fff;border:1px solid #dfe7f5;color:#52657a;font-size:16px;line-height:1.9;">
        已生成${
  config.title || '策略'
}方案：针对高毛利爆款产品实施<strong style="color:#3157ff;">${
  config.strategy || '-'
}</strong>策略，同步推出增值服务包（延长质保、免费上门调试）。预计可提升毛利<strong style="color:#3157ff;">${
  config.expectedImprove || '-'
}</strong>，方案已同步给「<strong style="color:#3157ff;">${
  config.executor || '-'
}</strong>」执行。
      </div>`
    },
    getQuadrantPlanContent(config = this.getDefaultQuadrantPlanConfig()) {
      const solution = config.solution || {}
      const plans = Array.isArray(solution.plans) ? solution.plans : []
      return [
        `### ${solution.title || '智能提升方案'}`,
        solution.summary || '',
        '',
        `<div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;margin-top:16px;">
          ${plans
    .map((plan) =>
      this.getPlanCard(
        plan.title,
        plan.subtitle,
        plan.items || [],
        plan.expectedImprove,
        plan.difficulty
      )
    )
    .join('')}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:12px;margin-top:18px;">
          ${plans
    .map((plan, index) => {
      const style =
                index === 0
                  ? 'padding:12px 20px;border-radius:12px;font-weight:700;border:2px solid #4A5FF3;color:#4A5FF3;background:#fff;cursor:pointer;'
                  : 'padding:12px 0;border:none;background:transparent;color:#4A5FF3;font-weight:700;text-decoration:underline;cursor:pointer;'
      return `<button data-smart-action="apply-plan" data-plan="${
        plan.title || ''
      }" style="${style}">${
        plan.actionLabel || `应用${plan.title || ''}方案`
      }</button>`
    })
    .join('')}
        </div>`
      ].join('\n')
    },
    getPlanCard(title, subtitle, items, lift, difficulty) {
      return `<div style="padding:20px;border-radius:12px;background:#f8fafc;border:1px solid #cfd9ff;">
        <div style="display:flex;gap:14px;align-items:flex-start;margin-bottom:14px;">
          <div style="width:46px;height:46px;border-radius:12px;background:#4A5FF3;color:#fff;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:800;">↗</div>
          <div>
            <div style="font-size:18px;font-weight:800;color:#1f2937;">${title}</div>
            <div style="font-size:14px;color:#3157ff;font-weight:700;margin-top:2px;">${subtitle}</div>
          </div>
        </div>
        <div style="font-size:14px;color:#52657a;line-height:1.9;">
          ${items.map((item) => `<div>• ${item}</div>`).join('')}
        </div>
        <div style="height:1px;background:#dfe7f5;margin:16px 0;"></div>
        <div style="display:flex;align-items:center;justify-content:space-between;color:#8aa0b8;font-size:14px;">
          <span>预期提升: <strong style="color:#3157ff;">${lift}</strong></span>
          <span style="padding:8px 12px;border-radius:8px;background:#eef0ff;color:#3157ff;font-weight:700;">难度：${difficulty}</span>
        </div>
      </div>`
    },
    getIndicatorCombinationContent(
      plan,
      config = this.getDefaultIndicatorCombinationConfig()
    ) {
      const existingIndicators = config.existingIndicators || {}
      const newIndicators = config.newIndicators || {}
      const existingItems = Array.isArray(existingIndicators.items)
        ? existingIndicators.items
        : []
      const newItems = Array.isArray(newIndicators.items) ? newIndicators.items : []
      return [
        `### ${config.title || '提升毛利率的组合指标'}`,
        config.summary || `基于【${plan}】方案，已生成关键指标组合，建议优先应用核心指标`,
        '',
        `<div style="font-size:15px;font-weight:800;color:#3157ff;margin-top:18px;margin-bottom:12px;">${
          existingIndicators.title || '现有指标'
        }</div>`,
        ...existingItems.map((item) =>
          this.getIndicatorCard(
            item.name,
            item.tag,
            `当前: <strong style="color:#3157ff;">${
              item.currentValue || '-'
            }</strong> <span style="color:#8aa0b8;margin:0 10px;">→</span> 目标: <strong style="color:#3157ff;">${
              item.targetValue || '-'
            }</strong>`,
            `负责人：${item.owner || '-'}`,
            '应用'
          )
        ),
        '',
        `<div style="font-size:15px;font-weight:800;color:#3157ff;margin-top:20px;margin-bottom:12px;">${
          newIndicators.title || '新指标（待创建）'
        }</div>`,
        ...newItems.map((item) =>
          this.getNewIndicatorCard(
            item.name,
            item.description,
            `负责人：${item.owner || '-'}`,
            '创建',
            item.tag
          )
        ),
        '',
        `<div style="font-size:15px;line-height:1.8;color:#52657a;margin-top:18px;">${
          config.recommendation || ''
        }</div>`,
        '',
        `<div style="display:flex;flex-wrap:wrap;gap:14px;margin-top:18px;align-items:center;">
          <button data-smart-action="apply-recommended-indicators" style="padding:14px 28px;border-radius:12px;font-size:16px;font-weight:800;border:2px solid #4A5FF3;color:#4A5FF3;background:#fff;cursor:pointer;">应用推荐方案</button>
          <button data-smart-action="apply-all-indicators" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;cursor:pointer;">全部应用</button>
          <span style="color:#d4dbe8;">|</span>
          <button data-smart-action="create-missing-indicators" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;cursor:pointer;">生成缺失指标，丰富指标库</button>
          <span style="color:#d4dbe8;">|</span>
          <button data-smart-action="save-method-skill" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;cursor:pointer;">将方法论沉淀skill</button>
        </div>`
      ].join('\n')
    },
    getIndicatorCard(title, tag, valueLine, owner, actionText) {
      return `<div style="padding:18px 20px;border-radius:12px;background:#fff;border:1px solid #e3e9f4;margin-bottom:12px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
          <div style="min-width:0;">
            <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;">
              <span style="font-size:17px;font-weight:800;color:#1f2937;">${title}</span>
              <span style="padding:5px 10px;border-radius:6px;background:#eef0ff;color:#4A5FF3;font-size:14px;">${tag}</span>
            </div>
            <div style="font-size:15px;color:#8aa0b8;margin-bottom:12px;">${valueLine}</div>
            <div style="font-size:14px;color:#6b7f95;">♙ ${owner}</div>
          </div>
          <button data-smart-action="apply-single-indicator" data-indicator="${title}" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;cursor:pointer;">${actionText}</button>
        </div>
      </div>`
    },
    getNewIndicatorCard(title, desc, owner, actionText, tag = '新指标') {
      return `<div style="padding:18px 20px;border-radius:12px;background:#fff;border:1px dashed #4A5FF3;margin-bottom:12px;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
          <div style="min-width:0;">
            <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;">
              <span style="font-size:17px;font-weight:800;color:#1f2937;">${title}</span>
              <span style="padding:5px 10px;border-radius:6px;background:#eef0ff;color:#4A5FF3;font-size:14px;">${tag}</span>
            </div>
            <div style="font-size:15px;color:#52657a;line-height:1.8;margin-bottom:12px;">${desc}</div>
            <div style="font-size:14px;color:#6b7f95;">♙ ${owner}</div>
          </div>
          <button data-smart-action="create-single-indicator" data-indicator="${title}" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;cursor:pointer;">${actionText}</button>
        </div>
      </div>`
    },
    getSingleIndicatorApplyContent(config = this.getDefaultSingleIndicatorApplyConfig()) {
      return [
        `### ${config.title || '指标应用成功'}`,
        '',
        `正在应用指标「<span style="color:#3157ff;font-weight:800;">${
          config.indicatorName || '-'
        }</span>」，该指标从当前值 <span style="color:#e11d48;font-weight:800;">${
          config.currentValue || '-'
        }</span> 调整为目标值 <span style="color:#009f86;font-weight:800;">${
          config.targetValue || '-'
        }</span>。`,
        '',
        `已生成KPI： <span style="color:#3157ff;font-weight:800;">${
          config.kpiName || '-'
        }${config.kpiTarget || ''}</span>`,
        '',
        `KPI已下发给： <span style="color:#0f66d8;font-weight:800;">${
          config.receiver || '-'
        }</span>`
      ].join('\n')
    },
    getSingleIndicatorCreateThinkingContent(indicatorName = '产品结构优化率') {
      return [
        '### 创建新指标',
        `正在创建「${indicatorName}」指标...`,
        '',
        '<div style="display:flex;align-items:center;gap:10px;color:#7c3aed;font-size:15px;font-weight:700;margin:12px 0;">',
        '  <span style="width:22px;height:22px;border-radius:50%;background:#7c3aed;color:#fff;display:inline-flex;align-items:center;justify-content:center;font-size:13px;">↻</span>',
        '  深度思考中...',
        '</div>',
        '<div style="margin-top:12px;padding:16px 18px;border-radius:12px;background:#fbf7ff;border:1px solid #e8d5ff;color:#52657a;font-size:15px;">指标元数据保存完成...</div>'
      ].join('\n')
    },
    getSingleIndicatorCreateResultContent(
      config = this.getDefaultSingleIndicatorCreateConfig()
    ) {
      return [
        `### ${config.title || '指标创建成功'}`,
        config.message || `新指标「${config.indicatorName || '-'}」已成功添加到指标库`,
        '',
        `<div style="margin-top:14px;padding:16px 18px;border-radius:10px;background:#f5f8fc;color:#52657a;font-size:15px;line-height:1.8;">
          <div><strong style="color:#1f2937;">指标名称：</strong><span style="color:#3157ff;font-weight:800;">${
  config.indicatorName || '-'
}</span></div>
          <div><strong style="color:#1f2937;">指标ID：</strong>${
  config.indicatorId || '-'
}</div>
          <div><strong style="color:#1f2937;">创建时间：</strong>${
  config.createdTime || '-'
}</div>
          <div><strong style="color:#1f2937;">状态：</strong><span style="color:#16a34a;font-weight:800;">${
  config.status || '-'
}</span></div>
          <div><strong style="color:#1f2937;">计算逻辑：</strong>${
  config.calculationLogic || '-'
}</div>
          <div><strong style="color:#1f2937;">预警阈值：</strong>${
  config.warningThreshold || '-'
}</div>
        </div>`
      ].join('\n')
    },
    getRecommendedIndicatorLine(item, index) {
      const order = item.index || String(index + 1)
      return `<div style="display:grid;grid-template-columns:max-content minmax(0,1fr);column-gap:6px;align-items:start;margin:10px 0;line-height:1.75;text-align:left;color:#1f2937;">
        <span style="color:#52657a;">${order}.</span>
        <span>「<span style="color:#1f2937;font-weight:800;">${
  item.indicatorName || '-'
}</span>」从 <span style="color:#e11d48;font-weight:800;">${
  item.currentValue || '-'
}</span> → <span style="color:#0f66d8;font-weight:800;">${
  item.targetValue || '-'
}</span>，已生成KPI「<span style="font-weight:800;">${item.kpiName || '-'}${
  item.kpiTarget || ''
}</span>」，下发给「<span style="font-weight:800;">${
  item.receiver || '-'
}</span>」</span>
      </div>`
    },
    getRecommendedIndicatorsSuccessContent(
      config = this.getDefaultRecommendedIndicatorsConfig()
    ) {
      return [
        `### ${config.title || '推荐方案应用成功'}`,
        config.summary || '',
        '',
        `<div style="margin-top:12px;color:#52657a;">
          ${(config.items || [])
    .map((item, index) => this.getRecommendedIndicatorLine(item, index))
    .join('')}
        </div>`,
        '<hr style="border:none;border-top:1px solid #e3e9f4;margin:18px 0;">',
        config.footer || ''
      ].join('\n')
    },
    getAllIndicatorLine(item, index) {
      const order = item.index || String(index + 1)
      return `<div style="display:grid;grid-template-columns:max-content minmax(0,1fr);column-gap:4px;align-items:start;margin:8px 0;line-height:1.75;text-align:left;">
        <span style="color:#52657a;">${order}.</span>
        <span>「<span style="color:#3157ff;font-weight:800;">${
  item.indicatorName || '-'
}</span>」${item.currentValue || '-'} → ${item.targetValue || '-'}，KPI「${
  item.kpiName || '-'
}${item.kpiTarget || ''}」，下发给「<span style="color:#0f66d8;font-weight:800;">${
  item.receiver || '-'
}</span>」</span>
      </div>`
    },
    getAllIndicatorsSuccessContent(config = this.getDefaultAllIndicatorsConfig()) {
      return [
        `### ${config.title || '全部指标应用成功'}`,
        config.summary || '',
        '',
        `<div style="margin-top:14px;color:#52657a;">
          ${(config.items || [])
    .map((item, index) => this.getAllIndicatorLine(item, index))
    .join('')}
        </div>`,
        '<hr style="border:none;border-top:1px solid #e3e9f4;margin:18px 0;">',
        config.footer || ''
      ].join('\n')
    },
    getCreateIndicatorsContent(
      done = [false, false, false],
      status = '准备创建指标...',
      config = this.getDefaultMissingIndicatorConfig()
    ) {
      const defaultConfig = this.getDefaultMissingIndicatorConfig()
      const contentConfig = config || defaultConfig
      const items =
        Array.isArray(contentConfig.items) && contentConfig.items.length > 0
          ? contentConfig.items
          : defaultConfig.items
      return [
        `### ${contentConfig.title || defaultConfig.title}`,
        contentConfig.summary || defaultConfig.summary,
        '',
        ...items.map((item, index) => this.getCreateIndicatorCard(item, done[index])),
        '',
        `<div style="margin-top:16px;padding:14px 18px;border-radius:10px;background:#eef4ff;border:1px solid #c7d6ff;text-align:center;color:${
          done.every(Boolean) ? '#25b21f' : '#52657a'
        };font-size:15px;font-weight:${
          done.every(Boolean) ? '700' : '500'
        };">${status}</div>`,
        '',
        done.some(Boolean)
          ? '<button data-smart-action="save-method-skill" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;cursor:pointer;margin-top:14px;">将方法论沉淀成skill</button>'
          : `<div style="display:flex;flex-wrap:wrap;gap:18px;align-items:center;margin-top:18px;">
              <button data-smart-action="start-create-indicators" style="padding:14px 28px;border-radius:12px;font-size:16px;font-weight:800;border:2px solid #4A5FF3;color:#4A5FF3;background:#fff;cursor:pointer;">开始创建指标</button>
              <button data-smart-action="save-method-skill" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;cursor:pointer;">将方法论沉淀成skill</button>
            </div>`
      ].join('\n')
    },
    getCreateIndicatorCard(item, done) {
      const icon = done
        ? '<span style="width:24px;height:24px;border-radius:50%;border:2px solid #25b21f;color:#25b21f;display:inline-flex;align-items:center;justify-content:center;font-size:15px;font-weight:800;">✓</span>'
        : '<span style="width:24px;height:24px;border-radius:50%;border:2px dashed #8aa0ff;display:inline-flex;align-items:center;justify-content:center;"></span>'
      return `<div style="padding:18px 20px;border-radius:12px;background:#fff;border:${
        done ? '1.5px solid #4A5FF3' : '1px dashed #8aa0ff'
      };margin-top:16px;">
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;">
          ${icon}
          <span style="font-size:17px;font-weight:800;color:#1f2937;">${
  item.name || '-'
}</span>
          <span style="padding:5px 10px;border-radius:6px;background:#eef0ff;color:#4A5FF3;font-size:14px;">${
  item.tag || '新指标'
}</span>
        </div>
        <div style="padding:12px 14px;border-radius:8px;background:#f3f7ff;border:1px solid #dfe7f5;margin-bottom:12px;">
          <div style="font-size:13px;color:#a0afc0;margin-bottom:8px;">${
  item.formulaTitle || '计算公式'
}</div>
          <div style="font-size:14px;color:#4A5FF3;">${item.formula || '-'}</div>
        </div>
        <div style="font-size:14px;color:#7a8b9d;line-height:1.8;">${
  item.description || ''
}</div>
      </div>`
    },
    getSkillDepositThinkingContent(step) {
      return [
        '### 方法论沉淀',
        '正在分析对话内容，提取可沉淀的方法论...',
        '',
        '<span style="color:#25b21f;font-weight:700;">正在分析...</span>',
        '',
        `<div style="margin-top:10px;padding:16px;border-radius:10px;background:#f8fff6;border:1px solid #d8efcf;color:#52657a;font-size:15px;">${step}</div>`
      ].join('\n')
    },
    getSkillDepositResultContent(config = this.getDefaultSkillDepositConfig()) {
      const skill = config.skill || {}
      return [
        `### ${config.title || '方法论沉淀结果'}`,
        config.summary || '',
        '',
        `<div style="margin-top:18px;padding:22px;border-radius:12px;background:#f8fafc;border:1px solid #e3e9f4;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
            <div>
              <div style="font-size:18px;font-weight:800;color:#1f2937;margin-bottom:10px;">${
  skill.name || 'PVMC毛利分析Skill'
}</div>
              <div style="font-size:15px;color:#52657a;line-height:1.8;">${
  skill.description || ''
}</div>
            </div>
            <button data-smart-action="view-pvmc-skill" style="border:none;background:transparent;color:#4A5FF3;font-size:15px;font-weight:700;text-decoration:underline;white-space:nowrap;cursor:pointer;">查看skill</button>
          </div>
        </div>`,
        '',
        config.footer || ''
      ].join('\n')
    },
    getSkillImpactCard(item) {
      return `<div style="padding:18px;border-radius:12px;background:#fff;border:1px solid #dfe7f5;">
        <div style="font-size:14px;color:#8aa0b8;margin-bottom:12px;font-weight:600;">${
  item.name || ''
}${item.code ? ` (${item.code})` : ''}</div>
        <div style="font-size:28px;font-weight:800;color:#4A5FF3;margin-bottom:10px;">${
  item.value || ''
}</div>
        <div style="font-size:14px;color:#52657a;">${
  item.content || item.description || ''
}</div>
      </div>`
    },
    async fetchIndBaseDataByNames(names) {
      try {
        const res = await fetch('/prod-api/bigdata/ind-base-data/list/by-names', {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ names })
        })
        const data = await res.json()
        return data?.data?.[0] || {}
      } catch (e) {
        console.warn('[IndexHomePage] fetchIndBaseDataByNames failed:', e)
        return {}
      }
    },
    getSkillAnalysisResultContent(config = this.getDefaultSkillAnalysisConfig()) {
      const pvmcImpact = config.pvmcImpact || {}
      const rootCause = config.rootCause || {}
      const impactItems = Array.isArray(pvmcImpact.items) ? pvmcImpact.items : []
      const rootCauseItems = Array.isArray(rootCause.items) ? rootCause.items : []
      return [
        `### ${config.title || 'PVMC毛利分析Skill分析结果'}`,
        config.summary || 'PVMC毛利分析Skill分析完成，以下是详细分析报告',
        '',
        `<div style="margin-top:18px;padding:18px;border-radius:12px;background:#f8fafc;border:1px solid #e3e9f4;">
          <div style="font-size:16px;font-weight:700;color:#4A5FF3;margin-bottom:16px;">${
  pvmcImpact.title || 'PVMC四维度影响分析'
}</div>
          <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;">
            ${impactItems.map(this.getSkillImpactCard).join('')}
          </div>
          <div style="margin-top:16px;padding:14px 16px;border-radius:10px;background:#eef4ff;border:1px solid #c7d6ff;color:#52657a;line-height:1.8;font-size:14px;">
            <strong style="color:#4A5FF3;">总影响分析：</strong>${
  pvmcImpact.summaryText || ''
}
          </div>
        </div>`,
        '',
        `<div style="margin-top:18px;padding:18px;border-radius:12px;background:#fff;border:1px solid #e3e9f4;">
          <div style="font-size:16px;font-weight:700;color:#4A5FF3;margin-bottom:16px;">${
  rootCause.title || '核心发现与根因分析'
}</div>
          ${rootCauseItems
    .map((item) =>
      this.getCauseBlock(
        `${item.title || ''}${
          item.contribution ? `（${item.contribution}）` : ''
        }`,
        item.content || item.description || ''
      )
    )
    .join('')}
        </div>`
      ].join('\n')
    }
  }
}
